// QuoteDetailPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useClientData } from '../hooks/useClientData';
import AddPropertyModal from '../components/AddPropertyModal';
import { Property } from '../types';
import AddVehicleModal from '../components/AddVehicleModal';
import AddHouseholdContentsModal, { HouseholdContents } from '../components/AddHouseholdContentsModal';
import TrailerCaravanModal from '../components/TrailerCaravanModal';
import QuoteValueAddedModal from '../components/QuoteValueAdded';
import QuoteRiskItemsModal, { EditableRiskItemCard } from '../components/QuoteRiskItems';
import WaterCraftModal from '../components/AddWaterCraftModal';
import EmailConfirmation from '../components/EmailConfirmation';
import EmailTemplateModal from '../components/EmailTemplateModal';
import AddPersonModal from '../components/AddPersonModal';
import { PersonOfInterest, DriverLicense } from '../types';
import DateInput from '../components/DateInput';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface BaseQuote {
  id: string;
  referenceNumber: string;
  reference: string;
  createdDate: string;
  created_at: string;
  status: string;
  total_value: number;
  product_count: number;
  documents?: any[];
}

// Update the ClientDocument interface
interface ClientDocument {
  id: string;
  name: string;
  fileName?: string;
  file_name?: string;
  originalName?: string;
  type: 'upload' | 'quote' | 'proposal' | 'quotation';
  documentType?: string;
  document_type?: string;
  url?: string;
  file_path?: string;
  createdAt: string;
  created_at?: string;
  quoteReference?: string;
  quote_reference?: string;
  size?: number;
  file_size?: number;
  description?: string;
}

// Add this interface for quotations
interface ClientQuotation {
  id: string;
  referenceNumber: string;
  reference: string;
  createdDate: string;
  created_at: string;
  status: string;
  total_value?: number;
  product_count?: number;
  documents?: ClientDocument[];
}

// Update the modal props interface
interface ClientDocumentsModalProps {
  isVisible: boolean;
  onClose: () => void;
  clientId: string | null;
  quoteIds: string[];
  allQuotes: QuoteSummary[];
}

interface AdminUnderwriterData {
  id: string;
  name: string;
  email?: string;
  type: 'administrator' | 'underwriter';
  quotation_status?: string;
  premium?: string;
}

interface RemovalReason {
  quoteId: string;
  productId: string;
  productType: string;
  productName: string;
  reason: string;
  removedAt: string;
}

interface QuoteSummary {
  id: string;
  referenceNumber: string;
  reference: string;
  createdDate: string;
  created_at: string;
  status: string;
  client_id: string;
  consultant_id?: string;
  administrator_ids?: string[];
  administrator_id?: string;
  underwriter_ids?: string[];
  underwriter_id?: string;
  consultant?: { id?: string | number; first_name: string; last_name: string };
  total_value?: number;
  product_count?: number;
  premium?: string | number;
}

interface ConsultantData {
  id: string;
  first_name: string;
  last_name: string;
  name?: string;
  surname?: string;
  email?: string;
}

interface QuoteDetailData {
  id: string;
  referenceNumber: string;
  reference: string;
  createdDate: string;
  created_at: string;
  status: string;
  client_id: string;
  consultant_id?: string;
  administrator_id?: string;
  consultant?: { first_name: string; last_name: string };
  insurers?: { id: string; name: string }[];
  vehicles?: any[];
  properties?: any[];
  riskItems?: any[];
  householdContents?: any[];
  trailersCaravans?: any[];
  valueAddedProducts?: any[];
  products?: any[];
  documents?: { id: string; name: string; url: string }[];
   premium?: string | number;
}

const ClientDocumentsModal: React.FC<ClientDocumentsModalProps> = ({
  isVisible,
  onClose,
  clientId,
  quoteIds,
  allQuotes
}) => {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [quotations, setQuotations] = useState<ClientQuotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQuotationsList, setShowQuotationsList] = useState(false);
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);

  // ✅ ADDED: States for inline upload
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  useEffect(() => {
    console.log('🛠️ Modal useEffect fired!', { isVisible, clientId, quoteIds });
    if (isVisible && clientId) {
      fetchAllDocuments();
      fetchQuotations();
    }
  }, [isVisible, clientId, quoteIds]);

  const fetchQuotations = async () => {
  setLoading(true);
  setError(null);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token');

    // Fetch all quotes for this client directly from API
    const quotesResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/quotes/client/${clientId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    console.log('Quotes response:', quotesResponse.data);

    let quotationsList: ClientQuotation[] = [];

    if (quotesResponse.data?.data && Array.isArray(quotesResponse.data.data)) {
      console.log(`Found ${quotesResponse.data.data.length} quotes for client`);
      
      // First, create base quotations from the list
      const baseQuotations = quotesResponse.data.data.map((quote: any) => ({
        id: quote.id,
        referenceNumber: quote.referenceNumber || quote.reference || `Quote-${quote.id}`,
        reference: quote.reference || quote.referenceNumber || `Quote-${quote.id}`,
        createdDate: quote.createdDate || quote.created_at,
        created_at: quote.created_at,
        status: quote.status || 'draft',
        total_value: quote.total_value || 0,
        product_count: 0,
        documents: []
      }));

      console.log('Base quotations:', baseQuotations);

      // Then fetch details for each quote to get product counts
const quotationsWithDetails = await Promise.all(
  baseQuotations.map(async (quote: BaseQuote) => {
    let productCount = 0;
    try {
      const quoteDetailRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quote.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (quoteDetailRes.data?.data) {
        const quoteData = quoteDetailRes.data.data;
        
        // Count products from the quote data
        productCount = 
          (quoteData.vehicles?.length || 0) +
          (quoteData.properties?.length || 0) +
          (quoteData.riskItems?.length || 0);
        
        console.log(`Quote ${quote.id} initial count from quote data:`, productCount);
      }

      // Fetch from all individual product endpoints with better logging
const productEndpoints = [
  { name: 'properties', url: `${process.env.REACT_APP_API_URL}/api/properties?quote_id=${quote.id}` },
  { name: 'household contents', url: `${process.env.REACT_APP_API_URL}/api/household-contents?quote_id=${quote.id}` },
  { name: 'trailers/caravans', url: `${process.env.REACT_APP_API_URL}/api/trailer-caravans?quote_id=${quote.id}` },
  { name: 'value added', url: `${process.env.REACT_APP_API_URL}/api/value-added?quote_id=${quote.id}` },
  { name: 'water craft', url: `${process.env.REACT_APP_API_URL}/api/water-craft?quote_id=${quote.id}` },
  { name: 'risk items', url: `${process.env.REACT_APP_API_URL}/api/risk-items?quote_id=${quote.id}` }
];

      for (const endpoint of productEndpoints) {
  try {
    console.log(`Fetching ${endpoint.name} for quote ${quote.id} from:`, endpoint.url);
    const response = await axios.get(endpoint.url, { 
      headers: { Authorization: `Bearer ${token}` },
      timeout: 5000
    });
    
    console.log(`Response for ${endpoint.name} quote ${quote.id}:`, response.data);
    
    // Handle different response structures
    let items = null;
    
    // Check if response.data is an array (direct array response)
    if (Array.isArray(response.data)) {
      items = response.data;
      console.log(`✅ ${endpoint.name} returned direct array with ${items.length} items`);
    }
    // Check if response.data.data is an array (wrapped response)
    else if (response.data?.data && Array.isArray(response.data.data)) {
      items = response.data.data;
      console.log(`✅ ${endpoint.name} returned wrapped array with ${items.length} items`);
    }
    // Check if response.data has a different property
    else if (response.data?.items && Array.isArray(response.data.items)) {
      items = response.data.items;
      console.log(`✅ ${endpoint.name} returned items array with ${items.length} items`);
    }
    
    if (items) {
      productCount += items.length;
      console.log(`✅ Quote ${quote.id} added ${items.length} ${endpoint.name}`);
    } else {
      console.log(`⚠️ No items found in response for ${endpoint.name} quote ${quote.id}`);
    }
  } catch (err: any) {
    console.log(`❌ Error fetching ${endpoint.name} for quote ${quote.id}:`, err.message);
  }
}

      console.log(`📊 Quote ${quote.id} final product count:`, productCount);
      
    } catch (err) {
      console.log(`Could not fetch details for quote ${quote.id}:`, err);
    }
    
    // Return a new object with the product count
    return {
      ...quote,
      product_count: productCount
    };
  })
);

      quotationsList = quotationsWithDetails;
      console.log('Quotations with details:', quotationsList);

      // Fetch documents for each quotation
      await Promise.all(
        quotationsList.map(async (quote) => {
          try {
            const quoteDocsRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/quotes/${quote.id}/documents`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (quoteDocsRes.data?.documents && Array.isArray(quoteDocsRes.data.documents)) {
              quote.documents = quoteDocsRes.data.documents.map((doc: any) => ({
                id: doc.id,
                name: doc.file_name || doc.fileName || 'Document',
                fileName: doc.file_name || doc.fileName,
                type: 'quotation',
                documentType: doc.document_type || doc.documentType || 'Document',
                url: doc.url,
                file_path: doc.file_path,
                createdAt: doc.created_at || doc.createdAt || new Date().toISOString(),
                size: doc.file_size || doc.size,
                description: doc.description,
                quoteReference: quote.referenceNumber || quote.reference
              }));
            }
          } catch (err) {
            console.log(`No documents found for quote ${quote.id}`);
          }
        })
      );

      // Sort by date (newest first)
      quotationsList.sort((a, b) => 
        new Date(b.created_at || b.createdDate).getTime() - new Date(a.created_at || a.createdDate).getTime()
      );
    }

    setQuotations(quotationsList);
    console.log('Final quotations list:', quotationsList);
  } catch (err: any) {
    console.error('Error fetching quotations:', err);
    setError(err.message || 'Failed to fetch quotations');
  } finally {
    setLoading(false);
  }
};

  const fetchAllDocuments = async () => {
  setLoading(true);
  setError(null);
  
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token');

    const allDocs: ClientDocument[] = [];

    // 1. Fetch client-personal documents via /api/client-documents?clientId=
    try {
      const clientDocsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/client-documents?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // This endpoint returns a bare array
      const clientDocs: any[] = Array.isArray(clientDocsRes.data)
        ? clientDocsRes.data
        : Array.isArray(clientDocsRes.data?.data)
          ? clientDocsRes.data.data
          : [];

      console.log('Client documents found:', clientDocs.length);
      clientDocs.forEach((doc: any) => {
        allDocs.push({
          id: doc.id,
          name: doc.file_name || 'Document',
          fileName: doc.file_name,
          type: 'upload',
          documentType: doc.document_type || 'Document',
          url: undefined,
          file_path: doc.file_path,
          createdAt: doc.created_at || new Date().toISOString(),
          size: doc.file_size,
          description: doc.description
        });
      });
    } catch (err: any) {
      console.log('Error fetching client documents:', err.message);
    }

    // 2. Fetch signed documents from dedicated endpoint
    console.log('🔍 Fetching signed documents for clientId:', clientId);
    try {
      const signedDocsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotes/signed-documents`,
        { 
          params: { clientId: clientId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('📄 Signed documents response:', signedDocsRes.data);
      
      if (signedDocsRes.status === 200 && signedDocsRes.data?.documents && signedDocsRes.data.documents.length > 0) {
        console.log(`📄 Found ${signedDocsRes.data.documents.length} signed documents from API`);
        signedDocsRes.data.documents.forEach((doc: any) => {
          const displayName = doc.original_name || doc.file_name || doc.filename || 'Signed Document';
          
          console.log('📄 Adding signed document:', {
            id: doc.id,
            name: displayName,
            original_name: doc.original_name
          });
          
          allDocs.push({
            id: doc.id,
            name: displayName,
            fileName: doc.file_name || doc.filename,
            originalName: doc.original_name,
            type: 'quote',
            documentType: 'signed_proposal',
            url: doc.file_path,
            file_path: doc.file_path,
            createdAt: doc.created_at,
            size: doc.file_size,
            description: doc.description || 'Digitally signed proposal',
            quoteReference: doc.quote_reference
          });
        });
      } else {
        console.log('📄 No signed documents found');
      }
    } catch (err: any) {
      console.error('❌ Error fetching signed documents:', err.message);
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
      }
    }

        // 3. Fetch documents from each quote
    console.log(`🛠️ Starting Step 3 with quoteIds:`, quoteIds);
    for (const quoteId of quoteIds) {
      try {
        console.log(`🛠️ Fetching docs for quote ${quoteId}...`);
        const quoteDocsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/documents`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log(`🛠️ API returned ${quoteDocsRes.data?.documents?.length} documents for quote ${quoteId}`);
        
        const quoteRef = allQuotes.find(q => q.id === quoteId)?.referenceNumber || 
                        allQuotes.find(q => q.id === quoteId)?.reference || 
                        `Quote-${quoteId}`;

        if (quoteDocsRes.data?.documents && Array.isArray(quoteDocsRes.data.documents)) {
          console.log(`Found ${quoteDocsRes.data.documents.length} documents for quote ${quoteId}`);
          quoteDocsRes.data.documents.forEach((doc: any) => {
            // ✅ Process all documents, including signed ones. 
            // The /signed-documents endpoint is unreliable and often fails silently.
            
            allDocs.push({
              id: doc.id,
              name: doc.original_name || doc.file_name || doc.fileName || 'Document',
              fileName: doc.file_name || doc.fileName,
              type: 'quote',
              documentType: doc.document_type || doc.documentType || 'Document',
              url: doc.url,
              file_path: doc.file_path,
              createdAt: doc.created_at || doc.createdAt || new Date().toISOString(),
              size: doc.file_size || doc.size,
              description: doc.description,
              quoteReference: quoteRef
            });
          });
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          console.log(`No documents endpoint for quote ${quoteId}`);
        } else {
          console.log(`Error fetching documents for quote ${quoteId}:`, err.message);
        }
      }
    }

    console.log('Total documents found:', allDocs.length);
    
    // Sort by date (newest first)
    allDocs.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setDocuments(allDocs);
  } catch (err: any) {
    console.error('Error in fetchAllDocuments:', err);
    setError(err.message || 'Failed to fetch documents');
  } finally {
    setLoading(false);
  }
};

  // ✅ ADDED: Handlers for inline upload
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(false);
  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
    const file = e.dataTransfer.files[0];
    const newDesc = stripExtension(file.name);
    
    setTimeout(() => {
      setUploadFile(file);
      setUploadDescription((prev) =>
        prev.trim() === '' ? newDesc : prev
      );
    }, 50);
  }
};

const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target.files && e.target.files[0]) {
    const file = e.target.files[0];
    const newDesc = stripExtension(file.name);

    // Defer the state update slightly to let the OS file dialog fully close
    // and the browser return focus. This prevents React from dropping the render.
    setTimeout(() => {
      setUploadFile(file);
      setUploadDescription((prev) =>
        prev.trim() === '' ? newDesc : prev
      );
    }, 50);
  }
  
  // Reset the input value so selecting the same file again still triggers onChange
  e.target.value = '';
};

  const handleInlineUpload = async () => {
  if (!uploadFile || !uploadType || !clientId) return;

  const currentQuoteId = quoteIds?.[0];
  if (!currentQuoteId) {
    setUploadMessage('No quote available to attach this file to.');
    return;
  }

  setIsUploading(true);
  setUploadMessage('');

  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token');

    const formData = new FormData();
    formData.append('files', uploadFile); // MUST match upload.array('files')
    formData.append('quoteId', String(currentQuoteId));
    formData.append('description', uploadDescription || uploadFile.name);

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/quotes/upload-files`,
      formData,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' // Explicitly set to ensure boundary is correct
        } 
      }
    );

    setUploadMessage('File uploaded successfully!');
    setUploadFile(null);
    setUploadType('');
    setUploadDescription('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    await fetchAllDocuments();

    setTimeout(() => setUploadMessage(''), 3000);
  } catch (err: any) {
    console.error('Upload error:', {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
    });

    const data = err.response?.data;
    const msg =
      (typeof data === 'string' && data) ||
      data?.message ||
      data?.error ||
      err.message ||
      'Failed to upload file';

    setUploadMessage(msg);
  } finally {
      setIsUploading(false);
  }
};


  const handleDownload = async (doc: ClientDocument) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token found');

    // Personal docs (type 'upload') and quote docs use different endpoints.
    const isPersonalDoc = doc.type === 'upload';

    const endpoint = isPersonalDoc
      ? `${process.env.REACT_APP_API_URL}/api/client-documents/download/${doc.id}`
      : `${process.env.REACT_APP_API_URL}/api/quotes/files/download-blob/${doc.id}`;

    const response = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });

    const contentType =
      (response.headers?.['content-type'] as string | undefined) ||
      (doc as any).mime_type ||
      'application/octet-stream';

    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', doc.name || 'document');
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Give the browser a moment to start the download before revoking
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5_000);
  } catch (error: any) {
    console.error('Error downloading document:', error);
    alert(error.response?.data?.message || 'Failed to download document');
  }
};

  const handleViewDocument = async (doc: ClientDocument) => {
  try {
    const token = localStorage.getItem('token');

    console.log('🔍 Viewing document:', {
      id: doc.id,
      name: doc.name,
      type: doc.type,
      documentType: doc.documentType,
      file_path: doc.file_path
    });

    // ✅ Detect signed proposals and route them to the correct backend endpoint
    const isSignedProposal = 
      doc.documentType === 'signed_proposal' || 
      doc.name?.includes('signed_proposal') || 
      doc.file_path?.includes('signed-proposals');

    const isPersonalDoc = doc.type === 'upload';

    let url = '';
    if (isSignedProposal) {
      url = `${process.env.REACT_APP_API_URL}/api/quotes/files/download-blob/${doc.id}`;
    } else if (isPersonalDoc) {
      url = `${process.env.REACT_APP_API_URL}/api/client-documents/download/${doc.id}`;
    } else {
      url = `${process.env.REACT_APP_API_URL}/api/quotes/files/download-blob/${doc.id}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });

    // Prefer the server's Content-Type; fall back to the doc's mime_type;
    // final fallback to application/pdf so the browser renders it, not dumps it.
    const contentType =
      (response.headers?.['content-type'] as string | undefined) ||
      (doc as any).mime_type ||
      (doc as any).documentType ||
      'application/pdf';

    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);

    // Open in a new tab. Some browsers download PDFs to a temp file instead of
    // rendering them; the explicit MIME type is what makes Chrome/Firefox render.
    const newTab = window.open(blobUrl, '_blank');
    if (!newTab) {
      // Popup blocked — fall back to a download prompt
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.name || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);

  } catch (error: any) {
    console.error('Error viewing document:', error);
    alert(error.response?.data?.message || 'Failed to view document');
  }
};

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'sent':
      case 'accepted':
      case 'active':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'draft':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
      case 'declined':
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentIcon = (doc: ClientDocument) => {
    const fileType = doc.documentType?.toLowerCase() || '';
    if (fileType.includes('pdf')) {
      return (
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    } else if (fileType.includes('image')) {
      return (
        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-[9999]">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
<div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
  <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
    Client Documents & Quotations
  </h2>
  <button
    onClick={onClose}
    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
  >
    ×
  </button>
</div>

        {/* Filter Buttons */}
<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
  <div className="flex gap-2">
    <button
      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-indigo-600 text-white"
    >
      {/* ✅ Count = uploads + attached files + quotation records */}
      All Documents ({
        documents.length + 
        quotations.reduce((total, quote) => total + (quote.documents?.length || 0), 0) +
        quotations.length
      })
    </button>
  </div>
</div>

        {/* Content */}
<div className="p-6">
  
  {/* ✅ INLINE UPLOAD SECTION */}
  <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Upload New Document</h2>
          <p className="text-sm text-gray-500">Add new files to client's document repository</p>
        </div>
      </div>

      {uploadMessage && (
        <div className={`mb-6 p-3 rounded-lg border ${uploadMessage.includes('success') ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {uploadMessage}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Document Type <span className="text-red-500">*</span></label>
          <select
            value={uploadType}
            onChange={(e) => setUploadType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Select Document Type</option>
            <option value="id_document">ID Document</option>
            <option value="proof_of_address">Proof of Address</option>
            <option value="bank_statements">Bank Statements</option>
            <option value="policy_document">Policy Document</option>
            <option value="claim_document">Claim Document</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            placeholder="Enter document description"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Select File <span className="text-red-500">*</span></label>
        <div 
  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
    dragActive ? 'border-blue-500 bg-blue-50' : 'border-blue-300 bg-blue-50/50 hover:bg-blue-50'
  }`}
  onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
  onClick={(e) => {
    // Prevent double-firing if the user clicked the hidden input directly
    if (e.target === fileInputRef.current) return;
    fileInputRef.current?.click();
  }}
>
  <input 
    type="file" 
    ref={fileInputRef} 
    onChange={handleFileChange} 
    className="hidden" 
    onClick={(e) => e.stopPropagation()} 
  />
          
          {uploadFile ? (
            <div className="flex flex-col items-center w-full max-w-sm mx-auto">
              <div className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-2 text-left relative">
                <div className="flex justify-between items-start mb-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  {uploadType && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">{uploadType.replace(/_/g, ' ')}</span>}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm break-all line-clamp-2">{uploadFile.name}</h4>
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{uploadFile.name.split('.').pop()?.toUpperCase() || 'FILE'}</span>
                  <span>•</span>
                  <span>{(uploadFile.size / 1024).toFixed(2)} KB</span>
                </div>
              </div>
              <p className="text-sm text-blue-600 font-medium hover:underline cursor-pointer">Click to change file</p>
            </div>
          ) : (
            <div className="flex flex-col items-center text-gray-500">
              <svg className="w-10 h-10 text-blue-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <p className="text-sm text-gray-700"><span className="font-semibold text-blue-600 hover:text-blue-700">Upload a file</span> or drag and drop</p>
              <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG, DOC, DOCX, TXT up to 10MB</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleInlineUpload}
          disabled={!uploadFile || !uploadType || isUploading}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          {isUploading ? 'Uploading...' : 'Upload Document'}
        </button>
        <button
          onClick={() => { setUploadFile(null); setUploadType(''); setUploadDescription(''); }}
          disabled={isUploading || (!uploadFile && !uploadType)}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-bold text-white bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          Cancel
        </button>
      </div>
    </div>
  

  {loading ? (
    <div className="flex justify-center items-center py-12">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <span className="ml-3 text-gray-600">Loading...</span>
    </div>
  ) : error ? (
    <div className="text-center py-12">
      <div className="text-red-500 mb-2">
        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <p className="text-gray-600">{error}</p>
      <button
        onClick={() => {
          fetchAllDocuments();
          fetchQuotations();
        }}
        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
      >
        Try Again
      </button>
    </div>
  ) : false ? (
    // Show Quotations List when "Quotations" filter is selected
    quotations.length === 0 ? (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-2">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-500">No quotations found for this client</p>
      </div>
    ) : (
      <div className="space-y-4">
        {quotations.map((quote) => (
          <div
            key={quote.id}
            className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow bg-white"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-indigo-600">
                  {quote.referenceNumber || quote.reference}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Created: {formatDate(quote.created_at || quote.createdDate)}
                </p>
              </div>
              
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              
              <div>
                <span className="text-gray-600">Products:</span>
                <span className="ml-2 font-medium">{quote.product_count || 0}</span>
              </div>
            </div>

            {/* Documents in this quotation */}
            {quote.documents && quote.documents.length > 0 && (
  <div className="mt-4 pt-4 border-t border-gray-100">
    <p className="text-sm font-medium text-gray-700 mb-2">
      Attached Documents ({quote.documents.length})
      {quote.documents.some(doc => doc.documentType === 'signed_proposal') && (
        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
          ✓ Signed
        </span>
      )}
    </p>
    <div className="space-y-2">
      {quote.documents.slice(0, 3).map((doc) => (
        <div key={doc.id} className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            {doc.documentType === 'signed_proposal' ? (
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
            <span className={`${doc.documentType === 'signed_proposal' ? 'text-green-600 font-medium' : 'text-gray-600'} truncate max-w-xs`}>
              {doc.name}
              {doc.documentType === 'signed_proposal' && ' ✓'}
            </span>
          </div>
          <button
            onClick={() => handleViewDocument(doc)}
            className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View
          </button>
        </div>
      ))}
      {quote.documents.length > 3 && (
        <p className="text-xs text-gray-500 mt-1">
          +{quote.documents.length - 3} more documents
        </p>
      )}
    </div>
  </div>
)}
          </div>
        ))}
      </div>
    )
        ) : (
    // Combined Documents View
    (() => {
      // 1. All standalone uploaded documents
      const uploadDocs = documents;

      // 2. All quotation documents
      const quotationDocs = quotations.flatMap(q =>
        (q.documents || []).map(doc => ({
          ...doc,
          type: doc.type || 'quotation',
          quoteReference: doc.quoteReference || q.referenceNumber
        }))
      );

      const allCombinedDocs = [...uploadDocs, ...quotationDocs];

      if (allCombinedDocs.length === 0 && quotations.length === 0) {
        return (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-500">No documents found</p>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          {/* SECTION 1: UPLOADED FILES & ATTACHED FILE CARDS */}
          {allCombinedDocs.length > 0 && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">File Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allCombinedDocs.map((doc) => (
                  <div
                    key={`${doc.type}-${doc.id}`}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white flex flex-col justify-between"
                  >
                    <div className="flex items-start gap-3">
                      {getDocumentIcon(doc)}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate" title={doc.name}>
                          {doc.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 capitalize">
                          {doc.documentType?.replace(/_/g, ' ') || 'Document'}
                        </p>
                        {doc.quoteReference && (
                          <p className="text-xs text-indigo-600 mt-1 font-medium">
                            {doc.quoteReference}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <span>{formatFileSize(doc.size)}</span>
                          <span>•</span>
                          <span>{formatDate(doc.createdAt)}</span>
                        </div>
                        {doc.description && (
                          <p className="text-xs text-gray-600 mt-2 line-clamp-2">
                            {/* ✅ Format any raw ISO timestamps inside the description */}
                            {doc.description.replace(
                              /(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})/g,
                              (match, date, hr, min, sec) => {
                                const hour = parseInt(hr, 10);
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                const displayHour = hour % 12 || 12;
                                return `${date} ${displayHour}:${min} ${ampm}`;
                              }
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                      <button
                        onClick={() => handleViewDocument(doc)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 2: QUOTATIONS LIST */}
          {quotations.length > 0 && (
            <div className="pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">Quotations</h3>
              <div className="space-y-4">
                {quotations.map((quote) => (
                  <div
                    key={`quote-${quote.id}`}
                    className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow bg-white"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-indigo-600">
                          {quote.referenceNumber || quote.reference}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Created: {formatDate(quote.created_at || quote.createdDate)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-600">Products:</span>
                        <span className="ml-2 font-medium">{quote.product_count || 0}</span>
                      </div>
                    </div>

                    {quote.documents && quote.documents.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Attached Documents ({quote.documents.length})
                        </p>
                        <div className="space-y-2">
                          {quote.documents.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded border border-gray-200">
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-gray-700 truncate max-w-xs">{doc.name}</span>
                              </div>
                              <button
                                onClick={() => handleViewDocument(doc)}
                                className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                              >
                                View
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    })()
  )}
</div>

      </div>
    </div>
  );
};

// ============================================================================
// Quote Email Modal
// ============================================================================
interface QuoteEmailRecipient {
  email: string;
  label: string;
  type: 'Client' | 'POI' | 'Custom';
}

interface QuoteDocumentOption {
  id: number;
  file_name: string;
  original_name: string;
  file_size?: number;
  document_type?: string;
  created_at?: string;
  scope?: 'quote' | 'client';
}

interface QuoteEmailModalProps {
  isVisible: boolean;
  onClose: () => void;
  quoteId: string;
  clientId: string;
  quoteRef: string;
  clientName?: string;
  onSent?: () => void;
}

const QuoteEmailModal: React.FC<QuoteEmailModalProps> = ({
  isVisible,
  onClose,
  quoteId,
  clientId,
  quoteRef,
  clientName = '',
  onSent
}) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [sentTo, setSentTo] = useState('');
  const [createRecipient, setCreateRecipient] = useState('');
  const [subject, setSubject] = useState(`[${quoteRef}] `);
  const [message, setMessage] = useState(
    `Client Name: ${clientName}\n\n`
  );
  const [emailOptions, setEmailOptions] = useState<QuoteEmailRecipient[]>([]);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [clientEmail, setClientEmail] = useState<string>('');

  const [documents, setDocuments] = useState<QuoteDocumentOption[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  const MAX_DOCUMENTS = 5;

  // Reset + fetch on open
  useEffect(() => {
    if (!isVisible) return;

    // We'll populate recipients once we fetch the emails
    setRecipients([]);
    setSentTo('');
    setCreateRecipient('');
    setSubject(`[${quoteRef}] `);
    setMessage(`Client Name: ${clientName}\n\n`);
    setSelectedDocuments([]);
    setError(null);
    setUploadProgress(null);

    fetchEmailOptions().then(() => {
      // Auto-add the client's primary email if we found one
      // Note: We need to handle this inside fetchEmailOptions now to avoid race conditions
    });
    
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, quoteId, clientId]);

  const fetchEmailOptions = async () => {
    setIsLoadingEmails(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const options: QuoteEmailRecipient[] = [];

      // Client emails
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/client-emails/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = res.data?.data || [];
        list.forEach((e: any, idx: number) => {
          if (e.email) {
            options.push({ email: e.email, label: `${e.email} (Client)`, type: 'Client' });
            // Set the primary client email (or the first one)
            if (idx === 0 || e.is_primary) {
              setClientEmail(e.email);
            }
          }
        });
      } catch (err) {
        console.warn('Could not fetch client emails:', err);
      }

      // Persons of interest
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        list.forEach((p: any) => {
          if (p.email) {
            options.push({ email: p.email, label: `${p.email} (POI)`, type: 'POI' });
          }
        });
      } catch (err) {
        console.warn('Could not fetch persons of interest:', err);
      }

      // Deduplicate
      const seen = new Set<string>();
      const unique = options.filter((o) => {
        const k = o.email.toLowerCase().trim();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      setEmailOptions(unique);
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const fetchDocuments = async () => {
    if (!quoteId) return;
    setIsLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/all-documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const list: QuoteDocumentOption[] = (res.data?.documents || []).map((d: any) => ({
        id: d.id,
        file_name: d.file_name || d.filename || 'document.pdf',
        original_name: d.original_name || d.file_name || 'document.pdf',
        file_size: d.file_size,
        document_type: d.document_type,
        created_at: d.created_at,
        scope: d.scope
      }));

      setDocuments(list);
    } catch (err) {
      console.warn('Could not fetch quote documents:', err);
      setDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleAddRecipient = () => {
    const email = sentTo.trim();
    if (!email) {
      setError('Please select an email address from the dropdown');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email address is already added');
      return;
    }
    setRecipients([...recipients, email]);
    setSentTo('');
    setError(null);
  };

  const handleAddCreateRecipient = () => {
    const email = createRecipient.trim();
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setError('Please enter an email address');
      return;
    }
    if (!regex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (recipients.includes(email)) {
      setError('This email address is already added');
      return;
    }
    setRecipients([...recipients, email]);
    setCreateRecipient('');
    setError(null);
  };

  const handleRemoveRecipient = (email: string) => {
    setRecipients(recipients.filter((r) => r !== email));
  };

  const handleDocumentToggle = (docId: number) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : prev.length >= MAX_DOCUMENTS
          ? prev
          : [...prev, docId]
    );
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${units[i]}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (recipients.length === 0 && !sentTo.trim()) {
      setError('Please add at least one recipient');
      return;
    }
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    const allRecipients = [...recipients];
    if (sentTo.trim() && !allRecipients.includes(sentTo.trim())) {
      allRecipients.push(sentTo.trim());
    }

    const selectedDocs = documents.filter((d) => selectedDocuments.includes(d.id));

    try {
      setIsSubmitting(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const userRaw = localStorage.getItem('user');
      let employee = 'System';
      try {
        const u = JSON.parse(userRaw || '{}');
        employee = u.username || u.name || u.email || 'System';
      } catch {
        /* noop */
      }

      const refNo = `REF-${Date.now()}-${Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0')}`;

      const formData = new FormData();
      formData.append('to', JSON.stringify(allRecipients));
      formData.append('subject', subject.trim());
      formData.append('message', message.trim());
      formData.append('client_id', clientId);
      formData.append('ref_no', refNo);
      formData.append('employee', employee);
      formData.append('document_ids', JSON.stringify(selectedDocuments));
      formData.append(
        'documents',
        JSON.stringify(
          selectedDocs.map((d) => ({
            id: d.id,
            file_name: d.file_name,
            original_name: d.original_name
          }))
        )
      );

      const totalFiles = selectedDocs.length;
      setUploadProgress({ current: 0, total: totalFiles });

      // Download each selected doc as a blob and attach
      let processed = 0;
      const successfullyAttached: any[] = [];

      for (const doc of selectedDocs) {
        try {
          const dl = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/files/download-blob/${doc.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
              timeout: 30000
            }
          );

          const blob: Blob = dl.data;
          if (blob && blob.size > 0) {
            const name = doc.original_name || doc.file_name || 'document.pdf';
            formData.append('files', blob, name);
            successfullyAttached.push(doc);
          }
        } catch (err) {
          console.warn(`Could not download document ${doc.id}:`, err);
        } finally {
          processed++;
          setUploadProgress({ current: processed, total: totalFiles });
        }
      }

      formData.set(
        'documents',
        JSON.stringify(
          successfullyAttached.map((d) => ({
            id: d.id,
            file_name: d.file_name,
            original_name: d.original_name
          }))
        )
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/send-email-with-attachments`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send email');
      }

      alert(`✅ Email sent to ${allRecipients.length} recipient(s)`);
      onSent?.();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to send email';
      setError(msg);
      alert(`❌ ${msg}`);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Send Email</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
            <p className="text-xs text-gray-500 mb-2">
              Select from existing contacts and click Add, or create a new recipient below
            </p>
            <div className="flex gap-2">
              <select
                value={sentTo}
                onChange={(e) => setSentTo(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={isLoadingEmails}
              >
                <option value="">Select an email address...</option>
                {emailOptions.length === 0 && !isLoadingEmails && (
                  <option value="" disabled>No email addresses found</option>
                )}
                {emailOptions.map((o, i) => (
                  <option key={i} value={o.email}>{o.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddRecipient}
                className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Create Recipient</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={createRecipient}
                onChange={(e) => setCreateRecipient(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter a new email address"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCreateRecipient();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddCreateRecipient}
                className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients ({recipients.length})
            </label>
            {recipients.length === 0 ? (
              <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                No recipients added yet.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                {recipients.map((email, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(email)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={5}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Files
            </label>
            {isLoadingDocuments ? (
              <div className="text-gray-500 text-sm py-2">Loading documents...</div>
            ) : documents.length === 0 ? (
              <div className="text-gray-500 text-sm py-2">No documents found for this quote.</div>
            ) : (
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                {documents.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer"
                    onClick={() => handleDocumentToggle(doc.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDocuments.includes(doc.id)}
                      readOnly
                      disabled={!selectedDocuments.includes(doc.id) && selectedDocuments.length >= MAX_DOCUMENTS}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.original_name || doc.file_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {doc.scope === 'client' ? 'Client Doc' : 'Quote Doc'} • {doc.document_type || 'Document'} • {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {uploadProgress && (
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading files...</span>
                <span>{uploadProgress.current} of {uploadProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md disabled:opacity-50"
            >
              {isSubmitting
                ? uploadProgress
                  ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                  : 'Sending...'
                : 'Send Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Quote WhatsApp Modal
// ============================================================================
interface QuoteWhatsAppModalProps {
  isVisible: boolean;
  onClose: () => void;
  quoteId: string;
  clientId: string;
  quoteRef: string;
  clientName?: string;
  clientPhone?: string;
  onSent?: () => void;
}

interface WhatsAppPOI {
  id: number;
  name: string;
  surname: string;
  contact_number: string;
}

const QuoteWhatsAppModal: React.FC<QuoteWhatsAppModalProps> = ({
  isVisible,
  onClose,
  quoteId,
  clientId,
  quoteRef,
  clientName = '',
  clientPhone = '',
  onSent
}) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [sendToClient, setSendToClient] = useState('');
  const [sendToPoi, setSendToPoi] = useState('');
  const [customPoiNumber, setCustomPoiNumber] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pois, setPois] = useState<WhatsAppPOI[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  const [documents, setDocuments] = useState<QuoteDocumentOption[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);

  useEffect(() => {
    if (!isVisible) return;

    // Auto-add client's phone number if available
    const initialRecipients: string[] = [];
    if (clientPhone) {
      initialRecipients.push(clientPhone);
    }
    
    setRecipients(initialRecipients);
    setSendToClient('');
    setSendToPoi('');
    setCustomPoiNumber(''); // ✅ ADD THIS
    setMessage('');
    setSelectedDocuments([]);
    setError(null);

    fetchContacts();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, quoteId, clientId, clientPhone]);

  const fetchContacts = async () => {
    setIsLoadingContacts(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/persons-of-interest?clientId=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setPois(list.filter((p: any) => p.contact_number));
      } catch {
        setPois([]);
      }
    } finally {
      setIsLoadingContacts(false);
    }
  };

  const fetchDocuments = async () => {
    if (!quoteId) return;
    setIsLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/all-documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const list: QuoteDocumentOption[] = (res.data?.documents || []).map((d: any) => ({
        id: d.id,
        file_name: d.file_name || d.filename || 'document.pdf',
        original_name: d.original_name || d.file_name || 'document.pdf',
        file_size: d.file_size,
        document_type: d.document_type,
        created_at: d.created_at
      }));

      setDocuments(list);
    } catch {
      setDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleAddClient = () => {
    const phone = sendToClient.trim();
    if (!phone) {
      setError('Please select a client phone number');
      return;
    }
    if (recipients.includes(phone)) {
      setError('This phone number is already added');
      return;
    }
    setRecipients([...recipients, phone]);
    setSendToClient('');
    setError(null);
  };

        const handleAddPoi = () => {
    // Check custom typed number first, then the dropdown
    const phone = customPoiNumber.trim() || sendToPoi.trim();
    
    if (!phone) {
      setError('Please enter or select a phone number');
      return;
    }
    if (recipients.includes(phone)) {
      setError('This phone number is already added');
      return;
    }
    setRecipients([...recipients, phone]);
    setSendToPoi('');
    setCustomPoiNumber('');
    setError(null);
  };

  const handleRemoveRecipient = (phone: string) => {
    setRecipients(recipients.filter((r) => r !== phone));
  };

  const handleDocumentToggle = (docId: number) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const formatPhoneForWhatsApp = (phoneNumber: string): string | null => {
    if (!phoneNumber) return null;
    let clean = phoneNumber.replace(/[^\d+]/g, '');
    if (clean.startsWith('+')) {
      clean = clean.substring(1);
    } else if (clean.startsWith('0') && clean.length === 10) {
      clean = '27' + clean.substring(1);
    } else if (!clean.startsWith('27') && clean.length > 9) {
      clean = '27' + clean;
    }
    if (clean.length !== 11 || !clean.startsWith('27')) return null;
    return clean;
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${units[i]}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (recipients.length === 0) {
      setError('Please add at least one recipient');
      return;
    }
    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const userRaw = localStorage.getItem('user');
      let employee = 'System';
      try {
        const u = JSON.parse(userRaw || '{}');
        employee = u.username || u.name || u.email || 'System';
      } catch {
        /* noop */
      }

      const selectedDocs = documents.filter((d) => selectedDocuments.includes(d.id));

      const fullMessage = `Quote: ${quoteRef}\n\n${message.trim()}`;

      const refNo = `REF-${Date.now()}-${Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0')}`;

      const payload = {
        to: recipients,
        message: fullMessage,
        client_id: parseInt(clientId),
        quote_id: parseInt(quoteId),
        ref_no: refNo,
        employee,
        document_ids: selectedDocuments,
        documents: selectedDocs.map((d) => ({
          id: d.id,
          file_name: d.file_name,
          original_name: d.original_name
        }))
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/whatsapp-quotes/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save WhatsApp record');
      }

      // Build the WhatsApp web message with links
      const firstRecipient = recipients[0];
      const formatted = formatPhoneForWhatsApp(firstRecipient);
      if (!formatted) {
        setError('Invalid phone number format');
        return;
      }

      let whatsappMessage = `Hello ${clientName || 'Client'},\n\n`;
      whatsappMessage += `Quote: ${quoteRef}\n\n`;
      whatsappMessage += message.trim();

            if (selectedDocs.length > 0) {
        whatsappMessage += '\n\n📎 *Attached Files:*\n';

        // ✅ Use the API URL, not the frontend URL, and match the backend route
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.qbx.co.za';

        for (const doc of selectedDocs) {
          const fileName = doc.original_name || doc.file_name || 'Document';
          try {
            const tokenRes = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/quotes/files/public-token/${doc.id}`,
              {},
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            const publicToken = tokenRes.data?.data?.token;
            if (publicToken) {
              // ✅ FIX: Point to the correct backend route
              const fileUrl = `${apiBaseUrl}/api/quotes/files/public/${publicToken}`;
              whatsappMessage += `\n${fileName}\n${fileUrl}\n`;
            } else {
              whatsappMessage += `\n${fileName}\nPlease request the file from your consultant.\n`;
            }
          } catch {
            whatsappMessage += `\n${fileName}\nPlease request the file from your consultant.\n`;
          }
        }

        whatsappMessage += `\n💡 Tap the links above to view or download the files.`;
      }

      const encoded = encodeURIComponent(whatsappMessage);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const url = isMobile
        ? `https://api.whatsapp.com/send?phone=${formatted}&text=${encoded}`
        : `https://web.whatsapp.com/send?phone=${formatted}&text=${encoded}`;

      const w = window.open(url, '_blank');
      if (w) w.focus();

      onSent?.();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to send WhatsApp message';
      setError(msg);
      alert(`❌ ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Send WhatsApp Message</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <p className="text-sm text-blue-700">
              <span className="font-semibold">Quote:</span> {quoteRef}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Send to Client Number
            </label>
            <div className="flex gap-2">
              <select
                value={sendToClient}
                onChange={(e) => setSendToClient(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                disabled={!clientPhone}
              >
                <option value="">
                  {clientPhone ? 'Select client phone number...' : 'No client phone number found'}
                </option>
                {clientPhone && <option value={clientPhone}>Client: {clientPhone}</option>}
              </select>
              <button
                type="button"
                onClick={handleAddClient}
                disabled={!sendToClient}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                  sendToClient
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add
              </button>
            </div>
          </div>

                              <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Send to Person of Interest Number
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Select a saved contact or type a custom phone number
            </p>
            <div className="flex gap-2">
              
              <input
                type="text"
                list="poi-options"
                value={customPoiNumber || sendToPoi}
                onChange={(e) => setCustomPoiNumber(e.target.value)}
                placeholder="Type or select a number..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPoi();
                  }
                }}
              />
              
              <datalist id="poi-options">
                {pois.map((p) => (
                  <option key={p.id} value={p.contact_number}>
                    {p.name} {p.surname}
                  </option>
                ))}
              </datalist>

              <button
                type="button"
                onClick={handleAddPoi}
                disabled={!(customPoiNumber || sendToPoi)}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                  (customPoiNumber || sendToPoi)
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients ({recipients.length})
            </label>
            {recipients.length === 0 ? (
              <div className="p-3 border border-gray-200 rounded-md bg-gray-50 text-gray-400 text-sm">
                No recipients added yet.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                {recipients.map((phone, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    {phone}
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipient(phone)}
                      className="ml-1 text-green-600 hover:text-green-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              rows={4}
              placeholder="WhatsApp message content..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Files</label>
            {isLoadingDocuments ? (
              <div className="text-gray-500 text-sm py-2">Loading documents...</div>
            ) : documents.length === 0 ? (
              <div className="text-gray-500 text-sm py-2">No documents found for this quote.</div>
            ) : (
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                {documents.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-start space-x-3 py-2 hover:bg-gray-50 px-2 rounded cursor-pointer select-none"
                    onClick={() => handleDocumentToggle(doc.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDocuments.includes(doc.id)}
                      // ✅ Remove onChange entirely to prevent double-firing
                      readOnly
                      className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500 pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.original_name || doc.file_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {doc.scope === 'client' ? 'Client Doc' : 'Quote Doc'} • {doc.document_type || 'Document'} • {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-md disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Send WhatsApp'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// Decline Quotation Modal
// ============================================================================
interface DeclineQuotationModalProps {
  isVisible: boolean;
  onClose: () => void;
  quoteId: string;
  quoteRef: string;
  onDeclined: () => void;
}

const DeclineQuotationModal: React.FC<DeclineQuotationModalProps> = ({
  isVisible,
  onClose,
  quoteId,
  quoteRef,
  onDeclined
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible) {
      setReason('');
      setError(null);
    }
  }, [isVisible]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/decline`,
        { reason: reason.trim() || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to decline quote');
      }

      onDeclined();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to decline quote';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-red-600">Decline Quotation</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-gray-700 text-sm">
            Are you sure you want to decline{' '}
            <span className="font-semibold">{quoteRef}</span>? This will set the
            quote status to <span className="font-semibold">declined</span>.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={4}
              placeholder="e.g. Client went with another insurer"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
            >
              {isSubmitting ? 'Declining...' : 'Decline Quotation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const stripExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
};

// ============================================================================
// Quote Notes Component (Adapted from ClientPersonalNotesTab)
// ============================================================================
interface QuoteNote {
  id?: number;
  quote_id: number;
  note_content: string;
  created_at?: string;
  updated_at?: string;
}

interface QuoteFullNotesProps {
  quoteId: string;
  clientId: string;
}

const QuoteFullNotes: React.FC<QuoteFullNotesProps> = ({ quoteId, clientId }) => {
  const { can } = usePermissions();
  const [notes, setNotes] = useState<QuoteNote[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New note form state
  const [newNoteContent, setNewNoteContent] = useState('');

  useEffect(() => {
    fetchNotesAndLogs();
  }, [quoteId]);

    const fetchNotesAndLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Fetch Notes and Audit Logs concurrently
      const [notesResult, logsResult] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_API_URL}/api/notes?quote_id=${quoteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          // ✅ Changed from ?quote_id= to /:quoteId
          `${process.env.REACT_APP_API_URL}/api/quote-activity-logs/${quoteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);

      // Handle Notes Result
      if (notesResult.status === 'fulfilled') {
        const notesData = Array.isArray(notesResult.value.data) 
          ? notesResult.value.data 
          : (notesResult.value.data?.data || []);
        setNotes(notesData);
      } else {
        console.error('Error fetching notes:', notesResult.reason);
      }

      // Handle Audit Logs Result
      if (logsResult.status === 'fulfilled') {
        const logsData = Array.isArray(logsResult.value.data) 
          ? logsResult.value.data 
          : (logsResult.value.data?.data || []);
        setAuditLogs(logsData);
      } else {
        console.error('Error fetching audit logs:', logsResult.reason);
        // We don't throw an error here so the notes still display
      }

    } catch (error: any) {
      console.error('Error in fetchNotesAndLogs:', error);
      setError('Failed to load notes and audit logs');
    }
  };

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newNoteContent.trim()) {
      setError('Content is required');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/notes`,
        {
          quote_id: parseInt(quoteId),
          client_id: parseInt(clientId),
          note_content: newNoteContent.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Note created successfully!');
      setNewNoteContent('');
      setIsCreateModalOpen(false);
      fetchNotesAndLogs(); // Refresh the list
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error creating note:', error);
      setError(error.response?.data?.message || 'Failed to create note');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setNewNoteContent('');
    setError('');
    setSuccess('');
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewNoteContent('');
    setError('');
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredNotes = () => {
    if (!searchTerm.trim()) return notes;
    const term = searchTerm.toLowerCase().trim();
    return notes.filter(note => 
      note.note_content?.toLowerCase().includes(term)
    );
  };

  const getFilteredAuditLogs = () => {
    if (!searchTerm.trim()) return auditLogs || [];
    const term = searchTerm.toLowerCase().trim();
    return (auditLogs || []).filter(log => 
      log.description?.toLowerCase().includes(term) ||
      log.performed_by?.toLowerCase().includes(term)
    );
  };

  const filteredNotes = getFilteredNotes();
  const filteredAuditLogs = getFilteredAuditLogs();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Quote Notes</h3>
        <div className="text-sm text-gray-500">
          Quote ID: {quoteId}
        </div>
      </div>

      {/* Create New Note Button and Search */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {can('notes', 'write') && (
            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Create New Note
            </button>
          )}
        </div>
  
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search notes & audit logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 px-3 py-2 pl-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <svg
              className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
          <div className="text-sm text-gray-500 whitespace-nowrap">
            {searchTerm ? (
              <span>Found {filteredNotes.length + filteredAuditLogs.length} result(s)</span>
            ) : (
              <span>{notes.length} note{notes.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Notes List */}
      <div className="space-y-4">
        {filteredNotes.length === 0 && !searchTerm && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">No notes created yet.</p>
            <p className="text-sm text-gray-400 mt-1">Click "Create New Note" to add your first note.</p>
          </div>
        )}
        {filteredNotes.length === 0 && searchTerm && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">No notes match your search.</p>
          </div>
        )}
        {filteredNotes.map((note) => (
          <div
            key={note.id}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
          >
            <div className="prose prose-sm max-w-none mb-4">
              <p className="text-gray-700 whitespace-pre-wrap">{note.note_content}</p>
            </div>
            
            <div className="text-xs text-gray-500 border-t pt-2 flex justify-between items-center">
              <div>
                <span>Created {formatDate(note.created_at)}</span>
                {note.updated_at && note.updated_at !== note.created_at && (
                  <span> • Updated {formatDate(note.updated_at)}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Audit Logs Section */}
      <div className="mt-8 border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-semibold">Audit Logs</h4>
          <button
            onClick={() => setShowAuditLogs(!showAuditLogs)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showAuditLogs ? 'Hide Audit Logs' : 'Show Audit Logs'}
          </button>
        </div>

        {showAuditLogs && (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {filteredAuditLogs.length === 0 && searchTerm && (
              <p className="text-gray-500 text-sm">No audit logs match your search.</p>
            )}
            {filteredAuditLogs.length === 0 && !searchTerm && (
              <p className="text-gray-500 text-sm">No changes recorded yet</p>
            )}
            {filteredAuditLogs.map((log) => (
              <div key={log.id} className="bg-gray-50 p-3 rounded border border-gray-200">
                <div className="text-sm text-gray-800 font-medium">
                  {log.description}
                </div>
                <div className="text-xs text-gray-500 mt-2 flex justify-between">
                  <span>
                    <span className="font-medium">By:</span> {log.performed_by || 'System'}
                  </span>
                  <span className="text-gray-400">
                    {formatDate(log.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Note Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Create New Note</h3>
              <button onClick={closeCreateModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleCreateNote} className="p-6 space-y-4">
              <div>
                <label htmlFor="noteContent" className="block text-sm font-medium text-gray-700 mb-1">
                  Content *
                </label>
                <textarea
                  id="noteContent"
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                  placeholder="Enter your note content..."
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !newNoteContent.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Creating...' : 'Create Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const QuoteDetailPage: React.FC = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // Quote master Email / WhatsApp / Decline modals
  const [isQuoteEmailModalVisible, setIsQuoteEmailModalVisible] = useState(false);
  const [isQuoteWhatsAppModalVisible, setIsQuoteWhatsAppModalVisible] = useState(false);
  const [isDeclineModalVisible, setIsDeclineModalVisible] = useState(false);
  const [productToView, setProductToView] = useState<{product: any, quote: QuoteSummary} | null>(null);
  const [isViewProductModalVisible, setIsViewProductModalVisible] = useState(false);
  const location = useLocation();
  const state = location.state as any;
  const searchParams = new URLSearchParams(location.search);
  
  const { id, clientId: paramClientId, quoteId: paramQuoteId } = useParams<{ id: string; clientId: string; quoteId: string }>();

  const clientIdFromQuery = searchParams.get('clientId');
  const clientIdFromState = state?.clientId || clientIdFromQuery || paramClientId

  const quoteIdFromQuery = searchParams.get('quoteId');
  const specificQuoteId = id || paramQuoteId || quoteIdFromQuery || (state as any)?.selectedQuoteId;

  const isExtractingProductsRef = useRef<{ [key: string]: boolean }>({});

// ✅ ADD DEBUG LOGGING
console.log('📍 QuoteDetailPage clientId extraction:', {
  clientIdFromState,
  clientIdFromQuery,
  paramClientId,
  specificQuoteId,
  fullUrl: window.location.href,
  searchParams: Object.fromEntries(searchParams)
});

  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  // Accept Quotation modal
  const [isAcceptQuotationModalOpen, setIsAcceptQuotationModalOpen] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  const [acceptFormData, setAcceptFormData] = useState<{
  id: string;
  name: string;
  type: 'administrator' | 'underwriter';
  premium: string;
  brokerFee: string;
  managementFee: string;
  insurerQuotationNumber: string;
}[]>([]);

  const [removalReason, setRemovalReason] = useState('');
  const [productToRemove, setProductToRemove] = useState<{quoteId: string, productId: string, productType: string, productName: string} | null>(null);
  // State to store removal reasons - load from localStorage on component mount
  const [removalReasons, setRemovalReasons] = useState<RemovalReason[]>([]);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'warning' | 'info'>('idle');
  const [saveMessage, setSaveMessage] = useState('');

  const [isAddProductModalVisible, setIsAddProductModalVisible] = useState(false);
  const [isPropertyModalVisible, setIsPropertyModalVisible] = useState(false);
  const [isVehicleModalVisible, setIsVehicleModalVisible] = useState(false);
  const [isHouseholdContentsModalVisible, setIsHouseholdContentsModalVisible] = useState(false);
  const [isTrailerCaravanModalVisible, setIsTrailerCaravanModalVisible] = useState(false);
  const [selectedTrailerCaravan, setSelectedTrailerCaravan] = useState<any>(null);
  const [isValueAddedModalVisible, setIsValueAddedModalVisible] = useState(false);
  const [isRiskItemsModalVisible, setIsRiskItemsModalVisible] = useState(false);
  const [isPreviousInsuranceModalVisible, setIsPreviousInsuranceModalVisible] = useState(false);
  const [previousInsuranceForm, setPreviousInsuranceForm] = useState({
    description: '',
    policyNumber: '',
    insurer: '',
    startDate: '',
    endDate: ''
  });
  const [previousInsurances, setPreviousInsurances] = useState<any[]>([]);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [previousClaim, setPreviousClaim] = useState({
    description: '',
    policyNumber: '',
    previousInsurer: '',
    claimDate: '',
    claimValue: ''
  });
  const [previousClaims, setPreviousClaims] = useState<any[]>([]);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [notes, setNotes] = useState<any[]>([]);
  // Person of Interest modal state
  const [isPersonModalVisible, setIsPersonModalVisible] = useState(false);
  // Notes display modal (activity log)
const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [personFormData, setPersonFormData] = useState<Partial<PersonOfInterest>>({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: 'active',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: [],
  });
  const [personErrors, setPersonErrors] = useState({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  });
  const [personsOfInterest, setPersonsOfInterest] = useState<PersonOfInterest[]>([]);
  const [isPreviousClaimModalVisible, setIsPreviousClaimModalVisible] = useState(false);
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);

  const [removedProducts, setRemovedProducts] = useState<Set<string>>(new Set());
  const [isDocumentsModalVisible, setIsDocumentsModalVisible] = useState(false);
  const [hasSavedInsurers, setHasSavedInsurers] = useState(false);
  const [savedPremiumValue, setSavedPremiumValue] = useState<string>('');
  const isJustSavedRef = useRef(false);
  const [isNewQuote, setIsNewQuote] = useState(false);

  // Open Previous Insurance Modal
  const handleOpenPreviousInsuranceModal = () => {
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    
    if (!currentQuoteId) {
      setSaveStatus('error');
      setSaveMessage('Please create or select a quote first');
      return;
    }
    
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    console.log('🔍 Navigating to previous insurance page with:', {
      quoteId: currentQuoteId,
      clientId: clientIdValue,
      returnUrl
    });
    
    navigate(`/add-previous-insurance/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  };

  // Open Claim Modal
  const handleOpenClaimModal = () => {
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    
    if (!currentQuoteId) {
      setSaveStatus('error');
      setSaveMessage('Please create or select a quote first');
      return;
    }
    
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    navigate(`/add-previous-claim/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  };

  // Open Note Modal
  const handleOpenNoteModal = () => {
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    
    if (!currentQuoteId) {
      setSaveStatus('error');
      setSaveMessage('Please create or select a quote first');
      return;
    }
    
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    navigate(`/add-note/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  };

  // Handle person input change
const handlePersonInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
  const { name, value } = e.target;
  setPersonFormData(prev => ({ ...prev, [name]: value }));
  setPersonErrors(prev => ({ ...prev, [name]: '' }));
};

// Handle driver licenses change
const handleDriverLicensesChange = (licenses: DriverLicense[]) => {
  setPersonFormData(prev => ({
    ...prev,
    drivers_license_codes: licenses
  }));
};

// Close person modal
const handleClosePersonModal = () => {
  setIsPersonModalVisible(false);
  setPersonFormData({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: [],
  });
  setPersonErrors({
    name: '',
    surname: '',
    email: '',
    designation: '',
    status: '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  });
};

// Save person to database
const savePersonToDatabase = async (personData: Partial<PersonOfInterest>, clientId: string) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    if (!clientId || isNaN(parseInt(clientId))) {
      throw new Error('Valid clientId is required');
    }
    
    const apiUrl = `${process.env.REACT_APP_API_URL}/api/persons-of-interest`;
    
    // Make sure drivers_license_codes is properly stringified
    const requestData = {
      clientId: clientId,
      ...personData,
      drivers_license_codes: JSON.stringify(personData.drivers_license_codes || [])
    };
    
    console.log('📤 Saving person with licenses:', requestData);
    
    const response = await axios.post(apiUrl, requestData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      timeout: 10000,
    });
    
    console.log('✅ Saved person response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error saving person:', error);
    throw error;
  }
};

// fetch persons of interest
const fetchPersonsOfInterest = async () => {
  console.log('🔵 fetchPersonsOfInterest called, clientIdFromState:', clientIdFromState);
  if (!clientIdFromState) return;
  
  try {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || '';
    
    const response = await fetch(`${API_URL}/api/persons-of-interest?clientId=${clientIdFromState}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Simplified mapping - keep whatever the API returns
    const mappedPersons = data.map((person: any) => {
      // Try to get licenses from either field name
      let licenses = [];
      
      if (person.drivers_license_codes && Array.isArray(person.drivers_license_codes)) {
        licenses = person.drivers_license_codes;
      } else if (person.drivers_license_code) {
        // If it's a string, try to parse it
        if (typeof person.drivers_license_code === 'string') {
          try {
            const parsed = JSON.parse(person.drivers_license_code);
            if (Array.isArray(parsed)) {
              licenses = parsed;
            }
          } catch {
            // Not JSON, leave as empty array
          }
        }
      }
      
      return {
        ...person,
        drivers_license_codes: licenses
      };
    });
    
    setPersonsOfInterest(mappedPersons);
  } catch (err) {
    console.error('❌ Error fetching persons of interest:', err);
  }
};

// Handle person submit
const handlePersonSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  const newErrors = {
    name: !personFormData.name ? 'Name is required' : '',
    surname: !personFormData.surname ? 'Surname is required' : '',
    email: !personFormData.email ? 'Email is required' : '',
    designation: !personFormData.designation ? 'Designation is required' : '',
    status: !personFormData.status ? 'Status is required' : '',
    contact_number: '',
    id_passport_number: '',
    date_of_birth: '',
    drivers_license_codes: '',
    server: '',
  };

  setPersonErrors(newErrors);

  const hasErrors = Object.values(newErrors).some(error => error !== '');
  if (hasErrors) {
    return;
  }

  if (!clientIdFromState || isNaN(parseInt(clientIdFromState))) {
    setPersonErrors((prev) => ({ ...prev, server: 'Valid clientId is required' }));
    return;
  }

  try {
    const savedPerson = await savePersonToDatabase(personFormData, clientIdFromState);
    setPersonsOfInterest(prev => [...prev, savedPerson]);
    setIsPersonModalVisible(false);
    
    // Refresh the persons list from database to ensure all are loaded
    await fetchPersonsOfInterest();
    
    setSaveStatus('success');
    setSaveMessage('Person added successfully!');
    
    setTimeout(() => {
      setSaveStatus('idle');
      setSaveMessage('');
    }, 3000);
  } catch (error) {
    console.error('❌ Error in handlePersonSubmit:', error);
    if (error instanceof Error) {
      setSaveStatus('error');
      setSaveMessage(`Failed to add person: ${error.message}`);
    } else {
      setSaveStatus('error');
      setSaveMessage('Failed to add person: Unknown error');
    }
  }
};

// Open person modal
const handleOpenPersonModal = () => {
  console.log('🔵 handleOpenPersonModal called, setting isPersonModalVisible to true');
  setIsPersonModalVisible(true);
};

  const [viewMode, setViewMode] = useState<'master' | 'detail'>('master');
  const [allQuotes, setAllQuotes] = useState<QuoteSummary[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<QuoteDetailData | null>(null);

   // ✅ ADD HERE: Stabilize quoteIds so the modal doesn't infinitely re-fetch
  const documentQuoteIds = React.useMemo(() => allQuotes.map(q => q.id), [allQuotes]);
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [user, setUser] = useState<{ id: number; email: string; username?: string } | null>(null);

  const [consultant, setConsultant] = useState<ConsultantData | null>(null);
  const [administrators, setAdministrators] = useState<AdminUnderwriterData[]>([]);
  const [underwriters, setUnderwriters] = useState<AdminUnderwriterData[]>([]);

  // ✅ NEW: Consultant reassignment state
  const [consultantsList, setConsultantsList] = useState<{ id: string; name: string }[]>([]);
  const [selectedConsultantId, setSelectedConsultantId] = useState<string>('');
  const [isSavingConsultant, setIsSavingConsultant] = useState(false);
  const [isLoadingConsultantsList, setIsLoadingConsultantsList] = useState(false);

  // ✅ NEW: Fetch consultants list for the dropdown
  useEffect(() => {
    const fetchConsultantsList = async () => {
      if (!can('quotes', 'write')) return; // Only fetch if they have write access
      setIsLoadingConsultantsList(true);
      try {
        const token = localStorage.getItem('token');
        // ✅ Use the exact endpoint that returns { first_name, last_name }
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/consultants`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data?.data || response.data || [];
        const formatted = data.map((c: any) => {
          // ✅ Combine first_name and last_name if name is not provided
          const firstName = c.first_name || c.name || '';
          const lastName = c.last_name || c.surname || '';
          const displayName = `${firstName} ${lastName}`.trim() || `Consultant ${c.id}`;
          
          return {
            id: c.id.toString(),
            name: displayName
          };
        });
        setConsultantsList(formatted);
      } catch (error) {
        console.error('Failed to fetch consultants list:', error);
      } finally {
        setIsLoadingConsultantsList(false);
      }
    };
    fetchConsultantsList();
  }, [can]);

  // ✅ NEW: Sync selected consultant when the quote data loads
  useEffect(() => {
    if (allQuotes.length > 0) {
      const currentQuote = allQuotes[0];
      if (currentQuote.consultant_id) {
        setSelectedConsultantId(currentQuote.consultant_id.toString());
      } else if (currentQuote.consultant?.id) {
        setSelectedConsultantId(currentQuote.consultant.id.toString());
      } else {
        setSelectedConsultantId('');
      }
    }
  }, [allQuotes]);
  
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const { client: clientFromHook, isLoading: isClientLoading } = useClientData(clientIdFromState);
  // State to store products for all quotes
  const [allQuoteProducts, setAllQuoteProducts] = useState<{[quoteId: string]: any[]}>({});

  const [isWaterCraftModalVisible, setIsWaterCraftModalVisible] = useState(false);

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showPropertyDetailsModal, setShowPropertyDetailsModal] = useState(false);
  const [propertyModalMode, setPropertyModalMode] = useState<'view' | 'edit'>('view');

  const [selectedHouseholdContents, setSelectedHouseholdContents] = useState<any>(null);
  const [showHouseholdContentsModal, setShowHouseholdContentsModal] = useState(false);
  const [householdModalMode, setHouseholdModalMode] = useState<'view' | 'edit'>('view');

  const [rfqDocuments, setRfqDocuments] = useState<{ id: string; file_type: string; url?: string }[]>([]);

  // Fetch logged-in user
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) setUser(JSON.parse(storedUser));
  }, []);

  // Add this near your other useEffect hooks
useEffect(() => {
  if (clientFromHook) {
    
    // Specifically check for address properties
    const addressFields = [
      'address_line_1', 'address_line_2', 'addressLine1', 'addressLine2',
      'suburb', 'city_town', 'city', 'cityTown',
      'postal_code', 'postalCode', 'zipCode', 'zip_code',
      'country', 'province', 'region', 'state',
      'residence_address'
    ];
    
    addressFields.forEach(field => {
      // @ts-ignore - temporarily ignore TypeScript
      if (clientFromHook[field]) {                
      }
    });
  }
}, [clientFromHook]);

  // Load and sync removal data on component mount
  useEffect(() => {
  if (!clientIdFromState) return;  
  
  const savedRemovedProducts = localStorage.getItem(`removedProducts_${clientIdFromState}`);
  const savedRemovalReasons = localStorage.getItem(`removalReasons_${clientIdFromState}`);  

  try {
    // Parse removed products
    if (savedRemovedProducts) {
      const removedProductsArray = JSON.parse(savedRemovedProducts);
      if (Array.isArray(removedProductsArray)) {
        setRemovedProducts(new Set(removedProductsArray));        
      }
    }
    
    // Parse removal reasons - ONLY set if we have valid data
    if (savedRemovalReasons && savedRemovalReasons !== '[]') {
      const removalReasonsData = JSON.parse(savedRemovalReasons);
      if (Array.isArray(removalReasonsData) && removalReasonsData.length > 0) {        
        setRemovalReasons(removalReasonsData);
      } else {

      }
    } else {
    
    }
    
  } catch (error) {
    console.error('❌ Error initializing removal data:', error);
    // Only set removed products, don't touch removalReasons
    if (savedRemovedProducts) {
      try {
        const removedProductsArray = JSON.parse(savedRemovedProducts);
        setRemovedProducts(new Set(removedProductsArray));
      } catch (e) {
        setRemovedProducts(new Set());
      }
    }
  }
}, [clientIdFromState]);

  // Load removed products from localStorage
  useEffect(() => {
  if (clientIdFromState && removalReasons && Array.isArray(removalReasons)) {
    // Only save if we actually have removal reasons, not empty arrays from initialization
    if (removalReasons.length > 0) {
      console.log('💾 Auto-saving removal reasons to localStorage:', removalReasons);
      localStorage.setItem(`removalReasons_${clientIdFromState}`, JSON.stringify(removalReasons));
    } else {
      console.log('ℹ️ Skipping auto-save: removal reasons array is empty');
    }
  }
}, [removalReasons, clientIdFromState]);

  // Debug useEffect to track state changes
  useEffect(() => {
    console.log('🔍 DEBUG - Current state:', {
      allQuotesCount: allQuotes.length,
      allQuotesIds: allQuotes.map(q => q.id),
      removalReasonsCount: removalReasons.length,
      removalReasons: removalReasons,
      removedProductsCount: removedProducts.size,
      removedProducts: Array.from(removedProducts),
      loading
    });
    
    // Check if we should show removal reasons
    if (allQuotes.length > 0 && removalReasons.length > 0) {
      const currentQuoteIds = allQuotes.map(quote => quote.id);
      const filteredRemovalReasons = removalReasons.filter(removal => 
        currentQuoteIds.includes(removal.quoteId)
      );
      console.log('🔍 DEBUG - Filtered removal reasons for current quotes:', filteredRemovalReasons);
    }
  }, [allQuotes, removalReasons, removedProducts, loading]);

  const handleSendSignatureLink = async (quoteId: string, clientId: string) => {
    try {
      setSaveStatus('loading');
      setSaveMessage('Generating secure signature link and sending email to client...');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get client email
      const clientEmail = clientFromHook?.email;
      if (!clientEmail) {
        setSaveStatus('error');
        setSaveMessage('Client email address is required');
        return;
      }

      // Get the current quote from allQuotes
      const currentQuote = allQuotes.find(q => q.id === quoteId);
      if (!currentQuote) {
        throw new Error('Quote not found');
      }

      // Get the accept data from localStorage
      const acceptData = JSON.parse(localStorage.getItem('proposalAcceptData') || '{}');

            const backendBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      // ✅ Include the insurer reference so /api/proposal/view/:quoteId can
      // render the administrator/underwriter name in the "additional services" block.
      // Priority: insurerQuotationNumber → quotation_ref → acceptData.insurers[0] → quote reference.
      const insurerRef =
        acceptData?.insurers?.[0]?.insurerQuotationNumber ||
        acceptData?.insurers?.[0]?.quotation_ref ||
        acceptData?.quotation_ref ||
        acceptData?.insurerQuotationNumber ||
        currentQuote.referenceNumber ||
        currentQuote.reference ||
        String(quoteId);

      const signatureLink =
        `${backendBaseUrl}/api/proposal/view/${quoteId}?quoteNumber=${encodeURIComponent(insurerRef)}`;

      // LOG: Log the signature link email attempt
    const logId = await logEmailToDatabase({
      clientId: clientIdFromState!,
      recipient: clientEmail,
      recipientType: 'client',
      subject: `Signature Required: Quote ${currentQuote?.referenceNumber || quoteId}`,
      quoteId: quoteId,
      emailType: 'signature_link',
      status: 'pending',
      metadata: {
        signatureLink: signatureLink,
        expiryHours: 48,
        premiumAmount: acceptData.premium || '',
        brokerFee: acceptData.brokerFee || '',
        managementFee: acceptData.managementFee || ''
      }
    });

      // Determine the consultant to save
      // 1. Try from acceptData (if it was passed from create quote page)
      // 2. Try from the current quote state
      // 3. Fallback to current user ID
      const consultantIdToSave = 
        acceptData.consultant_id || 
        allQuotes[0]?.consultant_id || 
        allQuotes[0]?.consultant?.id || 
        user?.id;

      // Call backend to generate signature link and send email
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/send-signature-link`,
        {
          clientEmail: clientEmail,
          clientName: clientFromHook ? `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim() : 'Client',
          quoteId: quoteId,
          clientId: clientId,
          consultantId: consultantIdToSave,
          acceptData: acceptData,
          signatureLink: signatureLink,
          premiumAmount: acceptData.premium || '',
          brokerFee: acceptData.brokerFee || '',
          managementFee: acceptData.managementFee || '',
          expiryHours: 48
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );

      console.log('📧 Signature link response:', response.data);

      // LOG: Update log status based on response
    if (logId) {
      if (response.data.success) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-logs/${logId}/status`,
          { status: 'sent' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-logs/${logId}/status`,
          { status: 'failed', errorMessage: response.data.message || 'Unknown error' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    }

      if (response.data.success) {
        setSaveStatus('success');
        setSaveMessage(`✅ Secure signature link sent to ${clientEmail}. Link expires in 48 hours.`);
        
        localStorage.removeItem('proposalAcceptData');
        
        setTimeout(() => {
          navigate(`/quotes/${quoteId}`, {
            state: { clientId, selectedQuoteId: quoteId }
          });
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Failed to send signature link');
      }

    } catch (error: any) {
      console.error('❌ Error sending signature link:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = error.response?.data?.message || error.message || 'Failed to send signature link';
      
      setSaveStatus('error');
      setSaveMessage(errorMessage);
      
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);
    }
  };

  // Handle browser navigation and persistence
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Ensure data is saved before page unload
      if (clientIdFromState) {
        localStorage.setItem(`removalReasons_${clientIdFromState}`, JSON.stringify(removalReasons));
        const removedArray = Array.from(removedProducts);
        localStorage.setItem(`removedProducts_${clientIdFromState}`, JSON.stringify(removedArray));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [clientIdFromState, removalReasons, removedProducts]);

  const getCompleteProductModalDetails = (product: any): string => {
  if (!product || !product.data) return '';
  
  const data = product.data;
  let details = '';
  
  switch(product.type) {
    case 'property':
      details = `
   Address: ${data.addressLine1 || ''} ${data.addressLine2 || ''}
   Suburb: ${data.suburb || ''}
   City: ${data.city || ''}
   Property Type: ${data.propertyType || ''}
   Residence Type: ${data.residenceType || ''}
   Cover Type: ${data.coverType || ''}
   Insured Value: R ${(data.insuredValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Building Age: ${data.buildingAge || ''} years
   Premium: ${data.premium || 'N/A'}
   Excess: R ${(data.excess || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      
      // Add boolean fields
      const propertyBooleans = [
        { label: 'Swimming Pool', value: data.hasSwimmingPool },
        { label: 'Motorized Gate', value: data.hasMotorizedGate },
        { label: 'Unoccupied >60 Days', value: data.isUnoccupiedMoreThan60Days },
        { label: 'In Commune', value: data.isCommune },
        { label: 'Under Construction', value: data.underConstruction },
        { label: 'Steep Slope', value: data.builtOnSteepSlope },
        { label: 'Retaining Walls', value: data.hasExcessiveRetainingWalls },
        { label: 'Near Water Body', value: data.nearWaterBody },
        { label: 'Rising Damp', value: data.hasRisingDamp },
        { label: 'Wall Cracks', value: data.hasWallCracks },
        { label: 'Subsidence History', value: data.hasSubsidenceHistory },
        { label: 'Outbuilding', value: data.hasOutbuilding },
        { label: 'Perimeter Fence', value: data.hasPerimeterFence },
        { label: 'Electric Fence', value: data.hasElectricFence },
        { label: 'Thatch Roof', value: data.hasThatchRoof },
        { label: 'Accidental Damage Cover', value: data.hasAccidentalDamageCover },
        { label: 'Power Surge Cover', value: data.hasPowerSurgeCover }
      ];
      
      details += '\n   Security Features:';
      propertyBooleans.forEach(item => {
        if (item.value) details += `\n     - ${item.label}: Yes`;
      });
      
      // Add materials
      if (data.wallMaterials && data.wallMaterials.length > 0) {
        details += `\n   Wall Materials: ${Array.isArray(data.wallMaterials) ? data.wallMaterials.join(', ') : data.wallMaterials}`;
      }
      
      break;
      
    case 'vehicle':
      // Extract all fields with proper fallbacks
      const postalCode = data.postal_code || data.postalCode || '';
      const propertyType = data.property_type || data.propertyType || 'N/A';
      const residenceType = data.residence_type || data.residenceType || 'N/A';
      const coverType = data.cover_type || data.coverType || '';
      const vehicleType = data.vehicle_type || data.vehicleType || '';
      const registrationNumber = data.registration_number || data.registrationNumber || '';
      const make = data.make || '';
      const model = data.model || '';
      const year = data.year || '';
      const insuredValue = data.insured_value || data.insuredValue || 0;
      const variant = data.variant || '';
      const color = data.color || '';
      const vinNumber = data.vin_number || data.vinNumber || '';
      const engineNumber = data.engine_number || data.engineNumber || '';
      const mmcode = data.mmcode || '';
      const vehicleUsage = data.usage || data.vehicle_usage || '';
      const safetyFeatures = data.safety_features || data.safetyFeatures || '';
      const excessBuyBack = data.excess_buy_back || data.excessBuyBack || false;
      const creditShortfall = data.credit_shortfall || data.creditShortfall || false;
      const carHire = data.car_hire || data.carHire || false;
      const dayParking = data.day_parking_location || data.dayParkingLocation || '';
      const nightParking = data.night_parking_location || data.nightParkingLocation || '';
      const registeredOwner = data.registered_owner || data.registeredOwner || '';
      const regularDriver = data.regular_driver || data.regularDriver || '';
      const otherDrivers = data.other_drivers || data.otherDrivers || '';
      
      // Build details in the exact order requested
      details = `
   Postal Code: ${postalCode}
   Property Type: ${propertyType}
   Residence Type: ${residenceType}
   Cover Type: ${coverType}
   
   Vehicle Type: ${vehicleType}
   Reg. Number: ${registrationNumber}
   Make: ${make}
   Model: ${model}
   Year: ${year}
   Insured Value: R ${insuredValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Variant: ${variant}
   Color: ${color}
   VIN Number: ${vinNumber}
   Engine Number: ${engineNumber}
   MMCode: ${mmcode}
   Vehicle Usage: ${vehicleUsage}
   Cover Type: ${coverType}
   Safety Features: ${safetyFeatures}
   Excess Buy Back: ${excessBuyBack ? 'Yes' : 'No'}
   Credit Shortfall: ${creditShortfall ? 'Yes' : 'No'}
   Car Hire: ${carHire ? 'Yes' : 'No'}
   Day Parking Location: ${getParkingLocationName(dayParking)}
   Night Parking Location: ${getParkingLocationName(nightParking)}
   
   Registered Owner: ${getPersonName(registeredOwner)}
   Regular Driver: ${getPersonName(regularDriver)}
   Other Drivers: ${getPersonName(otherDrivers)}`;
      
      break;
      
    case 'risk_item':
      details = `
   Item Type: ${data.itemType || data.item_type || ''}
   Item Name: ${data.itemName || data.item_name || ''}
   Serial Number: ${data.serialNumber || data.serial_number || ''}
   Description: ${data.description || ''}
   Insured Value: R ${(data.insuredValue || data.insured_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Cover Type: ${data.coverType || data.cover_type || ''}
   No Claims Bonus: ${data.noClaimsBonus || data.no_claims_bonus || 'N/A'}
   Premium: ${data.premium || 'N/A'}
   Excess: R ${(data.excess || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      break;
      
    case 'household':
      details = `
   Address: ${data.addressLine1 || ''} ${data.addressLine2 || ''}
   Suburb: ${data.suburb || ''}
   City: ${data.cityTown || ''}
   Property Type: ${data.propertyType || ''}
   Residence Type: ${data.residenceType || ''}
   Cover Type: ${data.coverType || ''}
   Insured Value: R ${(data.insuredValue || data.insured_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Years Lived: ${data.yearsLivedOnProperty || ''}
   Premium: ${data.premium || 'N/A'}
   Excess: R ${(data.excess || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      
      // Add household boolean fields
      const householdBooleans = [
        { label: 'Conducts Business', value: data.conductsBusiness },
        { label: 'Surveillance Camera', value: data.hasSurveillanceCamera },
        { label: 'External Sliding Door', value: data.hasExternalSlidingDoor },
        { label: 'Security Gate', value: data.hasSecurityGate },
        { label: 'In Security Complex', value: data.inSecurityComplex },
        { label: 'Alarm Records', value: data.hasAlarmRecords },
        { label: 'Neighborhood Watch', value: data.hasNeighborhoodWatch },
        { label: 'Accidental Damage Cover', value: data.hasAccidentalDamageCover },
        { label: 'Power Surge Cover', value: data.hasPowerSurgeCover }
      ];
      
      details += '\n   Features:';
      householdBooleans.forEach(item => {
        if (item.value) details += `\n     - ${item.label}: Yes`;
      });
      break;
      
    case 'water_craft':
      details = `
   Vessel Name: ${data.vesselName || ''}
   Craft Type: ${data.craftType || ''}
   Year: ${data.year || ''}
   Hull Make: ${data.hullMake || ''}
   Hull Length: ${data.hullLength || ''}
   Vessel Usage: ${data.vesselUsage || ''}
   Total Sum Insured: R ${(data.totalSumInsured || data.total_sum_insured || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Premium: ${data.premium || data.totalMonthlyPremium || 'N/A'}`;
      break;
      
    case 'trailer':
      details = `
   Type: ${data.type || ''}
   Description: ${data.description || ''}
   Insured Value: R ${(data.insuredValue || data.insured_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Premium: ${data.premium || 'N/A'}`;
      break;
      
    case 'value_added':
      details = `
   Product Type: ${data.productType || ''}
   Description: ${data.description || ''}
   Value: R ${(data.value || data.insuredValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
   Premium: ${data.premium || 'N/A'}`;
      break;
      
    default:
      details = `\n   Type: ${product.type}\n   Value: R ${(product.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  
  return details;
};

  const handleEmailQuote = async (withPDF: boolean = true) => {
    try {
      setSaveStatus('loading');
      setSaveMessage(withPDF ? 'Generating PDF and preparing email...' : 'Preparing email...');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get the current quote
      if (allQuotes.length === 0) {
        throw new Error('No quotes available');
      }

      const currentQuote = allQuotes[0];
      const quoteId = currentQuote.id;

      console.log('📧 Email button clicked for quote:', quoteId);

      // Get email recipients
      const emailRecipients: string[] = [];
      const adminQuotationEmails = administrators
        .filter(admin => {
          const hasQuotationsEmail = admin.email !== undefined && 
                                    admin.email !== null && 
                                    admin.email.trim() !== '';
          return hasQuotationsEmail;
        })
        .map(admin => admin.email!.trim());

      if (adminQuotationEmails.length === 0) {
        setSaveStatus('error');
        setSaveMessage('No quotation email addresses found for administrators. Please check administrator settings.');
        return;
      }

      emailRecipients.push(...adminQuotationEmails);

      // Get ALL products from ALL quotes
      const allProductsList: any[] = [];
      Object.values(allQuoteProducts).forEach(products => {
        products.forEach(product => {
          const productKey = `${product.quoteId}-${product.id}-${product.type}`;
          if (!removedProducts.has(productKey)) {
            allProductsList.push(product);
          }
        });
      });

      // ========== IMPORTANT: Prepare data for PDF generation ==========
      // Generate products summary FIRST
      const quoteSummary = generateDetailedProductsSummary();

      const pdfData = {
        quoteReference: currentQuote.referenceNumber || currentQuote.reference,
        client_name: clientFromHook ? `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim() : 'Client',
        // ADD THESE FIELDS FROM QUOTE INFORMATION CARD:
        client_number: clientFromHook?.client_number || '', // Client Number
        id_passport_number: clientFromHook?.id_passport_number || '', // Identification Number
        // END OF ADDED FIELDS
        quote_date: formatDate(currentQuote.createdDate || currentQuote.created_at),
        total_products: getTotalProductsCount(),
        quote_summary: quoteSummary, // Now quoteSummary is defined!
        // Add flags for backend
        hide_total_insured_value: true,
        show_full_details: true,
        pdf_section_title: "Full Insured Item Details",
        // Keep administrators and underwriters in data (backend will ignore them in PDF)
        administrators: administrators.map(admin => ({
          name: admin.name,
          quotation_status: admin.quotation_status || 'active',
          premium: admin.premium || ''
        })),
        underwriters: underwriters.map(uw => ({
          name: uw.name,
          quotation_status: uw.quotation_status || 'active',
          premium: uw.premium || ''
        }))
      };

      // ========== Prepare COMPLETE email data with PDF flag ==========
      const emailData = {
        quote_id: quoteId,
        recipients: emailRecipients,
        subject: `Quotation - ${currentQuote.referenceNumber || currentQuote.reference}`,
        client_name: pdfData.client_name,
        client_email: clientFromHook?.email || '',
        client_phone: clientFromHook?.mobile_number || '',
        // ⚠️ CHANGE: Only custom message, no automatic quote summary
        
        // Remove these if they're being added to email body automatically:
        // total_insured_value: pdfData.total_insured_value,
        // total_products: pdfData.total_products,
        // quote_reference: pdfData.quoteReference,
        // quote_date: pdfData.quote_date,
        // administrators: pdfData.administrators,
        // underwriters: pdfData.underwriters,
        include_pdf: withPDF,
        pdf_data: withPDF ? pdfData : null, // PDF will contain all the quote details
        // Add flag for backend
        generate_full_details_pdf: true
      };

      // Send the email with PDF data
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/send-email`,
        emailData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // Increase timeout for PDF generation
        }
      );

      if (response.data.success) {
        setSaveStatus('success');
        if (withPDF && response.data.withPdf !== false) {
          setSaveMessage(`Email with PDF sent successfully to administrator!`);
        } else {
          setSaveMessage(`Email sent successfully to administrator!`);
        }
        
        if (adminQuotationEmails.length > 0) {
          setTimeout(() => {
            if (withPDF && response.data.withPdf !== false) {
              setSaveMessage(`Quote email with PDF sent to: ${adminQuotationEmails.join(', ')}`);
            } else {
              setSaveMessage(`Quote email sent to: ${adminQuotationEmails.join(', ')}`);
            }
          }, 2000);
        }
      } else if (response.data.needsConfiguration) {
        setSaveStatus('warning');
        setSaveMessage('⚠️ Email configuration required. Please set up SMTP settings in Email Configuration.');
        
        setTimeout(() => {
          if (window.confirm('Would you like to configure email settings now?')) {
            navigate('/email-settings');
          }
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to send email');
      }

    } catch (error: any) {
      console.error('❌ Error sending email:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      let errorMessage = error.response?.data?.message || error.message || 'Failed to send email';
      
      setSaveStatus('error');
      setSaveMessage(errorMessage);
      
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 5000);
    }
  };

// Generate FULL product details for PDF
const generateDetailedProductsSummary = (): string => {
  // Get all products from all quotes
  const allProductsList: any[] = [];
  Object.values(allQuoteProducts).forEach(products => {
    products.forEach(product => {
      const productKey = `${product.quoteId}-${product.id}-${product.type}`;
      if (!removedProducts.has(productKey)) {
        allProductsList.push(product);
      }
    });
  });

  if (allProductsList.length === 0) {
    return 'No products available';
  }

  let fullDetails = '';
  
  // Group by quote
  const productsByQuote: {[key: string]: any[]} = {};
  allProductsList.forEach(product => {
    if (!productsByQuote[product.quoteId]) {
      productsByQuote[product.quoteId] = [];
    }
    productsByQuote[product.quoteId].push(product);
  });

  Object.entries(productsByQuote).forEach(([qId, products]) => {
    const quote = allQuotes.find(q => q.id === qId);
    fullDetails += `\nQuote: ${quote?.referenceNumber || quote?.reference || `Quote-${qId}`}\n`;
    fullDetails += '='.repeat(60) + '\n';
    
    products.forEach((product, index) => {
      fullDetails += `\n${index + 1}. ${product.name}\n`;
      fullDetails += `   Type: ${getProductTypeDisplay(product.type)}\n`;
      
      // Get COMPLETE product details including ALL modal fields
      const productModalDetails = getCompleteProductModalDetails(product);
      if (productModalDetails) {
        fullDetails += productModalDetails;
      }
      
      fullDetails += '\n' + '-'.repeat(60) + '\n';
    });
  });

  return fullDetails.trim();
};

  // Add this function to handle viewing property details
  const handleViewPropertyDetails = (product: any, quote: QuoteSummary) => {
    // Set the selected property
    setSelectedProperty(product.data as Property);
    setPropertyModalMode('view');
    setShowPropertyDetailsModal(true);
  };

  // Add this function to handle saving updated property
  const handleUpdateProperty = async (updatedProperty: Property) => {
    try {
      console.log('💾 Saving updated property:', updatedProperty);
      
      const quoteId = productToView?.quote.id;
      if (!quoteId) {
        console.error('No quote ID found');
        setSaveStatus('error');
        setSaveMessage('Failed to update: No quote ID');
        return;
      }
      
      // Update local state immediately
      setAllQuoteProducts(prev => {
        const currentProducts = prev[quoteId] || [];
        const updatedProducts = currentProducts.map(p => {
          if (p.id === updatedProperty.id) {
            return {
              ...p,
              name: updatedProperty.propertyType || p.name,
              value: updatedProperty.insuredValue || p.value,
              description: formatPropertyDescription(updatedProperty),
              data: { 
                ...p.data, 
                ...updatedProperty,
                // ✅ Ensure arrays are preserved
                wallMaterials: updatedProperty.wallMaterials || [],
                roofMaterials: updatedProperty.roofMaterials || [],
                floorMaterials: updatedProperty.floorMaterials || [],
                otherSurroundingAreas: updatedProperty.otherSurroundingAreas || [],
              }
            };
          }
          return p;
        });
        return { ...prev, [quoteId]: updatedProducts };
      });

      // Update backend
      if (updatedProperty.id && !updatedProperty.id.toString().startsWith('temp-')) {
        const token = localStorage.getItem('token');
        
        // ✅ CRITICAL: Convert arrays to JSON strings
        const backendData = {
          address_line_1: updatedProperty.addressLine1 || '',
          address_line_2: updatedProperty.addressLine2 || '',
          suburb: updatedProperty.suburb || '',
          city: updatedProperty.city || '',
          postal_code: updatedProperty.postalCode || '',
          region: updatedProperty.region || '',
          country: updatedProperty.country || 'South Africa',
          property_type: updatedProperty.propertyType || '',
          residence_type: updatedProperty.residenceType || '',
          building_age: updatedProperty.buildingAge || '',
          situated_at: updatedProperty.situatedAt || '',
          cover_type: updatedProperty.coverType || '',
          insured_value: updatedProperty.insuredValue || 0,
          has_thatch_roof: updatedProperty.hasThatchRoof === true,
          thatch_less_than_5m: updatedProperty.thatchLessThan5m === true,
          thatch_sabs_conductor: updatedProperty.thatchSabsConductor === true,
          thatch_treated: updatedProperty.thatchTreated === true,
          has_accidental_damage_cover: updatedProperty.hasAccidentalDamageCover === true,
          accidental_damage_cover_value: updatedProperty.accidentalDamageCoverValue || 0,
          has_power_surge_cover: updatedProperty.hasPowerSurgeCover === true,
          power_surge_cover_value: updatedProperty.powerSurgeCoverValue || 0,
          has_swimming_pool: updatedProperty.hasSwimmingPool === true,
          has_motorized_gate: updatedProperty.hasMotorizedGate === true,
          is_unoccupied_more_than_60_days: updatedProperty.isUnoccupiedMoreThan60Days === true,
          is_commune: updatedProperty.isCommune === true,
          under_construction: updatedProperty.underConstruction === true,
          built_on_steep_slope: updatedProperty.builtOnSteepSlope === true,
          has_excessive_retaining_walls: updatedProperty.hasExcessiveRetainingWalls === true,
          near_water_body: updatedProperty.nearWaterBody === true,
          has_rising_damp: updatedProperty.hasRisingDamp === true,
          has_wall_cracks: updatedProperty.hasWallCracks === true,
          has_subsidence_history: updatedProperty.hasSubsidenceHistory === true,
          has_outbuilding: updatedProperty.hasOutbuilding === true,
          has_perimeter_fence: updatedProperty.hasPerimeterFence === true,
          has_electric_fence: updatedProperty.hasElectricFence === true,
          // ✅ CRITICAL: Convert arrays to JSON strings
          wall_materials: JSON.stringify(updatedProperty.wallMaterials || []),
          roof_materials: JSON.stringify(updatedProperty.roofMaterials || []),
          floor_materials: JSON.stringify(updatedProperty.floorMaterials || []),
          other_surrounding_areas: JSON.stringify(updatedProperty.otherSurroundingAreas || []),
          premium: updatedProperty.premium || '',
          excess: updatedProperty.excess || 0,
          no_claim_bonus: updatedProperty.noClaimBonus || '',
          quote_id: quoteId
        };
        
        console.log('📤 Sending to backend (arrays as JSON strings):', backendData);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/properties/${updatedProperty.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(backendData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update property');
        }
        
        const savedProperty = await response.json();
        console.log('✅ Backend update successful:', savedProperty);
      }

      setSaveStatus('success');
      setSaveMessage('Property updated successfully!');
      
      setShowPropertyDetailsModal(false);
      setSelectedProperty(null);
      
      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 3000);
      
    } catch (error: any) {
      console.error('❌ Error updating property:', error);
      setSaveStatus('error');
      setSaveMessage(error.message || 'Failed to update property');
    }
  };

  // Add this function to switch from view to edit mode
  const handleEditProperty = () => {
    setPropertyModalMode('edit');
  };

  // Add this function to switch from view to edit mode for household contents
  const handleEditHouseholdContents = () => {
    setHouseholdModalMode('edit');
  };

  const mapHouseholdContentsData = (apiData: any): HouseholdContents => {
  return {
    id: apiData.id,
    // Map from snake_case to camelCase
    yearsLivedOnProperty: apiData.years_lived_on_property || apiData.yearsLivedOnProperty,
    burglarBarType: apiData.burglar_bar_type || apiData.burglarBarType,
    dayTimeOccupantType: apiData.day_time_occupant_type || apiData.dayTimeOccupantType,
    coverType: apiData.cover_type || apiData.coverType,
    insuredValue: apiData.insured_value || apiData.insuredValue,
    conductsBusiness: apiData.conducts_business || apiData.conductsBusiness || false,
    hasSurveillanceCamera: apiData.has_surveillance_camera || apiData.hasSurveillanceCamera || false,
    hasExternalSlidingDoor: apiData.has_external_sliding_door || apiData.hasExternalSlidingDoor || false,
    hasSecurityGate: apiData.has_security_gate || apiData.hasSecurityGate || false,
    inSecurityComplex: apiData.in_security_complex || apiData.inSecurityComplex || false,
    hasAlarmRecords: apiData.has_alarm_records || apiData.hasAlarmRecords || false,
    hasNeighborhoodWatch: apiData.has_neighborhood_watch || apiData.hasNeighborhoodWatch || false,
    
    // Add these crucial fields for cover values
    hasAccidentalDamageCover: apiData.has_accidental_damage_cover || apiData.hasAccidentalDamageCover || false,
    accidentalDamageValue: apiData.accidental_damage_value || apiData.accidentalDamageValue || 0, // ✅ ADD THIS
    hasPowerSurgeCover: apiData.has_power_surge_cover || apiData.hasPowerSurgeCover || false,
    powerSurgeValue: apiData.power_surge_value || apiData.powerSurgeValue || 0, // ✅ ADD THIS
    
    alarmSystemType: apiData.alarm_system_type || apiData.alarmSystemType,
    perimeterType: apiData.perimeter_type || apiData.perimeterType,
    addressLine1: apiData.address_line_1 || apiData.addressLine1,
    addressLine2: apiData.address_line_2 || apiData.addressLine2,
    suburb: apiData.suburb,
    cityTown: apiData.city_town || apiData.cityTown,
    postalCode: apiData.postal_code || apiData.postalCode,
    region: apiData.region,
    country: apiData.country,
    propertyType: apiData.property_type || apiData.propertyType,
    residenceType: apiData.residence_type || apiData.residenceType,
    buildingAge: apiData.building_age || apiData.buildingAge,
    situatedType: apiData.situated_type || apiData.situatedType,
    hasSwimmingPool: apiData.has_swimming_pool || apiData.hasSwimmingPool || false,
    hasMotorizedGate: apiData.has_motorized_gate || apiData.hasMotorizedGate || false,
    isPropertyUnoccupied: apiData.is_property_unoccupied || apiData.isPropertyUnoccupied || false,
    isCommune: apiData.is_commune || apiData.isCommune || false,
    underConstruction: apiData.under_construction || apiData.underConstruction || false,
    builtOnSteepSlope: apiData.built_on_steep_slope || apiData.builtOnSteepSlope || false,
    hasRetainingWalls: apiData.has_retaining_walls || apiData.hasRetainingWalls || false,
    nearWaterBody: apiData.near_water_body || apiData.nearWaterBody || false,
    hasRisingDamp: apiData.has_rising_damp || apiData.hasRisingDamp || false,
    hasWallCracks: apiData.has_wall_cracks || apiData.hasWallCracks || false,
    historyOfLandslip: apiData.history_of_landslip || apiData.historyOfLandslip || false,
    hasOutbuilding: apiData.has_outbuilding || apiData.hasOutbuilding || false,
    hasPerimeterFence: apiData.has_perimeter_fence || apiData.hasPerimeterFence || false,
    hasElectricFence: apiData.has_electric_fence || apiData.hasElectricFence || false,
    hasThatchRoof: apiData.has_thatch_roof || apiData.hasThatchRoof || false,
    wallMaterials: apiData.wall_materials || apiData.wallMaterials,
    roofMaterials: apiData.roof_materials || apiData.roofMaterials,
    floorMaterials: apiData.floor_materials || apiData.floorMaterials,
    otherSurroundingArea: apiData.other_surrounding_area || apiData.otherSurroundingArea,
    premium: apiData.premium,
    excess: apiData.excess,
    noClaimBonus: apiData.no_claim_bonus || apiData.noClaimBonus,
  };
};

  // Function to handle viewing product details
  const handleViewProductDetails = (product: any, quote: QuoteSummary) => {
  console.log('🔍 Viewing product details:', { product, quote });
  
  const currentQuoteId = quote.id;
  const clientIdValue = clientIdFromState || quote.client_id;
  const productId = product.id;
  const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
  
  // Navigate to the appropriate edit page based on product type
  switch (product.type) {
    case 'property':
      console.log('📦 Passing property data to edit page - FULL DATA:', product.data);
      console.log('📦 Material arrays in product.data:', {
        wallMaterials: product.data.wallMaterials,
        roofMaterials: product.data.roofMaterials,
        floorMaterials: product.data.floorMaterials,
        otherSurroundingAreas: product.data.otherSurroundingAreas
      });
      
      navigate(`/add-property/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`, {
        state: {
          existingPropertyData: product.data,
          isEditing: true,
          clientId: clientIdValue,
          fromMasterPage: true
        }
      });
      break;
      break;
    case 'household':
      navigate(`/add-household-contents/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'vehicle':
      navigate(`/add-vehicle/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'trailer':
      navigate(`/add-trailer-caravan/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'value_added':
      navigate(`/add-value-added/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'water_craft':
      navigate(`/add-water-craft/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'risk_item':
      navigate(`/add-risk-item/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'previous_insurance':
      navigate(`/add-previous-insurance/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'claim':
      navigate(`/add-previous-claim/${currentQuoteId}?clientId=${clientIdValue}&editId=${productId}&returnTo=${encodeURIComponent(returnUrl)}`);
      break;
    case 'note':
      // Open the view modal instead of routing to the edit page
      setProductToView({ product, quote });
      setIsViewProductModalVisible(true);
      break;
    default:
      // Fallback to modal for unknown types
      setProductToView({ product, quote });
      setIsViewProductModalVisible(true);
  }
};

  // Function to extract products for master view (without setting loading states)
const extractProductsForMasterView = async (quoteData: QuoteDetailData): Promise<any[]> => {
  
  // Layer 1: Check if we already have products for this quote
  const existingProducts = allQuoteProducts[quoteData.id] || [];
  
  // ✅ NEW: Clear session storage guard for this quote to force fresh fetch
  const processingKey = `processing_quote_${quoteData.id}`;
  sessionStorage.removeItem(processingKey);
  
  console.log(`🔄 extractProductsForMasterView called for quote ${quoteData.id}, existing products: ${existingProducts.length}`);
  
  // ⭐⭐ CRITICAL CHANGE: Start with existing products
  const products: any[] = [...existingProducts];
  const token = localStorage.getItem('token');

  if (!token) {
    console.error('❌ No token found for product extraction');
    sessionStorage.removeItem(processingKey);
    return existingProducts; // Return existing products if no token
  }

  // Create a Set to track existing product keys to avoid duplicates
  const existingProductKeys = new Set<string>();
  existingProducts.forEach(product => {
    const key = `${product.type}-${product.id}`;
    existingProductKeys.add(key);
  });

  // Define all product endpoints to check
  const productEndpoints = [
    { 
      url: `${process.env.REACT_APP_API_URL}/api/properties?quote_id=${quoteData.id}`,
      type: 'property',
      dataPath: ''
    },
    { 
      url: `${process.env.REACT_APP_API_URL}/api/household-contents?quote_id=${quoteData.id}`,
      type: 'household',
      dataPath: 'data'
    },

    { 
      url: `${process.env.REACT_APP_API_URL}/api/vehicles?quote_id=${quoteData.id}`,
      type: 'vehicle', 
      dataPath: 'data'
    },

    { 
      url: `${process.env.REACT_APP_API_URL}/api/trailer-caravans?quote_id=${quoteData.id}`,
      type: 'trailer',
      dataPath: 'data'
    },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/value-added?quote_id=${quoteData.id}`,
    type: 'value_added',
    dataPath: 'data'
    },

    { 
      url: `${process.env.REACT_APP_API_URL}/api/risk-items?quote_id=${quoteData.id}`,
      type: 'risk_item',
      dataPath: 'data'
    },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/water-craft?quote_id=${quoteData.id}`,
    type: 'water_craft',
    dataPath: 'data'
    },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/previous-insurances?quote_id=${quoteData.id}`,
    type: 'previous_insurance',
    dataPath: 'data'
  },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/quotes/${quoteData.id}/documents`,
    type: 'file',
    dataPath: 'documents' // Changed from 'data' to 'documents'
    },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/clients-personal-claims?quote_id=${quoteData.id}`,
    type: 'claim',
    dataPath: 'data'
    },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/quotes/${quoteData.id}/documents`,
    type: 'document',
    dataPath: 'documents'
    },

    { 
    url: `${process.env.REACT_APP_API_URL}/api/notes?quote_id=${quoteData.id}`,
    type: 'note',
    dataPath: 'data'
    }

  ];

  let totalNewProducts = 0;

  // Fetch products from all endpoints
  for (const endpoint of productEndpoints) {
    
    try {
      console.log(`🔍 Fetching ${endpoint.type} for quote:`, quoteData.id);
      const response = await axios.get(endpoint.url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let productData = response.data;
      
      // Handle different response structures
      if (endpoint.type === 'document') {
      // Documents are in response.data.documents
      if (productData && productData.documents && Array.isArray(productData.documents)) {
        productData = productData.documents;
        console.log(`📦 ${endpoint.type} data extracted:`, productData);
      } else {
        console.log(`ℹ️ No documents found in response for ${endpoint.type}`);
        continue; // Skip if no documents
      }
    } else if (endpoint.type === 'file') {
      // Files from create quote page - use the same documents endpoint
      if (productData && productData.documents && Array.isArray(productData.documents)) {
        productData = productData.documents;
        console.log(`📦 ${endpoint.type} data extracted:`, productData.length, 'files');
      } else if (Array.isArray(productData)) {
        // ✅ Handle if backend returns a bare array
        console.log(`📦 ${endpoint.type} returned a bare array:`, productData.length, 'files');
      } else {
        console.log(`ℹ️ No files found in response for ${endpoint.type}`);
        continue;
      }
    } else if (endpoint.dataPath && endpoint.dataPath === 'data') {
      // ✅ Robust extraction: handle both { success, data: [...] } and bare arrays
      if (Array.isArray(productData)) {
        console.log(`📦 ${endpoint.type} bare array (${productData.length} items)`);
      } else if (productData && Array.isArray(productData.data)) {
        productData = productData.data;
        console.log(`📦 ${endpoint.type} extracted from .data (${productData.length} items)`);
      } else if (productData && productData.data !== undefined) {
        productData = productData.data;
        console.log(`📦 ${endpoint.type} extracted from .data:`, productData);
      } else {
        console.log(`ℹ️ ${endpoint.type} response had no .data property:`, productData);
        continue;
      }
    }
      
      if (productData && Array.isArray(productData)) {
        
        productData.forEach((item: any) => {
          if (item) {
            
            console.log(`🔍 ${endpoint.type} item data:`, item);

            let processedData = { ...item };

              if (endpoint.type === 'note') {
                processedData = {
                  id: item.id,
                  content: item.note_content || item.content,
                  createdAt: item.created_at || item.createdAt,
                  note_content: item.note_content || item.content,
                  created_at: item.created_at || item.createdAt
                };
              } else if (endpoint.type === 'file') {
                  processedData = {
                    id: item.id,
                    file_name: item.original_name || item.file_name || item.filename || item.fileName || 'Document',
                    description: item.description || '',
                    file_size: item.file_size || item.size || 0,
                    uploaded_at: item.created_at || item.uploaded_at || new Date().toISOString(),
                    document_type: item.document_type || item.mime_type || 'Document',
                    url: item.url || item.file_path
                  };
              } 
              else if (endpoint.type === 'property') {
  // Helper function to parse material arrays from various formats
  const parseMaterialsArray = (value: any): string[] => {
    if (!value) return [];
    
    // Helper to strip JSON artifacts
    const clean = (s: any) => String(s).replace(/^[{\["\s]+/, '').replace(/[}\]"\s]+$/, '').trim();

    if (Array.isArray(value)) {
       return value.map(clean).filter(Boolean);
    }
    
    if (typeof value === 'string') {
      if (value === '' || value === '[]') return [];
      
      // Check for JSON *first* before checking for commas
      if (value.startsWith('[') && value.endsWith(']')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) return parsed.map(clean).filter(Boolean);
        } catch(e) {}
      }
      
      // Postgres format
      if (value.startsWith('{') && value.endsWith('}')) {
        return value.slice(1, -1).split(',').map(clean).filter(Boolean);
      }
      
      // Comma separated
      if (value.includes(',')) {
        return value.split(',').map(clean).filter(Boolean);
      }
      
      return [clean(value)];
    }
    return [];
};
  
  processedData = {
    ...item,
    id: item.id,
    addressLine1: item.address_line_1,
    addressLine2: item.address_line_2,
    suburb: item.suburb,
    city: item.city,
    postalCode: item.postal_code,
    region: item.region,
    country: item.country,
    propertyType: item.property_type,
    residenceType: item.residence_type,
    buildingAge: item.building_age,
    situatedAt: item.situated_at,
    coverType: item.cover_type,
    insuredValue: item.insured_value,
    hasSwimmingPool: item.has_swimming_pool === true || item.has_swimming_pool === 1 || item.has_swimming_pool === 'true',
    hasMotorizedGate: item.has_motorized_gate === true || item.has_motorized_gate === 1 || item.has_motorized_gate === 'true',
    isUnoccupiedMoreThan60Days: item.is_unoccupied_more_than_60_days === true || item.is_unoccupied_more_than_60_days === 1 || item.is_unoccupied_more_than_60_days === 'true',
    isCommune: item.is_commune === true || item.is_commune === 1 || item.is_commune === 'true',
    underConstruction: item.under_construction === true || item.under_construction === 1 || item.under_construction === 'true',
    builtOnSteepSlope: item.built_on_steep_slope === true || item.built_on_steep_slope === 1 || item.built_on_steep_slope === 'true',
    hasExcessiveRetainingWalls: item.has_excessive_retaining_walls === true || item.has_excessive_retaining_walls === 1 || item.has_excessive_retaining_walls === 'true',
    nearWaterBody: item.near_water_body === true || item.near_water_body === 1 || item.near_water_body === 'true',
    hasRisingDamp: item.has_rising_damp === true || item.has_rising_damp === 1 || item.has_rising_damp === 'true',
    hasWallCracks: item.has_wall_cracks === true || item.has_wall_cracks === 1 || item.has_wall_cracks === 'true',
    hasSubsidenceHistory: item.has_subsidence_history === true || item.has_subsidence_history === 1 || item.has_subsidence_history === 'true',
    hasOutbuilding: item.has_outbuilding === true || item.has_outbuilding === 1 || item.has_outbuilding === 'true',
    hasPerimeterFence: item.has_perimeter_fence === true || item.has_perimeter_fence === 1 || item.has_perimeter_fence === 'true',
    hasElectricFence: item.has_electric_fence === true || item.has_electric_fence === 1 || item.has_electric_fence === 'true',
    hasThatchRoof: item.has_thatch_roof === true || item.has_thatch_roof === 1 || item.has_thatch_roof === 'true',
    thatchLessThan5m: item.thatch_less_than_5m === true || item.thatch_less_than_5m === 1 || item.thatch_less_than_5m === 'true',
    thatchSabsConductor: item.thatch_sabs_conductor === true || item.thatch_sabs_conductor === 1 || item.thatch_sabs_conductor === 'true',
    thatchTreated: item.thatch_treated === true || item.thatch_treated === 1 || item.thatch_treated === 'true',
    hasPowerSurgeCover: item.has_power_surge_cover === true || item.has_power_surge_cover === 1 || item.has_power_surge_cover === 'true',
    powerSurgeCoverValue: item.power_surge_cover_value || 0,
    hasAccidentalDamageCover: item.has_accidental_damage_cover === true || item.has_accidental_damage_cover === 1 || item.has_accidental_damage_cover === 'true',
    accidentalDamageCoverValue: item.accidental_damage_cover_value || 0,
    // ✅ CRITICAL: Parse material arrays using the improved parser
    wallMaterials: parseMaterialsArray(item.wall_materials),
    roofMaterials: parseMaterialsArray(item.roof_materials),
    floorMaterials: parseMaterialsArray(item.floor_materials),
    otherSurroundingAreas: parseMaterialsArray(item.other_surrounding_areas),
    premium: item.premium,
    excess: item.excess,
    noClaimBonus: item.no_claim_bonus,
  };
}
              else if (endpoint.type === 'household') {
                processedData = {
                  id: item.id,
                  yearsLivedOnProperty: item.years_lived_on_property,
                  burglarBarType: item.burglar_bar_type,
                  dayTimeOccupantType: item.day_time_occupant_type,
                  coverType: item.cover_type,
                  insuredValue: item.insured_value,
                  conductsBusiness: item.conducts_business || false,
                  hasSurveillanceCamera: item.has_surveillance_camera || false,
                  hasExternalSlidingDoor: item.has_external_sliding_door || false,
                  hasSecurityGate: item.has_security_gate || false,
                  inSecurityComplex: item.in_security_complex || false,
                  hasAlarmRecords: item.has_alarm_records || false,
                  hasNeighborhoodWatch: item.has_neighborhood_watch || false,
                  hasAccidentalDamageCover: item.has_accidental_damage_cover || false,
                  hasPowerSurgeCover: item.has_power_surge_cover || false,
                  alarmSystemType: item.alarm_system_type,
                  perimeterType: item.perimeter_type,
                  addressLine1: item.address_line_1,
                  addressLine2: item.address_line_2,
                  suburb: item.suburb,
                  cityTown: item.city_town,
                  postalCode: item.postal_code,
                  region: item.region,
                  country: item.country,
                  propertyType: item.property_type,
                  residenceType: item.residence_type,
                  buildingAge: item.building_age,
                  situatedType: item.situated_type,
                  hasSwimmingPool: item.has_swimming_pool || false,
                  hasMotorizedGate: item.has_motorized_gate || false,
                  isPropertyUnoccupied: item.is_property_unoccupied || false,
                  isCommune: item.is_commune || false,
                  underConstruction: item.under_construction || false,
                  builtOnSteepSlope: item.built_on_steep_slope || false,
                  hasRetainingWalls: item.has_retaining_walls || false,
                  nearWaterBody: item.near_water_body || false,
                  hasRisingDamp: item.has_rising_damp || false,
                  hasWallCracks: item.has_wall_cracks || false,
                  historyOfLandslip: item.history_of_landslip || false,
                  hasOutbuilding: item.has_outbuilding || false,
                  hasPerimeterFence: item.has_perimeter_fence || false,
                  hasElectricFence: item.has_electric_fence || false,
                  hasThatchRoof: item.has_thatch_roof || false,
                  wallMaterials: item.wall_materials,
                  roofMaterials: item.roof_materials,
                  floorMaterials: item.floor_materials,
                  otherSurroundingArea: item.other_surrounding_area,
                  premium: item.premium,
                  excess: item.excess,
                  noClaimBonus: item.no_claim_bonus,
                  // Keep original fields too for backward compatibility
                  ...item
                };

                              } else if (endpoint.type === 'trailer') {
                // ✅ Map snake_case from database to camelCase for EditableTrailerCaravanCard
                processedData = {
                  id: item.id,
                  vehicleType: item.vehicle_type || item.vehicleType || 'Trailer',
                  registrationNumber: item.registration_number || item.registrationNumber || '',
                  make: item.make || '',
                  model: item.model || '',
                  year: item.year || '',
                  coverType: item.cover_type || item.coverType || '',
                  insuredValue: item.insured_value || item.insuredValue || 0,
                  useRetailValue: item.use_retail_value || item.useRetailValue || false,
                  premium: item.premium || 0,
                  excess: item.excess || 0,
                  noClaimsBonus: item.no_claims_bonus || item.noClaimsBonus || 'None',
                  // Keep original fields for backward compatibility
                  ...item
                };
              } else if (endpoint.type === 'file') {

              } else if (endpoint.type === 'file') {
                // Mapping for uploaded files from create quote page
                processedData = {
                  id: item.id,
                  file_name: item.original_name,
                  fileName: item.original_name,
                  description: item.description,
                  file_size: item.file_size,
                  fileSize: item.file_size,
                  uploaded_at: item.created_at,
                  uploadedAt: item.created_at,
                  document_type: item.mime_type,
                  documentType: item.mime_type,
                  url: null,
                  file_path: item.file_path,
                  mime_type: item.mime_type,
                  mimetype: item.mime_type,
                  quote_id: item.quote_id
                };
              }

              
            
            // ⭐⭐ CRITICAL: Check if this product already exists
            const productKey = `${endpoint.type}-${item.id}`;
            if (existingProductKeys.has(productKey)) {
              console.log(`⏸️ Skipping duplicate product: ${productKey}`);
              return; // Skip this product, it already exists
            }

            // ✅ Skip adding signed documents as products
            if (endpoint.type === 'file') {
              const fileName = item.file_name || item.original_name || '';
              if (fileName.includes('signed_proposal') || fileName.includes('Signed_Proposal')) {
                console.log(`⏸️ Skipping signed document as product: ${fileName}`);
                return; // Skip this item
              }
            }

            if (endpoint.type === 'document') {
              if (item.document_type === 'signed_proposal' || item.documentType === 'signed_proposal') {
                console.log(`⏸️ Skipping signed document as product`);
                return; // Skip this item
              }
            }
            
            const product = {
              id: item.id || `${endpoint.type}-${Date.now()}-${Math.random()}`,
              type: endpoint.type,
              name: getProductName(item, endpoint.type),
              value: getProductValue(item, endpoint.type),
              description: getProductDescription(item, endpoint.type),
              data: processedData,
              source: 'api',
              quoteId: quoteData.id,
              quoteReference: quoteData.referenceNumber || quoteData.reference || `Quote-${quoteData.id}`,
              uniqueKey: `${endpoint.type}-${item.id || Date.now()}-${Math.random()}`
            };
            
            console.log(`📦 Created ${endpoint.type} product:`, product);
            products.push(product);
            existingProductKeys.add(productKey);
            totalNewProducts++;
          }
        });

      } else {
        console.log(`ℹ️ No ${endpoint.type} found or invalid format`);
      }
    } catch (error: any) {
      console.log(`ℹ️ ${endpoint.type} endpoint error:`, {
        status: error.response?.status,
        message: error.response?.data?.message,
        url: endpoint.url
      });
    }
  }

  console.log(`📊 FINAL: Total products for quote ${quoteData.id}:`, products.length);
  console.log(`📊 Breakdown: ${existingProducts.length} existing + ${totalNewProducts} new`);

  // Only update state if we found new products
  if (totalNewProducts > 0) {
    console.log(`🔄 Updating products state with ${totalNewProducts} new products`);
    setAllQuoteProducts(prev => ({
      ...prev,
      [quoteData.id]: products
    }));
  } else {
    console.log(`ℹ️ No new products found, keeping existing ${existingProducts.length} products`);
  }

  // ⭐⭐ NEW: Clean up session storage
  sessionStorage.removeItem(processingKey);
  console.log('✅ ====== END extractProductsForMasterView ======');

  return products;
};

// Helper function to get products for a specific quote
const getProductsForQuote = (quoteId: string): any[] => {
  const apiProducts = allQuoteProducts[quoteId] || [];
  const localProducts = allQuoteProducts[quoteId] || [];
  
  // Filter out removed products
  const filteredProducts = localProducts.filter(product => {
    const productKey = `${quoteId}-${product.id}-${product.type}`;
    const isRemoved = removedProducts.has(productKey);
    return !isRemoved;
  });
  
  // ✅ Filter out signed documents from products list
  const nonSignedProducts = filteredProducts.filter(product => {
    // Exclude signed documents (type 'file' with signed_proposal in name)
    if (product.type === 'file' && product.name?.includes('signed_proposal')) {
      return false;
    }
    // Exclude signed documents (type 'document' with signed_proposal documentType)
    if (product.type === 'document' && product.documentType === 'signed_proposal') {
      return false;
    }
    // Exclude signed documents (check data field)
    if (product.data?.document_type === 'signed_proposal') {
      return false;
    }
    if (product.data?.documentType === 'signed_proposal') {
      return false;
    }
    return true;
  });
  
  return nonSignedProducts;
};

// Helper function to get total products count
const getTotalProductsCount = (): number => {
  let total = 0;
  Object.keys(allQuoteProducts).forEach(quoteId => {
    const products = getProductsForQuote(quoteId);
    total += products.length;
  });
  return total;
};

// Replace the existing handleEmailQuoteWithMessage function with this updated version
const handleEmailQuoteWithMessage = async (
  withPDF: boolean = true, 
  customSubject?: string,
  customMessage?: string
) => {
  console.log('🚀 handleEmailQuoteWithMessage called with:', {
    withPDF,
    customSubject,
    customMessageLength: customMessage?.length
  });

  try {
    setSaveStatus('loading');
    setSaveMessage(withPDF ? 'Generating PDF and preparing email...' : 'Preparing email...');

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Get the current quote
    if (allQuotes.length === 0) {
      throw new Error('No quotes available');
    }

    const currentQuote = allQuotes[0];
    const quoteId = currentQuote.id;

    console.log('📧 Email button clicked for quote:', quoteId);

    // Get email recipients - FILTERED BY ACTIVE STATUS
    const emailRecipients: string[] = [];
    const adminRecipients: { email: string; name: string; id: string }[] = [];
    const underwriterRecipients: { email: string; name: string; id: string }[] = [];

    // Filter administrators with ACTIVE status
    const activeAdmins = administrators.filter(admin => {
      const isActive = admin.quotation_status === 'active' || admin.quotation_status === undefined;
      const hasQuotationsEmail = admin.email !== undefined && admin.email !== null && admin.email.trim() !== '';
      return isActive && hasQuotationsEmail;
    });

    activeAdmins.forEach(admin => {
      adminRecipients.push({ email: admin.email!.trim(), name: admin.name, id: admin.id });
      emailRecipients.push(admin.email!.trim());
    });

    // Filter underwriters with ACTIVE status
    const activeUnderwriters = underwriters.filter(underwriter => {
      const isActive = underwriter.quotation_status === 'active' || underwriter.quotation_status === undefined;
      const hasQuotationsEmail = underwriter.email !== undefined && underwriter.email !== null && underwriter.email.trim() !== '';
      return isActive && hasQuotationsEmail;
    });

    activeUnderwriters.forEach(uw => {
      underwriterRecipients.push({ email: uw.email!.trim(), name: uw.name, id: uw.id });
      emailRecipients.push(uw.email!.trim());
    });

    if (emailRecipients.length === 0) {
      setSaveStatus('error');
      setSaveMessage('No quotation email addresses found for ACTIVE administrators or underwriters. Please check insurer settings.');
      return;
    }

    // Prepare PDF data (same as before)
    const quoteSummary = generateDetailedProductsSummary();
    const pdfData = {
      quoteReference: currentQuote.referenceNumber || currentQuote.reference,
      client_name: clientFromHook ? `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim() : 'Client',
      client_number: clientFromHook?.client_number || '',
      id_passport_number: clientFromHook?.id_passport_number || '',
      client_address: extractClientAddress(),
      quote_date: formatDate(currentQuote.createdDate || currentQuote.created_at),
      total_products: getTotalProductsCount(),
      quote_summary: quoteSummary,
      products: getAllProductsForPDF(),
      products_details: generateProductsDetailsForPDF(),
      hide_total_insured_value: true,
      show_full_details: true,
      pdf_section_title: "Full Insured Item Details",
      administrators: administrators.map(admin => ({
        name: admin.name,
        quotation_status: admin.quotation_status || 'active',
        premium: admin.premium || '',
        email: admin.email || ''
      })),
      underwriters: underwriters.map(uw => ({
        name: uw.name,
        quotation_status: uw.quotation_status || 'active',
        premium: uw.premium || '',
        email: uw.email || ''
      }))
    };

    const emailData = {
      quote_id: quoteId,
      recipients: emailRecipients,
      subject: customSubject || `Quotation - ${currentQuote.referenceNumber || currentQuote.reference}`,
      client_name: pdfData.client_name,
      client_email: clientFromHook?.email || '',
      client_phone: clientFromHook?.mobile_number || '',
      quote_summary: customMessage || '',
      include_pdf: withPDF,
      pdf_data: withPDF ? pdfData : null,
      generate_full_details_pdf: true,
      recipient_info: {
        administrators: administrators.map(admin => ({
          name: admin.name,
          email: admin.email,
          status: admin.quotation_status,
          included: admin.quotation_status === 'active'
        })),
        underwriters: underwriters.map(uw => ({
          name: uw.name,
          email: uw.email,
          status: uw.quotation_status,
          included: uw.quotation_status === 'active'
        }))
      }
    };

    console.log('📤 Sending email with PDF:', withPDF);

    // LOG: Create a log entry for the email attempt (pending status)
    const mainLogId = await logEmailToDatabase({
      clientId: clientIdFromState!,
      recipient: emailRecipients.join(', '),
      recipientType: 'administrator',
      subject: emailData.subject,
      quoteId: quoteId,
      emailType: 'quotation',
      status: 'pending',
      metadata: {
        recipientCount: emailRecipients.length,
        includePdf: withPDF,
        hasClientCopy: !!clientFromHook?.email,
        administrators: activeAdmins.map(a => a.name),
        underwriters: activeUnderwriters.map(u => u.name)
      }
    });

    // Send the email with PDF data
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/send-email`,
      emailData,
      { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      }
    );

    console.log('📧 Email response:', response.data);

    // LOG: Update main log status based on response
    if (response.data.success) {
      if (mainLogId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-logs/${mainLogId}/status`,
          { status: 'sent' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } else {
      if (mainLogId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-logs/${mainLogId}/status`,
          { status: 'failed', errorMessage: response.data.message || 'Unknown error' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    }

    // LOG: Log client email separately if client email exists
    let clientLogId = null;
    if (clientFromHook?.email && clientFromHook.email.trim()) {
      clientLogId = await logEmailToDatabase({
        clientId: clientIdFromState!,
        recipient: clientFromHook.email,
        recipientType: 'client',
        subject: `Your Quotation: ${emailData.subject}`,
        quoteId: quoteId,
        emailType: 'quotation',
        status: response.data.clientEmailSent ? 'sent' : (response.data.clientEmailError ? 'failed' : 'pending'),
        errorMessage: response.data.clientEmailError,
        metadata: {
          parentLogId: mainLogId,
          isClientCopy: true
        }
      });

      // Update client log if needed
      if (clientLogId && response.data.clientEmailSent) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-logs/${clientLogId}/status`,
          { status: 'sent' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else if (clientLogId && response.data.clientEmailError) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/email-logs/${clientLogId}/status`,
          { status: 'failed', errorMessage: response.data.clientEmailError },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    }

    // LOG: Log individual emails for each administrator and underwriter
    for (const admin of activeAdmins) {
      await logEmailToDatabase({
        clientId: clientIdFromState!,
        recipient: admin.email!,
        recipientType: 'administrator',
        subject: emailData.subject,
        quoteId: quoteId,
        emailType: 'quotation',
        status: response.data.success ? 'sent' : 'failed',
        metadata: {
          parentLogId: mainLogId,
          recipientName: admin.name,
          recipientId: admin.id
        }
      });
    }

    for (const uw of activeUnderwriters) {
      await logEmailToDatabase({
        clientId: clientIdFromState!,
        recipient: uw.email!,
        recipientType: 'underwriter',
        subject: emailData.subject,
        quoteId: quoteId,
        emailType: 'quotation',
        status: response.data.success ? 'sent' : 'failed',
        metadata: {
          parentLogId: mainLogId,
          recipientName: uw.name,
          recipientId: uw.id
        }
      });
    }

    // Update UI with success message
    if (response.data.success) {
      setSaveStatus('success');
      let successMessage = 'Email ';
      
      if (withPDF && response.data.withPdf !== false) {
        successMessage += 'with PDF ';
      }
      
      successMessage += 'sent successfully to ';
      
      const recipientsList = [
        ...activeAdmins.map(a => a.name),
        ...activeUnderwriters.map(u => u.name)
      ].join(', ');
      
      successMessage += recipientsList || 'recipients';
      
      // Add client email status
      if (clientFromHook?.email) {
        if (response.data.clientEmailSent) {
          successMessage += `\n✓ Client email sent to: ${clientFromHook.email}`;
        } else if (response.data.clientEmailError) {
          successMessage += `\n⚠️ Client email failed: ${response.data.clientEmailError}`;
        }
      }
      
      setSaveMessage(successMessage);
      
    } else if (response.data.needsConfiguration) {
      setSaveStatus('warning');
      setSaveMessage('⚠️ Email configuration required. Please set up SMTP settings in Email Configuration.');
      
      setTimeout(() => {
        if (window.confirm('Would you like to configure email settings now?')) {
          navigate('/email-settings');
        }
      }, 2000);
    } else {
      throw new Error(response.data.message || 'Failed to send email');
    }

  } catch (error: any) {
    console.error('❌ Error sending email:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
    let errorMessage = error.response?.data?.message || error.message || 'Failed to send email';
    
    setSaveStatus('error');
    setSaveMessage(errorMessage);
    
    setTimeout(() => {
      setSaveStatus('idle');
      setSaveMessage('');
    }, 5000);
  }
};

const extractClientAddress = (): string => {
  if (!clientFromHook?.addresses || !Array.isArray(clientFromHook.addresses)) return '';
  
  const primaryAddress = clientFromHook.addresses.find(addr => addr.is_primary) || clientFromHook.addresses[0];
  if (!primaryAddress) return '';
  
  // Use type assertion to access properties safely
  const addressObj = primaryAddress as any;
  
  // Check for both snake_case and camelCase properties
  const streetAddress = addressObj.address_line_1 || addressObj.addressLine1 || '';
  const city = addressObj.city || addressObj.city_town || addressObj.cityTown || '';
  const region = addressObj.region || addressObj.state || addressObj.province || '';
  const postalCode = addressObj.postal_code || addressObj.postalCode || addressObj.zip_code || addressObj.zipCode || '';
  const country = addressObj.country || addressObj.country_name || '';
  
  const addressParts = [];
  if (streetAddress && streetAddress.trim()) addressParts.push(cleanString(streetAddress.trim()));
  if (city && city.trim()) addressParts.push(cleanString(city.trim()));
  if (region && region.trim()) addressParts.push(cleanString(region.trim()));
  if (postalCode && postalCode.trim()) addressParts.push(cleanString(postalCode.trim()));
  if (country && country.trim()) addressParts.push(cleanString(country.trim()));
  
  return addressParts.join(', ');
};

// Add this helper function to clean the string
const cleanString = (str: string): string => {
  if (!str) return '';
  
  // Remove UTF-8 BOM and other special characters
  // Common BOM sequences: EF BB BF (UTF-8), FE FF (UTF-16 BE), FF FE (UTF-16 LE)
  
  // Method 1: Remove any non-printable characters (ASCII 0-31 except 9,10,13)
  let cleaned = str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Method 2: Remove specific BOM characters
  cleaned = cleaned.replace(/^\uFEFF/, ''); // UTF-16 BE BOM
  cleaned = cleaned.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
  
  // Method 3: Remove UTF-8 BOM (EF BB BF in hex)
  if (cleaned.charCodeAt(0) === 0xFEFF || cleaned.charCodeAt(0) === 0xFFFE) {
    cleaned = cleaned.substring(1);
  }
  
  // Method 4: More aggressive cleaning - remove any non-ASCII characters at the start
  cleaned = cleaned.replace(/^[^\x20-\x7E]+/, '');
  
  // Method 5: Trim and ensure proper spacing
  cleaned = cleaned.trim();
  
  return cleaned;
};

const getAllProductsForPDF = (): any[] => {
  const allProducts: any[] = [];
  
  Object.values(allQuoteProducts).forEach(products => {
    products.forEach(product => {
      const productKey = `${product.quoteId}-${product.id}-${product.type}`;
      if (!removedProducts.has(productKey)) {
        allProducts.push({
          id: product.id,
          type: product.type,
          name: product.name,
          value: product.value || 0,
          description: product.description || '',
          productType: getProductTypeDisplay(product.type)
        });
      }
    });
  });
  
  return allProducts;
};

const generateProductsDetailsForPDF = (): string => {
  const allProducts = getAllProductsForPDF();
  
  if (allProducts.length === 0) {
    return 'No products available';
  }
  
  let details = '';
  
  allProducts.forEach((product, index) => {
    details += `${index + 1}. ${product.name}\n`;
    details += `   Type: ${product.productType}\n`;
    details += `   Value: ${formatCurrency(product.value)}\n`;
    if (product.description && product.description !== 'N/A') {
      details += `   Description: ${product.description}\n`;
    }
    details += '\n';
  });
  
  return details;
};

// Function to handle product removal
const handleRemoveProduct = (quoteId: string, productId: string, productType: string, productName: string) => {
  console.log(`🗑️ Requesting removal for product:`, { quoteId, productId, productType, productName });
  
  // Store the product info and open the removal modal
  setProductToRemove({ quoteId, productId, productType, productName });
  setRemovalReason('');
  setIsRemoveModalVisible(true);
};

const handleAdminQuotationStatusChange = (adminId: string, status: string) => {
  setAdministrators(prev => 
    prev.map(admin => 
      admin.id === adminId 
        ? { ...admin, quotation_status: status }
        : admin
    )
  );
};

const handleAdminPremiumChange = (adminId: string, premium: string) => {
  setAdministrators(prev => 
    prev.map(admin => 
      admin.id === adminId 
        ? { ...admin, premium }
        : admin
    )
  );
};

const handleUnderwriterQuotationStatusChange = (underwriterId: string, status: string) => {
  setUnderwriters(prev => 
    prev.map(uw => 
      uw.id === underwriterId 
        ? { ...uw, quotation_status: status }
        : uw
    )
  );
};

const handleUnderwriterPremiumChange = (underwriterId: string, premium: string) => {
  setUnderwriters(prev => 
    prev.map(uw => 
      uw.id === underwriterId 
        ? { ...uw, premium }
        : uw
    )
  );
};

// ✅ NEW: Handler to save the assigned consultant
const handleAssignConsultant = async () => {
  const currentQuoteId = allQuotes[0]?.id;
  if (!currentQuoteId) return;
  
  setIsSavingConsultant(true);
  try {
    const token = localStorage.getItem('token');
    
    // Send the partial update to the backend
    const payload = { 
      consultant_id: selectedConsultantId ? parseInt(selectedConsultantId) : null 
    };
    
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/quotes/${currentQuoteId}`,
      payload,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );

    if (response.data.success) {
      // Update local state to reflect the change immediately
      setAllQuotes(prev => prev.map(q => 
        q.id === currentQuoteId 
          ? { ...q, consultant_id: selectedConsultantId || undefined } 
          : q
      ));

      setSaveStatus('success');
      setSaveMessage('Consultant assigned successfully!');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  } catch (error: any) {
    console.error('Failed to assign consultant:', error);
    setSaveStatus('error');
    setSaveMessage(error.response?.data?.message || 'Failed to assign consultant.');
    setTimeout(() => setSaveStatus('idle'), 4000);
  } finally {
    setIsSavingConsultant(false);
  }
};

const saveInsurersData = async () => {
  try {
    isJustSavedRef.current = true;
    setSaveStatus('loading');
    setSaveMessage('Saving insurers data...');
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('💾 Starting to save insurers data...');
    
    // Get current quote ID
    const currentQuoteId = allQuotes[0]?.id;
    if (!currentQuoteId) {
      throw new Error('No quote found to save insurers data');
    }

    // ✅ CRITICAL FIX: Create a local copy of the CURRENT state
    // This prevents the "second click" issue by ensuring we save exactly what's on screen.
    const adminsToSave = [...administrators];
    const underwritersToSave = [...underwriters];
    
    // Save administrators
    const adminPromises = adminsToSave.map(async (admin) => {
      try {
        // ✅ FIX: Send NULL instead of 0 when empty, so the field stays truly blank
        const premiumValue = admin.premium && admin.premium.trim() !== '' 
          ? parseFloat(admin.premium) 
          : null;
        
        const adminData = {
          quotation_status: admin.quotation_status || 'active',
          premium: premiumValue,
          quoteId: currentQuoteId
        };
        
        console.log(`💾 Saving administrator ${admin.id} (${admin.name}):`, adminData);
        
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/administrators/${admin.id}`,
          adminData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          }
        );

        console.log(`✅ Administrator ${admin.id} saved successfully:`, response.data);
        return response.data;
      } catch (error: any) {
        console.error(`❌ Error saving administrator ${admin.id}:`, {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        throw error;
      }
    });

    // Save underwriters
    const underwriterPromises = underwriters.map(async (uw) => {
      try {
        // ✅ FIX: Send NULL instead of 0 when empty
        const premiumValue = uw.premium && uw.premium.trim() !== '' 
          ? parseFloat(uw.premium) 
          : null;
        
        const uwData = {
          quotation_status: uw.quotation_status || 'active',
          premium: premiumValue,
          quoteId: currentQuoteId
        };
        
        console.log(`💾 Saving underwriter ${uw.id} (${uw.name}):`, uwData);
        
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/underwriters/${uw.id}`,
          uwData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 10000
          }
        );

        console.log(`✅ Underwriter ${uw.id} saved successfully:`, response.data);
        return response.data;
      } catch (error: any) {
        console.error(`❌ Error saving underwriter ${uw.id}:`, {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        
        if (error.response?.data) {
          console.error(`🔍 Underwriter API error details:`, error.response.data);
          
          if (error.response.data.error?.includes('No fields to update') || 
              error.response.data.message?.includes('No fields to update')) {
            console.error(`⚠️ Field name mismatch for underwriter API.`);
          }
        }
        
        throw error;
      }
    });

    // Wait for all saves to complete
    const results = await Promise.allSettled([...adminPromises, ...underwriterPromises]);
    
    // Check results
    const successes = results.filter(r => r.status === 'fulfilled');
    const failures = results.filter(r => r.status === 'rejected');
    
    console.log(`📊 Save results: ${successes.length} succeeded, ${failures.length} failed`);
    
    if (failures.length > 0) {
      failures.forEach((failure: any, index) => {
        console.error(`Failure ${index + 1}:`, failure.reason?.response?.data || failure.reason?.message);
      });
      
      if (successes.length === 0) {
        throw new Error('All saves failed');
      }
    }
    
    // ✅ UPDATED SUCCESS CALLBACK
    if (failures.length > 0) {
      setSaveStatus('warning');
      setSaveMessage(`Partially saved: ${successes.length} succeeded, ${failures.length} failed`);
      setHasSavedInsurers(false);
    } else {
      console.log('✅ All insurers saved successfully');
      setSaveStatus('success');
      setSaveMessage('Insurers data saved successfully!');
      
      // ✅ CRITICAL FIX: Set isNewQuote to false after successful save
      setIsNewQuote(false);
      console.log('🆕 isNewQuote set to false after successful save');
      
          // ✅ Get the active insurer's premium after successful save using our LOCAL copies
    const activeAdmins = adminsToSave.filter(a => a.quotation_status === 'active' || a.quotation_status === undefined);
    const activeUnderwriters = underwritersToSave.filter(u => u.quotation_status === 'active' || u.quotation_status === undefined);
    const activeInsurer = [...activeAdmins, ...activeUnderwriters][0];
      
       if (activeInsurer?.premium) {
        // ✅ Format the premium to 2 decimal places to match what the DB will return
        const premiumValue = parseFloat(activeInsurer.premium).toFixed(2);
        
        // ✅ Update local state with formatted string immediately so it doesn't mismatch
        setAdministrators(prev => prev.map(a => 
          a.id === activeInsurer.id ? { ...a, premium: premiumValue } : a
        ));
        setUnderwriters(prev => prev.map(u => 
          u.id === activeInsurer.id ? { ...u, premium: premiumValue } : u
        ));
        setSavedPremiumValue(premiumValue);
        setHasSavedInsurers(true);
        
        // ✅ CRITICAL FIX: Save the premium to the quote
        if (currentQuoteId) {
          try {
            // Update the quote with the premium
            await axios.put(
              `${process.env.REACT_APP_API_URL}/api/quotes/${currentQuoteId}`,
              { premium: premiumValue },
              { 
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            console.log(`✅ Quote ${currentQuoteId} premium updated to: ${premiumValue}`);
          } catch (quoteError: any) {
            console.error('❌ Error updating quote premium:', quoteError);
            // Don't fail the whole operation, just log it
          }
        }
        
        setSavedPremiumValue(premiumValue);
        setHasSavedInsurers(true);
        console.log('✅ Accept Quotation button should now be enabled');
        
        // ✅ Also update the quote in local state
        if (currentQuoteId) {
          setAllQuotes(prev => prev.map(q => 
            q.id === currentQuoteId 
              ? { ...q, premium: premiumValue }
              : q
          ));
        }
        
        // Force a re-render to ensure button updates
        setTimeout(() => {
          console.log('🔄 Forcing re-render check for Accept button, hasSavedInsurers =', true);
        }, 100);
      } else {
        // No active insurer found with premium
        console.log('⚠️ No active insurer with premium found after save');
        setHasSavedInsurers(false);
      }
    }
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setSaveStatus('idle');
      setSaveMessage('');
      isJustSavedRef.current = false;
    }, 3000);
    
  } catch (error: any) {
    isJustSavedRef.current = false;
    console.error('❌ Error in saveInsurersData:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
    let errorMessage = 'Failed to save insurers data';
    
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    setSaveStatus('error');
    setSaveMessage(errorMessage);
    
    setTimeout(() => {
      setSaveStatus('idle');
      setSaveMessage('');
    }, 5000);
    
    throw error;
  }
};

// ✅ HELPER FUNCTION TO UPDATE QUOTE PREMIUM
const updateQuotePremium = async (premium: string, token: string) => {
  try {
    const quoteId = allQuotes[0]?.id;
    if (!quoteId) return;
    
    console.log(`💾 Updating quote ${quoteId} with premium: ${premium}`);
    
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
      { premium: premium },
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`✅ Quote premium updated:`, response.data);
    
    // Update local state
    setAllQuotes(prev => prev.map(q => 
      q.id === quoteId 
        ? { ...q, premium: premium }
        : q
    ));
    
    setSavedPremiumValue(premium);
    setHasSavedInsurers(true);
    
  } catch (error: any) {
    console.error(`❌ Error updating quote premium:`, error);
    throw error;
  }
};

// Add this helper function to fetch and refresh insurers data
const fetchInsurersForQuote = async () => {
  // ✅ Skip if we just saved
    if (isJustSavedRef.current) {
      console.log('⏸️ Skipping auto-refresh - just saved insurers');
      return;
    }

  if (viewMode === 'master' && allQuotes.length > 0) {
    const currentQuote = allQuotes[0];
    const token = localStorage.getItem("token");
    
    if (!token) {
      console.error('❌ No token found for fetching insurers');
      return;
    }

    console.log('🔍 DEBUG - Quote insurers data:', {
      quoteId: currentQuote.id,
      administrator_id: currentQuote.administrator_id,
      underwriter_id: currentQuote.underwriter_id,
    });

    // ✅ Use single ID fields (not arrays)
    let adminIds: string[] = [];
    let uwIds: string[] = [];

    // Check for single administrator_id
    if (currentQuote.administrator_id) {
      adminIds = [String(currentQuote.administrator_id)];
      console.log('✅ Using administrator_id:', adminIds);
    }

    // Check for single underwriter_id
    if (currentQuote.underwriter_id) {
      uwIds = [String(currentQuote.underwriter_id)];
      console.log('✅ Using underwriter_id:', uwIds);
    }

    // Fetch fresh data from backend
    if (adminIds.length > 0) {
      await fetchMultipleAdministrators(adminIds, token);
    } else {
      setAdministrators([]);
    }

    if (uwIds.length > 0) {
      await fetchMultipleUnderwriters(uwIds, token);
    } else {
      setUnderwriters([]);
    }
    
    console.log('✅ Insurers data refreshed:', {
      administrators: administrators,
      underwriters: underwriters
    });
  }
};

// Function to confirm removal with reason
const confirmRemoveProduct = async () => {
  if (!productToRemove || !removalReason.trim()) {
    setSaveStatus('error');
    setSaveMessage('Please provide a reason for removal');
    return;
  }

  const { quoteId, productId, productType, productName } = productToRemove;
  
  console.log(`🗑️ Confirming removal with reason:`, { quoteId, productId, productType, productName, reason: removalReason });

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Map product type to table name for the API
    const tableMap: { [key: string]: string } = {
      'property': 'quote_property',
      'vehicle': 'quote_vehicles',
      'household': 'quote_household_contents',
      'risk_item': 'quote_risk_items',
      'water_craft': 'quote_water_craft',
      'trailer': 'quote_trailer_caravan',
      'value_added': 'quote_value_added',
      'note': 'quote_notes',
      'file': 'quote_documents',
      'document': 'quote_documents',
      'claim': 'clients_personal_claims',
      'previous_insurance': 'previous_insurances'
    };

    const tableName = tableMap[productType];
    
    // If it's a product type that has a status column in the database, update it
    if (tableName && ['quote_property', 'quote_vehicles', 'quote_household_contents', 'quote_risk_items', 'quote_water_craft', 'quote_trailer_caravan', 'quote_value_added'].includes(tableName)) {
      try {
        // Call backend to update status to 'removed'
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}/products/${productType}/${productId}/remove`,
          { reason: removalReason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log(`✅ Product ${productId} marked as removed in database`);
      } catch (apiError: any) {
        console.error('❌ Failed to update product status in database:', apiError);
        // Continue with local removal even if API fails (optimistic update)
        // But show a warning
        setSaveStatus('warning');
        setSaveMessage('Product removed locally but database update failed. Please try again later.');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }

         // ✅ LOG ACTIVITY: Product Removed
    try {
      const userData = localStorage.getItem('user');
let performedBy = 'System';

if (userData) {
  try {
    const user = JSON.parse(userData);
    // Try different field combinations
    const firstName = user.first_name || user.name || '';
    const lastName = user.last_name || user.surname || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    if (fullName) {
      performedBy = fullName;
    } else if (user.username) {
      performedBy = user.username;
    } else if (user.email) {
      performedBy = user.email;
    }
    console.log('👤 User for activity log:', { firstName, lastName, fullName, performedBy });
  } catch (parseError) {
    console.error('Error parsing user data:', parseError);
  }
}
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quote-activity-logs`,
        {
          quote_id: parseInt(quoteId),
          action_type: 'product_removed',
          description: `Product removed: ${productName} (${getProductTypeDisplay(productType)})\nReason: ${removalReason}`,
          metadata: {
            productId,
            productType,
            productName,
            reason: removalReason,
            removedAt: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Activity logged: product_removed');
    } catch (logError) {
      console.error('❌ Failed to log activity:', logError);
      // Don't fail the removal if logging fails
    }

    // Create a unique identifier for this product
    const productKey = `${quoteId}-${productId}-${productType}`;
    
    // Add to removed products set
    setRemovedProducts(prev => {
      const newSet = new Set(prev);
      newSet.add(productKey);
      
      // Save to localStorage
      const removedArray = Array.from(newSet);
      localStorage.setItem(`removedProducts_${clientIdFromState}`, JSON.stringify(removedArray));
      console.log('💾 Saved removed products:', removedArray);
      
      return newSet;
    });

    // Add removal reason to state
    const removalRecord: RemovalReason = {
      ...productToRemove,
      reason: removalReason,
      removedAt: new Date().toISOString()
    };
    
    setRemovalReasons(prev => {
      // Remove existing reason if it exists
      const filteredReasons = prev.filter(reason => 
        !(reason.quoteId === quoteId && reason.productId === productId && reason.productType === productType)
      );
      
      // Add new reason
      const newReasons = [...filteredReasons, removalRecord];
      console.log('💾 Setting removal reasons:', newReasons);
      
      // Save to localStorage
      localStorage.setItem(`removalReasons_${clientIdFromState}`, JSON.stringify(newReasons));
      console.log('💾 Saved removal reasons to localStorage:', newReasons);
      
      return newReasons;
    });

    // Remove the product from allQuoteProducts state
    setAllQuoteProducts(prev => {
      const currentProducts = prev[quoteId] || [];
      const updatedProducts = currentProducts.filter(product => 
        !(product.id === productId && product.type === productType)
      );
      console.log(`🗑️ Removing product from state. Before: ${currentProducts.length}, After: ${updatedProducts.length}`);
      return {
        ...prev,
        [quoteId]: updatedProducts
      };
    });

    // Close modal and reset
    setIsRemoveModalVisible(false);
    setProductToRemove(null);
    setRemovalReason('');
    setSaveStatus('success');
    setSaveMessage('Product removed successfully!');
    setTimeout(() => setSaveStatus('idle'), 3000);

  } catch (error: any) {
    console.error('❌ Error removing product:', error);
    setSaveStatus('error');
    setSaveMessage(error.response?.data?.message || 'Failed to remove product');
    setTimeout(() => setSaveStatus('idle'), 4000);
  }
};

// Function to recover missing removal reasons from removed products
const recoverMissingRemovalReasons = () => {
  if (!clientIdFromState) return;

  console.log('🔄 Attempting to recover missing removal reasons...');
  
  const savedRemovedProducts = localStorage.getItem(`removedProducts_${clientIdFromState}`);
  const savedRemovalReasons = localStorage.getItem(`removalReasons_${clientIdFromState}`);
  
  if (savedRemovedProducts && (!savedRemovalReasons || savedRemovalReasons === '[]')) {
    try {
      const removedProductsArray = JSON.parse(savedRemovedProducts);
      console.log('📋 Found removed products for recovery:', removedProductsArray);
      
      if (removedProductsArray.length > 0) {
        // Create removal reasons from removed products
        const recoveredReasons: RemovalReason[] = removedProductsArray.map((productKey: string) => {
          const [quoteId, productId, productType] = productKey.split('-');
          return {
            quoteId,
            productId,
            productType,
            productName: 'Product (Recovered)',
            reason: 'Removal reason not recorded',
            removedAt: new Date().toISOString()
          };
        });
        
        console.log('🔄 Recovered removal reasons:', recoveredReasons);
        setRemovalReasons(recoveredReasons);
        localStorage.setItem(`removalReasons_${clientIdFromState}`, JSON.stringify(recoveredReasons));
        console.log('💾 Saved recovered removal reasons to localStorage');
      }
    } catch (error) {
      console.error('❌ Error recovering removal reasons:', error);
    }
  }
};

// Function to clear removal reasons for specific quote
const clearRemovalReasonsForQuote = (quoteId: string) => {
  setRemovalReasons(prev => {
    const filteredReasons = prev.filter(reason => reason.quoteId !== quoteId);
    console.log(`🗑️ Cleared removal reasons for quote ${quoteId}`, filteredReasons);
    return filteredReasons;
  });
  
  // Also remove from removed products set
  setRemovedProducts(prev => {
    const newSet = new Set(prev);
    const productsToRemove = Array.from(newSet).filter(productKey => 
      productKey.startsWith(`${quoteId}-`)
    );
    
    productsToRemove.forEach(productKey => {
      newSet.delete(productKey);
      console.log(`🗑️ Removed product key: ${productKey}`);
    });
    
    // Update localStorage
    const removedArray = Array.from(newSet);
    localStorage.setItem(`removedProducts_${clientIdFromState}`, JSON.stringify(removedArray));
    console.log('💾 Updated removed products in localStorage:', removedArray);
    
    return newSet;
  });
  
  setSaveStatus('success');
  setSaveMessage(`Removal history cleared for quote ${quoteId}`);
};

// Clear sessionStorage guards when component mounts to ensure fresh data
useEffect(() => {
  // Clear all processing guards for quotes
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('processing_quote_')) {
      sessionStorage.removeItem(key);
    }
  });
}, []);

// ✅ List of all product types that use dedicated pages
const validProductTypes = ['property', 'household', 'vehicle', 'trailer', 'value_added', 'water_craft', 'risk_item'];

// ✅ NEW: Listen for navigation returns from AddPropertyPage
useEffect(() => {
  // Check if we're returning from property addition
  const urlParams = new URLSearchParams(window.location.search);
  const refreshFlag = urlParams.get('refreshProducts');
  const productType = urlParams.get('productAdded');
  
  if (refreshFlag === 'true' && validProductTypes.includes(productType || '') && allQuotes.length > 0) {
    console.log(`🔄 Detected return from ${productType} addition, forcing product refresh`);
    
    // Clear the URL parameters
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    
    // Force refresh of products for the current quote
    const currentQuoteId = allQuotes[0].id;
    const token = localStorage.getItem('token');
    
    const refreshProducts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/quotes/${currentQuoteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data?.data) {
          console.log('🔄 Refreshing products after property addition...');
          await extractProductsForMasterView(response.data.data);
          
          // Show success message
          setSaveStatus('success');
          setSaveMessage('Property added successfully!');
          setTimeout(() => setSaveStatus('idle'), 3000);
        }
      } catch (error) {
        console.error('❌ Failed to refresh products:', error);
      }
    };
    
    refreshProducts();
  }
}, [allQuotes, location.search]);

// Save removal reasons to localStorage whenever they change
useEffect(() => {
  if (clientIdFromState) {
    localStorage.setItem(`removalReasons_${clientIdFromState}`, JSON.stringify(removalReasons));
    console.log('💾 Saved removal reasons to localStorage:', removalReasons);
  }
}, [removalReasons, clientIdFromState]);

  // MAIN DATA FETCHING - Determine view mode and fetch data
  useEffect(() => {
    // ✅ NEW: Check for refreshProducts flag in URL
const urlParams = new URLSearchParams(window.location.search);
const shouldRefresh = urlParams.get('refreshProducts') === 'true';
const productAdded = urlParams.get('productAdded');
const urlClientId = urlParams.get('clientId');

console.log('🔍 URL Refresh Check:', { shouldRefresh, productAdded, fullUrl: window.location.href });

if (shouldRefresh && validProductTypes.includes(productAdded || '')) {
  console.log(`🔄 ${productAdded} added detected, refreshing product list...`);
  
  // If we have clientId in URL but not in state, update it
  if (urlClientId && !clientIdFromState) {
    console.log('📍 Updating clientId from URL parameter:', urlClientId);
    // Force a re-render by setting a local variable
    window.localStorage.setItem('temp_client_id', urlClientId);
  }

  // ✅ Specific log for value_added
  if (productAdded === 'value_added') {
    console.log('🎯 VALUE_ADDED update detected - forcing product refresh');
  }
  
  // Clear the URL parameter without reloading the page
  const newUrl = window.location.pathname;
  window.history.replaceState({}, '', newUrl);
    
    // Force a refresh of the current quote's products
    if (allQuotes.length > 0) {
      const currentQuoteId = allQuotes[0].id;
      const token = localStorage.getItem('token');
      
      // Immediately fetch fresh quote data
      axios.get(`${process.env.REACT_APP_API_URL}/api/quotes/${currentQuoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(async (response) => {
        if (response.data?.data) {
          console.log('🔄 Refreshing products after property addition...');
          await extractProductsForMasterView(response.data.data);
          setSaveStatus('success');
          setSaveMessage('Property added successfully! Refreshing products...');
          setTimeout(() => setSaveStatus('idle'), 3000);
        }
      }).catch(error => {
        console.error('❌ Failed to refresh after property addition:', error);
      });
    }
  }
  console.log('🔍 MAIN useEffect running - ID:', id, 'Client ID:', clientIdFromState, 'Location State:', location.state);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ No token found');
        setLoading(false);
        return;
      }

      // Get the specific quote ID from params OR location state
      const specificQuoteId = id || paramQuoteId || (location.state as any)?.selectedQuoteId;
      console.log('🔍 Specific quote ID to fetch:', specificQuoteId);

      if (clientIdFromState) {
        console.log('✅ Client ID found in state:', clientIdFromState);
        
        try {
          const quotesResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/quotes/client/${clientIdFromState}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          console.log('✅ All quotes response:', quotesResponse.data);
          
          if (quotesResponse.data.data && Array.isArray(quotesResponse.data.data)) {
            const allQuotesData = quotesResponse.data.data.map((quote: any) => ({
  id: quote.id,
  referenceNumber: quote.referenceNumber || quote.reference || `Quote-${quote.id}`,
  reference: quote.reference || quote.referenceNumber || `Quote-${quote.id}`,
  createdDate: quote.createdDate || quote.created_at,
  created_at: quote.created_at,
  status: quote.status || 'draft',
  client_id: quote.client_id,
  consultant_id: quote.consultant_id,
  // ✅ CRITICAL FIX: Ensure administrator_ids is always an array
  administrator_ids: (() => {
    if (quote.administrator_ids && Array.isArray(quote.administrator_ids) && quote.administrator_ids.length > 0) {
      return quote.administrator_ids.map((id: any) => id.toString());
    }
    if (quote.administrator_id) {
      return [quote.administrator_id.toString()];
    }
    return [];
  })(),
  // ✅ CRITICAL FIX: Ensure underwriter_ids is always an array
  underwriter_ids: (() => {
    if (quote.underwriter_ids && Array.isArray(quote.underwriter_ids) && quote.underwriter_ids.length > 0) {
      return quote.underwriter_ids.map((id: any) => id.toString());
    }
    if (quote.underwriter_id) {
      return [quote.underwriter_id.toString()];
    }
    return [];
  })(),
  consultant: quote.consultant,
  total_value: quote.total_value || 0,
  product_count: quote.product_count || 0,
  premium: quote.premium || ''
}));
            
            // If we have a specific quote ID from navigation, only show that one
            let quotesToShow = allQuotesData;
            if (specificQuoteId) {
              quotesToShow = allQuotesData.filter((quote: any) => 
                quote.id === specificQuoteId || 
                quote.id.toString() === specificQuoteId.toString() ||
                (typeof quote.id === 'number' && quote.id === parseInt(specificQuoteId)) ||
                (typeof quote.id === 'string' && quote.id === specificQuoteId.toString())
              );
              console.log(`📋 Filtered to show only quote ${specificQuoteId}:`, quotesToShow);
            }
            
            setAllQuotes(quotesToShow);
// Clear processing guard for this specific quote
if (specificQuoteId) {
  sessionStorage.removeItem(`processing_quote_${specificQuoteId}`);
}
console.log(`📦 Set ${quotesToShow.length} quotes to state`);

// ✅ DETECT IF THIS IS A NEW QUOTE
if (quotesToShow.length === 1) {
  const quote = quotesToShow[0];
  const hasNoProducts = (quote.product_count === 0 || quote.product_count === undefined);
  const isDraft = quote.status === 'draft' || quote.status === 'pending';
  const createdDate = new Date(quote.createdDate || quote.created_at);
  const now = new Date();
  const diffMinutes = (now.getTime() - createdDate.getTime()) / 60000;
  const isRecentlyCreated = diffMinutes < 5;
  
  // ✅ CRITICAL FIX: Check if quote has a premium already
  const hasPremium = quote.premium && quote.premium !== '' && parseFloat(quote.premium) > 0;
  
  // A quote is NEW only if:
  // 1. No products AND
  // 2. Draft status AND
  // 3. Recently created AND
  // 4. No premium saved yet
  const isNew = hasNoProducts && isDraft && isRecentlyCreated && !hasPremium;
  
  setIsNewQuote(isNew);
  console.log(`🆕 Is this a new quote? ${isNew} (products: ${quote.product_count}, status: ${quote.status}, hasPremium: ${hasPremium})`);
} else {
  setIsNewQuote(false);
}
            
            // Load removal reasons AFTER quotes are set
            // This ensures we have the current quotes before filtering removal reasons
            setTimeout(() => {
              console.log('🔄 Loading removal reasons after quotes are set');
              const savedRemovalReasons = localStorage.getItem(`removalReasons_${clientIdFromState}`);
              if (savedRemovalReasons) {
                try {
                  const removalReasonsData = JSON.parse(savedRemovalReasons);
                  console.log('💾 Loaded removal reasons for filtering:', removalReasonsData);
                  setRemovalReasons(removalReasonsData);
                } catch (error) {
                  console.error('❌ Error loading removal reasons:', error);
                }
              }
            }, 100);
            
            // If we have a specific quote, automatically fetch its products for master view
            if (specificQuoteId && quotesToShow.length > 0) {
  const specificQuote = quotesToShow[0];
  console.log('🔍 Fetching products for specific quote:', specificQuoteId);
  
  // ⭐⭐ NEW: Check if already extracting products for this quote
  if (isExtractingProductsRef.current[specificQuoteId]) {
    console.log('⏸️ Already extracting products for quote:', specificQuoteId);
    return; // Skip this execution
  }
  
  // Mark as extracting
  isExtractingProductsRef.current[specificQuoteId] = true;
  
  try {
    const quoteDetailResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/quotes/${specificQuoteId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const quoteDetail = quoteDetailResponse.data.data;
    if (quoteDetail) {
      console.log('✅ Quote detail loaded:', quoteDetail);
      await extractProductsForMasterView(quoteDetail);
      console.log('✅ Products loaded for specific quote');
      
      // ⭐⭐ NEW: Update the quote in allQuotes with the detail data
      setAllQuotes(prev => prev.map(q => 
        q.id === specificQuoteId 
          ? { 
              ...q, 
              administrator_id: quoteDetail.administrator_id?.toString(),
              underwriter_id: quoteDetail.underwriter_id?.toString()
            } 
          : q
      ));
    }
  } catch (productError) {
    console.error('❌ Error fetching quote detail:', productError);
    
    // ⭐⭐ NEW: Still clean up even on error
    isExtractingProductsRef.current[specificQuoteId] = false;
    
    // Fallback: create a minimal quote detail object
    const fallbackQuoteDetail = {
      id: specificQuoteId,
      referenceNumber: specificQuote.referenceNumber,
      reference: specificQuote.reference,
      created_at: specificQuote.created_at,
      status: specificQuote.status,
      client_id: specificQuote.client_id
    };
    await extractProductsForMasterView(fallbackQuoteDetail as QuoteDetailData);
  } finally {
    // ⭐⭐ NEW: Always clean up when done
    isExtractingProductsRef.current[specificQuoteId] = false;
  }
}
            
          } else {
            console.warn('⚠️ No quotes data found in response');
            setAllQuotes([]);
            setViewMode('master');
          }
        } catch (quotesError: any) {
          console.error('❌ Error fetching quotes:', quotesError);
          setAllQuotes([]);
          setViewMode('master');
        }
      } else {
        console.error('❌ No client ID provided');
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ General error in fetchData:', error);
    } finally {
      setLoading(false);
      console.log('🏁 Loading set to false');
    }
  };

  fetchData();
}, [id, clientIdFromState, location.state]);

  // Function to fetch consultant
  const fetchConsultant = async (consultantId: string, token: string) => {
  try {
    console.log('👨‍💼 Fetching consultant:', consultantId);
    const consultantRes = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/consultants/${consultantId}`,
      { 
        headers: { 
          Authorization: `Bearer ${token}`
        } 
      }
    );
    const consultantData = consultantRes.data.data;
    setConsultant(consultantData);
    console.log('✅ Consultant set:', consultantData);
  } catch (err) {
    console.error('❌ Error fetching consultant:', err);
  }
};

  // Function to go back to master view
  const handleBackToMaster = () => {
    setViewMode('master');
    setSelectedQuote(null);
    setAllProducts([]);
    setConsultant(null);
    setAdministrators([]);
    setUnderwriters([]);
    window.history.pushState({}, '', `/quotes/master`);
    console.log('✅ Returned to master view');
  };

  // Extract products from quote data (for detail view)
  const extractProductsFromQuote = async (quoteData: QuoteDetailData) => {
    console.log('🚀 START extractProductsFromQuote for:', quoteData.referenceNumber);
    try {
      setLoadingProducts(true);
      const products: any[] = [];

      console.log('🔍 Starting product extraction...');
      console.log('📋 Quote ID:', quoteData.id, 'Client ID:', quoteData.client_id);

      // Check the API response for products
      const productArrays = [
        { key: 'vehicles', type: 'vehicle' },
        { key: 'properties', type: 'property' },
        { key: 'riskItems', type: 'risk_item' },
        { key: 'householdContents', type: 'household' },
        { key: 'trailersCaravans', type: 'trailer' },
        { key: 'valueAddedProducts', type: 'value_added' },
        { key: 'waterCrafts', type: 'water_craft' },
        { key: 'products', type: 'general' }
      ];

      let productsFromApi = 0;

      console.log('🔍 Checking product arrays in API response...');
      productArrays.forEach(({ key, type }) => {
        const data = quoteData[key as keyof QuoteDetailData];
        console.log(`   - ${key}:`, data);
        if (data && Array.isArray(data)) {
          const validProducts = data.filter(item => item !== null && typeof item === 'object');
          
          console.log(`📦 ${key} from API:`, validProducts.length);
          
          validProducts.forEach((item, index) => {
            products.push({
              id: item.id || `${type}-api-${index}-${Date.now()}`,
              type: type,
              name: getProductName(item, type),
              value: getProductValue(item),
              description: getProductDescription(item, type),
              data: item,
              source: 'api_response'
            });
            productsFromApi++;
          });
        }
      });

      console.log(`✅ Products from API response: ${productsFromApi}`);
      console.log('📊 Total products found:', products.length);
      setAllProducts(products);

    } catch (error) {
      console.error('❌ Error extracting products from quote:', error);
      setAllProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Helper functions for product data extraction
  const getProductName = (item: any, type: string): string => {
    switch (type) {
      case 'note':
        return 'Note';
      case 'file':
        return item.file_name || item.fileName || 'File';  
      case 'vehicle':
        return `${item.make || ''} ${item.model || ''}`.trim() || 'Vehicle';
      case 'property':
        return item.property_type || item.propertyType || item.address_line_1 || item.addressLine1 || item.name || 'Property';
      case 'risk_item':
        return item.itemName || item.item_name || 'Risk Item';
      case 'household':
        return item.contentType || item.name || 'Household Contents';
      case 'trailer':
        return item.vehicle_type || item.vehicleType || item.type || item.name || 'Trailer/Caravan';
      case 'value_added':
        return item.name || 'Value Added Product';
      case 'water_craft':
        return item.vessel_name || item.vesselName || `${item.craft_type || item.craftType || ''} ${item.year || ''}`.trim() || 'Water Craft';
      case 'claim':
        return 'Previous Claim';  
      default:
        return item.name || 'Product';
    }
  };

  const formatPropertyDescription = (property: Property): string => {
    const parts = [];
    
    // Debug what we're getting
    console.log('🔍 formatPropertyDescription received:', {
      addressLine1: property.addressLine1,
      suburb: property.suburb,
      city: property.city,
      propertyType: property.propertyType
    });
    
    if (property.addressLine1) parts.push(property.addressLine1);
    if (property.suburb) parts.push(property.suburb);
    if (property.city) parts.push(property.city);
    
    const result = parts.length > 0 ? parts.join(', ') : 'Property';
    console.log('✅ Formatted description:', result);
    
    return result;
  };

  // Helper function to get product value
  const getProductValue = (item: any, type?: string): number => {
  if (type === 'note' || type === 'file') return 0;
  
  let value = 0;
  
  // Check for claim-specific field
  if (type === 'claim') {
    if (item.claim_value !== undefined && item.claim_value !== null) {
      value = item.claim_value;
    } else if (item.claimValue !== undefined && item.claimValue !== null) {
      value = item.claimValue;
    }
  } 
  // Check for watercraft total_sum_insured
  else if (type === 'water_craft') {
    if (item.total_sum_insured !== undefined && item.total_sum_insured !== null) {
      value = item.total_sum_insured;
    } else if (item.totalSumInsured !== undefined && item.totalSumInsured !== null) {
      value = item.totalSumInsured;
    }
  }
  // For other product types
  else if (item.value !== undefined && item.value !== null) {
    value = item.value;
  } else if (item.insuredValue !== undefined && item.insuredValue !== null) {
    value = item.insuredValue;
  } else if (item.insured_value !== undefined && item.insured_value !== null) {
    value = item.insured_value;
  }
  
  const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value);
  console.log(`🔍 getProductValue for ${type}:`, { item, value, numericValue, isNaN: isNaN(numericValue) });
  return isNaN(numericValue) ? 0 : numericValue;
};

  // Helper function to get parking location name from ID
  const getParkingLocationName = (locationId: string): string => {
    const parkingLocations = [
      { id: '1', name: 'Garage' },
      { id: '2', name: 'Car Park' },
      { id: '3', name: 'Marked parking area' },
      { id: '4', name: 'Undercover parking' },
      { id: '5', name: 'Behind close gates' },
      { id: '6', name: 'House carport' },
      { id: '7', name: 'Security estate carport' },
      { id: '8', name: 'Street' },
      { id: '9', name: 'Driveway' },
      { id: '10', name: 'Security Complex' },
      { id: '11', name: 'Other' },
    ];
    
    if (!locationId) return 'Not specified';
    
    const location = parkingLocations.find(loc => loc.id === locationId);
    return location ? location.name : locationId;
  };

  // Helper function to get person name
  const getPersonName = (personId: string): string => {
    if (!personId) return 'Not specified';
    
    // Check if it's a client ID (starts with "client-")
    if (personId.startsWith('client-')) {
      if (clientFromHook) {
        return `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim() || 'Client';
      }
      return 'Client';
    }
    
    // For persons of interest, return placeholder for now
    // You can enhance this if you have persons of interest data
    return 'Person';
  };

  // Helper function to get all persons for vehicle
  const getAllPersonsForVehicle = (): Array<{id: string, name: string}> => {
    const persons: Array<{id: string, name: string}> = [];
    
    // Add client
    if (clientFromHook) {
      persons.push({
        id: `client-${clientFromHook.id}`,
        name: `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim()
      });
    }
    
    return persons;
  };

  // Helper function to get person name with data
  const getPersonNameWithData = (personId: string, personsList: Array<{id: string, name: string}>): string => {
    if (!personId) return 'Not specified';
    
    const person = personsList.find(p => p.id === personId);
    return person ? person.name : personId;
  };

  const getProductDescription = (item: any, type: string): string => {
    switch (type) {
      case 'vehicle':
        return item.registrationNumber || item.description || `${item.make || ''} ${item.model || ''}`.trim() || 'Vehicle';
      case 'property':
        // Use formatPropertyDescription to get address fields
        const propertyData: Property = {
          id: item.id,
          addressLine1: item.address_line_1 || item.addressLine1 || '',
          addressLine2: item.address_line_2 || item.addressLine2 || '',
          suburb: item.suburb || '',
          city: item.city || '',
          postalCode: item.postal_code || item.postalCode || '',
          region: item.region || '',
          country: item.country || '',
          propertyType: item.property_type || item.propertyType || '',
          residenceType: item.residence_type || item.residenceType || '',
          buildingAge: item.building_age || item.buildingAge || '',
          situatedAt: item.situated_at || item.situatedAt || '',
          coverType: item.cover_type || item.coverType || '',
          insuredValue: item.insured_value || item.insuredValue || 0,
        };
        return formatPropertyDescription(propertyData);
      case 'risk_item':
        return item.description || item.itemName || item.item_name || 'Risk Item';
      case 'household':
        return item.description || 'Household Contents';
      case 'trailer':
        return item.description || 'Trailer/Caravan';
      case 'value_added':
        return item.description || 'Value Added Product';
      case 'water_craft':
        return item.vessel_name || item.vesselName || `${item.hull_make || item.hullMake || ''} ${item.craft_type || item.craftType || ''}`.trim() || 'Water Craft';
      case 'note':
        return item.note_content || item.content || 'Note';
      case 'file':
        return item.description || item.file_name || item.fileName || 'File';
      case 'claim':
        return item.description || 'Previous Claim';
      default:
        return item.description || 'Product';
    }
  };

  const generateProductKey = (productType: string, productId: string, quoteId: string) => {
  return `${quoteId}-${productId}-${productType}`;
};

// Add this helper function for email logging
const logEmailToDatabase = async (emailData: {
  clientId: string;
  recipient: string;
  recipientType: 'client' | 'administrator' | 'underwriter';
  subject: string;
  quoteId: string;
  emailType: 'quotation' | 'signature_link' | 'proposal' | 'general';
  status: 'pending' | 'sent' | 'failed';
  errorMessage?: string;
  metadata?: any;
}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token for email logging');
      return null;
    }

    // Get user name from localStorage
    const userData = localStorage.getItem('user');
    let performedBy = 'System';
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const firstName = user.first_name || user.name || user.username || '';
        const lastName = user.last_name || user.surname || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        if (fullName) {
          performedBy = fullName;
        } else if (user.email) {
          performedBy = user.email;
        } else if (user.username) {
          performedBy = user.username;
        }
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
      }
    }

    // Add performedBy to metadata
    const updatedMetadata = {
      ...(emailData.metadata || {}),
      performedBy
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/email-logs`,
      {
        ...emailData,
        metadata: updatedMetadata
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    console.log(`✅ Email logged successfully by ${performedBy}:`, response.data);
    return response.data.id;
  } catch (error: any) {
    // Don't throw error - logging failure shouldn't break email sending
    console.error('❌ Failed to log email:', error.response?.data || error.message);
    return null;
  }
};

// Helper function to log product addition
const logProductAddition = async (quoteId: string | null | undefined, productType: string, productName: string, metadata: any = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    // If quoteId is null or undefined, skip logging
    if (!quoteId) {
      console.log('⏸️ Skipping activity log - no quote ID yet');
      return;
    }

    // Get user name from localStorage
    const userData = localStorage.getItem('user');
    let performedBy = 'System';
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const firstName = user.first_name || user.name || user.username || '';
        const lastName = user.last_name || user.surname || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        if (fullName) {
          performedBy = fullName;
        } else if (user.email) {
          performedBy = user.email;
        } else if (user.username) {
          performedBy = user.username;
        }
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
      }
    }

    await axios.post(
  `${API_URL}/api/quote-activity-logs`,
  {
    quote_id: parseInt(quoteId),
    action_type: 'product_updated',
    description: `Product updated: ${productName} (${getProductTypeDisplay(productType)})`,
    performed_by: performedBy,  // ✅ ADD THIS - Top level field
    metadata: {
      productType,
      productName,
      performedBy,
      ...metadata,
      updatedAt: new Date().toISOString()
    }
  },
  { headers: { Authorization: `Bearer ${token}` } }
);
    console.log(`✅ Activity logged: product_added - ${productName} by ${performedBy}`);
  } catch (logError) {
    console.error('❌ Failed to log product addition:', logError);
    // Don't fail the navigation if logging fails
  }
};

// Helper function to log product update
const logProductUpdate = async (quoteId: string, productType: string, productName: string, metadata: any = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Get user name from localStorage
    const userData = localStorage.getItem('user');
    let performedBy = 'System';
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const firstName = user.first_name || user.name || user.username || '';
        const lastName = user.last_name || user.surname || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        if (fullName) {
          performedBy = fullName;
        } else if (user.email) {
          performedBy = user.email;
        } else if (user.username) {
          performedBy = user.username;
        }
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
      }
    }

    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/quote-activity-logs`,
      {
        quote_id: parseInt(quoteId),
        action_type: 'product_updated',
        description: `Product updated: ${productName} (${getProductTypeDisplay(productType)})`,
        metadata: {
          productType,
          productName,
          performedBy,
          ...metadata,
          updatedAt: new Date().toISOString()
        }
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    console.log(`✅ Activity logged: product_updated - ${productName} by ${performedBy}`);
  } catch (logError) {
    console.error('❌ Failed to log product update:', logError);
  }
};

// WhatsApp helper functions
const getTotalInsuredValue = (): number => {
  let total = 0;
  Object.values(allQuoteProducts).forEach(products => {
    products.forEach(product => {
      total += product.value || 0;
    });
  });
  return total;
};

const generateProductsSummary = (): string => {
  let summary = '';
  
  Object.entries(allQuoteProducts).forEach(([quoteId, products]) => {
    const quote = allQuotes.find(q => q.id === quoteId);
    if (quote && products.length > 0) {
      summary += `\n*Quote: ${quote.referenceNumber || quote.reference}*\n`;
      
      products.forEach((product, index) => {
        summary += `• ${product.name}: ${formatCurrency(product.value)}\n`;
      });
    }
  });
  
  return summary || 'No products available';
};

// WhatsApp share function
const handleWhatsAppShare = () => {
  if (allQuotes.length === 0) {
    setSaveStatus('error');
    setSaveMessage('No quotes available to share');
    return;
  }

  // Get client's mobile number from clientFromHook
  const clientPhone = clientFromHook?.mobile_number || clientFromHook?.phone;
  
  if (!clientPhone) {
    setSaveStatus('error');
    setSaveMessage('Client phone number not available');
    return;
  }

  console.log('📱 Original client phone:', clientPhone);

  // Format phone number for WhatsApp
  const formattedPhone = formatPhoneForWhatsApp(clientPhone);
  
  if (!formattedPhone) {
    setSaveStatus('error');
    setSaveMessage('Invalid mobile number format');
    return;
  }

  console.log('📱 Formatted phone for WhatsApp:', formattedPhone);

  const currentQuote = allQuotes[0];
  
  // Create WhatsApp message with formatted text
  const message = `*QUOTATION SUMMARY*

Hello ${clientFromHook?.name || 'Valued Client'}!

Here is your quotation summary:

*Quote Reference:* ${currentQuote.referenceNumber || currentQuote.reference}
*Created:* ${formatDate(currentQuote.createdDate || currentQuote.created_at)}
*Status:* ${currentQuote.status}

*Products Summary:*
${generateProductsSummary()}

*Total Products:* ${getTotalProductsCount()}

Please review the quotation and let me know if you have any questions.

Best regards,
${consultant ? `${consultant.first_name} ${consultant.last_name}` : 'Your Insurance Consultant'}`;

  // Use WhatsApp Web URL for better desktop experience
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappUrl = isMobile 
    ? `https://api.whatsapp.com/send?phone=${formattedPhone}&text=${encodeURIComponent(message)}`
    : `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encodeURIComponent(message)}`;
  
  console.log('🔗 Opening WhatsApp URL:', whatsappUrl);
  
  // Open WhatsApp in a new tab
  const newWindow = window.open(whatsappUrl, '_blank');
  if (newWindow) {
    newWindow.focus();
  }
  
  setSaveStatus('success');
  setSaveMessage('WhatsApp opened with quote details!');
};

// Quote master modal success handlers
const handleQuoteDeclineSuccess = () => {
  const currentQuoteId = allQuotes[0]?.id;
  if (!currentQuoteId) return;
  setAllQuotes((prev) =>
    prev.map((q) =>
      q.id === currentQuoteId ? { ...q, status: 'declined' } : q
    )
  );
  setSaveStatus('success');
  setSaveMessage('Quotation declined successfully.');
  setTimeout(() => {
    setSaveStatus('idle');
    setSaveMessage('');
  }, 3000);
};

const handleQuoteEmailSent = () => {
  setSaveStatus('success');
  setSaveMessage('Email sent successfully.');
  setTimeout(() => {
    setSaveStatus('idle');
    setSaveMessage('');
  }, 3000);
};

const handleQuoteWhatsAppSent = () => {
  setSaveStatus('success');
  setSaveMessage('WhatsApp message prepared.');
  setTimeout(() => {
    setSaveStatus('idle');
    setSaveMessage('');
  }, 3000);
};

const handleOpenAcceptQuotationModal = () => {
  // Collect all ACTIVE administrators and underwriters
  const activeAdmins = administrators
    .filter(a => a.quotation_status === 'active' || a.quotation_status === undefined);
    
  const activeUnderwriters = underwriters
    .filter(u => u.quotation_status === 'active' || u.quotation_status === undefined);

  // VALIDATION: Check the rule - only 1 administrator OR 1 underwriter can be active
  const activeAdminCount = activeAdmins.length;
  const activeUnderwriterCount = activeUnderwriters.length;
  const totalActive = activeAdminCount + activeUnderwriterCount;

  // Rule 1: Cannot have both an administrator AND an underwriter active
  if (activeAdminCount > 0 && activeUnderwriterCount > 0) {
    setSaveStatus('warning');
    setSaveMessage('Please make sure only 1 Administrator or Underwriter is selected');
    
    // Show popup/dialog with OK button
    alert('Please make sure only 1 Administrator or Underwriter is selected');
    
    setTimeout(() => setSaveStatus('idle'), 4000);
    return;
  }
  
  // Rule 2: Cannot have more than 1 administrator active
  if (activeAdminCount > 1) {
    setSaveStatus('warning');
    setSaveMessage('Please make sure only 1 Administrator is selected');
    
    // Show popup/dialog with OK button
    alert('Please make sure only 1 Administrator is selected');
    
    setTimeout(() => setSaveStatus('idle'), 4000);
    return;
  }
  
  // Rule 3: Cannot have more than 1 underwriter active
  if (activeUnderwriterCount > 1) {
    setSaveStatus('warning');
    setSaveMessage('Please make sure only 1 Underwriter is selected');
    
    // Show popup/dialog with OK button
    alert('Please make sure only 1 Underwriter is selected');
    
    setTimeout(() => setSaveStatus('idle'), 4000);
    return;
  }

  // If we pass all validations, proceed
  const allActive = [
    ...activeAdmins.map(admin => ({
      id: admin.id,
      name: admin.name,
      type: 'administrator' as const,
      premium: admin.premium || '',
      brokerFee: '',
      managementFee: '',
      insurerQuotationNumber: "",
    })),
    ...activeUnderwriters.map(uw => ({
      id: uw.id,
      name: uw.name,
      type: 'underwriter' as const,
      premium: uw.premium || '',
      brokerFee: '',
      managementFee: '',
      insurerQuotationNumber: "",
    }))
  ];

  if (allActive.length === 0) {
    setSaveStatus('warning');
    setSaveMessage('No active insurers found. Cannot accept quotation.');
    setTimeout(() => setSaveStatus('idle'), 4000);
    return;
  }

  // Check if the active insurer has a premium entered
  const activeInsurer = allActive[0];
  if (!activeInsurer.premium || activeInsurer.premium === '' || parseFloat(activeInsurer.premium) <= 0) {
    setSaveStatus('warning');
    setSaveMessage(`Please enter a premium amount for ${activeInsurer.name} and click "Save Insurers" before accepting the quotation.`);
    alert(`Please enter a premium amount for ${activeInsurer.name} and click "Save Insurers" first.`);
    setTimeout(() => setSaveStatus('idle'), 5000);
    return;
  }

  // ✅ NEW: Verify that the saved premium matches the current input exactly
  if (activeInsurer.premium !== savedPremiumValue) {
    setSaveStatus('warning');
    setSaveMessage('You have unsaved changes. Please click "Save Insurers" again before accepting.');
    alert('You have unsaved changes in the premium. Please click "Save Insurers" to apply them first.');
    setTimeout(() => setSaveStatus('idle'), 5000);
    return;
  }

  // Check if the insurer data has been saved to database (not just in local state)
  // This ensures the user clicked "Save Insurers"
  const hasUnsavedAdminChanges = administrators.some(a => 
    a.quotation_status === 'active' && (!a.premium || a.premium === '' || parseFloat(a.premium) <= 0)
  );
  const hasUnsavedUnderwriterChanges = underwriters.some(u => 
    u.quotation_status === 'active' && (!u.premium || u.premium === '' || parseFloat(u.premium) <= 0)
  );

  if (hasUnsavedAdminChanges || hasUnsavedUnderwriterChanges) {
    setSaveStatus('warning');
    setSaveMessage('Please enter a premium amount for the active insurer and click "Save Insurers" before accepting the quotation.');
    alert('Please enter a premium amount and click "Save Insurers" button first to save your changes.');
    setTimeout(() => setSaveStatus('idle'), 5000);
    return;
  }

  setAcceptFormData(allActive);
  setIsAcceptQuotationModalOpen(true);
};

  // Force update when quotes are loaded to ensure removal reasons are displayed
  useEffect(() => {
    if (allQuotes.length > 0 && removalReasons.length > 0 && !loading) {
      console.log('🔄 Quotes and removal reasons loaded, component should show removal reasons');
      // This will force a re-render
    }
  }, [allQuotes, removalReasons, loading]);

  useEffect(() => {
  const fetchInsurersForQuote = async () => {
    if (viewMode === 'master' && allQuotes.length > 0) {
      const currentQuote = allQuotes[0];
      const token = localStorage.getItem("token");
      
      if (!token) {
        console.error('❌ No token found for fetching insurers');
        return;
      }

      console.log('🔍 DEBUG - Underwriter data in quote:', {
        quoteId: currentQuote.id,
        singleUnderwriterId: currentQuote.underwriter_id,
        underwriterIdsArray: currentQuote.underwriter_ids,
        hasArray: !!currentQuote.underwriter_ids,
        arrayLength: currentQuote.underwriter_ids?.length || 0,
        fullQuote: currentQuote
      });

      // Use single ID when arrays are empty or undefined
      let adminIds: string[] = [];
      let uwIds: string[] = [];

      // Check for array first, then single ID
      if (currentQuote.administrator_ids && currentQuote.administrator_ids.length > 0) {
        adminIds = currentQuote.administrator_ids;
        console.log('✅ Using administrator_ids array:', adminIds);
      } else if (currentQuote.administrator_id) {
        adminIds = [currentQuote.administrator_id.toString()];
        console.log('🔄 Using single administrator_id as array:', adminIds);
      }

      if (currentQuote.underwriter_ids && currentQuote.underwriter_ids.length > 0) {
        uwIds = currentQuote.underwriter_ids;
        console.log('✅ Using underwriter_ids array:', uwIds);
      } else if (currentQuote.underwriter_id) {
        uwIds = [currentQuote.underwriter_id.toString()];
        console.log('🔄 Using single underwriter_id as array:', uwIds);
      }

      console.log('🔄 Final underwriter IDs to fetch:', uwIds);

      if (adminIds.length > 0) {
        console.log('🔍 Fetching administrators...');
        await fetchMultipleAdministrators(adminIds, token);
      } else {
        console.log('⚠️ No administrator IDs found in quote data');
        setAdministrators([]);
      }

      if (uwIds.length > 0) {
        console.log('🔍 Fetching underwriters...');
        await fetchMultipleUnderwriters(uwIds, token);
      } else {
        console.log('⚠️ No underwriter IDs found in quote data');
        setUnderwriters([]);
      }
    }
  };

  fetchInsurersForQuote();
}, [allQuotes, viewMode]); // ❌ Remove isNewQuote so it only fires when the quote data actually changes

const fetchMultipleAdministrators = async (adminIds: string[], token: string) => {
  if (!adminIds || adminIds.length === 0) {
    console.log('ℹ️ No administrator IDs provided');
    setAdministrators([]);
    return;
  }
  
  try {
    console.log('🔍 Fetching administrators with IDs:', adminIds);
    
    const adminPromises = adminIds.map(async (adminId) => {
      try {
        // First, get the administrator details
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/administrators/${adminId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.data) {
          const adminData = response.data.data;
          const adminEmail = adminData.quotationsEmail || adminData.email || adminData.contact_email || adminData.contactEmail || '';
          
          // ✅ Get the premium from the administrator's own data
          const adminPremium = adminData.premium ? adminData.premium.toString() : '';
          
          // ✅ FIX: If this is a new quote, force the premium to be blank
          // We detect a new quote by checking if the quote has no products yet (isNewQuote state)
          const shouldShowBlankPremium = isNewQuote || !adminPremium || adminPremium === '0' || adminPremium === '0.00';
          const finalPremium = shouldShowBlankPremium ? '' : adminPremium;
          
          console.log(`✅ Admin ${adminId} (${adminData.name}) has premium: ${finalPremium || 'BLANK'}`);
          
          return {
            id: adminId,
            name: adminData.name || adminData.company_name || 'Administrator',
            email: adminEmail,
            type: 'administrator' as const,
            quotation_status: adminData.quotation_status || 'active',
            premium: finalPremium
          } as AdminUnderwriterData;
        }
        return null;
      } catch (error: any) {
        console.error(`❌ Error fetching administrator ${adminId}:`, error);
        return {
          id: adminId,
          name: `Administrator ${adminId}`,
          email: '',
          type: 'administrator' as const,
          quotation_status: 'active',
          premium: '' // Keep empty for errors
        } as AdminUnderwriterData;
      }
    });
    
    const adminResults = await Promise.all(adminPromises);
    const validAdmins = adminResults.filter(admin => admin !== null) as AdminUnderwriterData[];
    
    setAdministrators(validAdmins);
    
  } catch (err) {
    console.error("❌ Error in fetchMultipleAdministrators:", err);
    setAdministrators([]);
  }
};

const fetchMultipleUnderwriters = async (underwriterIds: string[], token: string) => {
  if (!underwriterIds || underwriterIds.length === 0) {
    console.log('ℹ️ No underwriter IDs provided');
    setUnderwriters([]);
    return;
  }
  
  try {
    console.log('🔍 Fetching underwriters with IDs:', underwriterIds);
    
    const underwriterPromises = underwriterIds.map(async (underwriterId) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/underwriters/${underwriterId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.data) {
          const uwData = response.data.data;
          const underwriterEmail = uwData.quotations_email || uwData.quotation_email || uwData.email || '';
          
          // ✅ Get the premium from the underwriter's own data
          const uwPremium = uwData.premium ? uwData.premium.toString() : '';
          
          // ✅ FIX: If this is a new quote, force the premium to be blank
          const shouldShowBlankPremium = isNewQuote || !uwPremium || uwPremium === '0' || uwPremium === '0.00';
          const finalPremium = shouldShowBlankPremium ? '' : uwPremium;
          
          console.log(`✅ Underwriter ${underwriterId} (${uwData.name || uwData.underwriter_name}) has premium: ${finalPremium || 'BLANK'}`);
          
          return {
            id: underwriterId,
            name: uwData.name || uwData.underwriter_name || 'Underwriter',
            email: underwriterEmail.trim(),
            type: 'underwriter' as const,
            quotation_status: uwData.quotation_status || 'active',
            premium: finalPremium
          } as AdminUnderwriterData;
        }
        return null;
      } catch (error: any) {
        console.error(`❌ Error fetching underwriter ${underwriterId}:`, error);
        return {
          id: underwriterId,
          name: `Underwriter ${underwriterId}`,
          email: '',
          type: 'underwriter' as const,
          quotation_status: 'active',
          premium: '' // Keep empty for errors
        } as AdminUnderwriterData;
      }
    });
    
    const underwriterResults = await Promise.all(underwriterPromises);
    const validUnderwriters = underwriterResults.filter(uw => uw !== null) as AdminUnderwriterData[];
    
    setUnderwriters(validUnderwriters);
    
  } catch (err) {
    console.error("❌ Error in fetchMultipleUnderwriters:", err);
    setUnderwriters([]);
  }
};

// ❌ REMOVED: Do not reset premiums for new quotes here.
// The data should always reflect what is in the database, even if it's a new quote.
// If the user wants to change the premium, they will overwrite it.

useEffect(() => {
  if (allQuotes.length === 0 || loading) return;

  console.log('🔄 Checking localStorage drafts (strict deduplication)...');

  allQuotes.forEach(quote => {
    const quoteId = quote.id;
    const key = `quote_${quoteId}_products`;
    const savedJson = localStorage.getItem(key);

    if (!savedJson || savedJson === '[]') {
      console.log(`No draft found in localStorage for quote ${quoteId}`);
      return;
    }

    try {
      const savedProducts = JSON.parse(savedJson);
      if (!Array.isArray(savedProducts) || savedProducts.length === 0) return;

      console.log(`📥 Found ${savedProducts.length} draft items in localStorage for quote ${quoteId}`);

      setAllQuoteProducts(prev => {
        const current = prev[quoteId] || [];

        // Create a signature for each existing product to detect duplicates more reliably
        const existingSignatures = new Set(
          current.map(p => {
            if (p.type === 'vehicle') {
              return `${p.type}|${p.data?.registration_number || p.data?.registrationNumber || ''}|${p.data?.vin_number || p.data?.vinNumber || ''}`;
            }
            if (p.type === 'property') {
              return `${p.type}|${p.data?.addressLine1 || p.data?.address_line_1 || ''}|${p.data?.suburb || ''}`;
            }
            if (p.type === 'household') {
              return `${p.type}|${p.data?.addressLine1 || ''}|${p.data?.insuredValue || 0}`;
            }
            // fallback for others
            return `${p.type}|${String(p.id)}|${p.data?.name || p.name || ''}`;
          })
        );

        const toAdd = savedProducts.filter(saved => {
          let signature;

          if (saved.type === 'vehicle') {
            signature = `${saved.type}|${saved.data?.registration_number || saved.data?.registrationNumber || ''}|${saved.data?.vin_number || saved.data?.vinNumber || ''}`;
          } else if (saved.type === 'property') {
            signature = `${saved.type}|${saved.data?.addressLine1 || saved.data?.address_line_1 || ''}|${saved.data?.suburb || ''}`;
          } else if (saved.type === 'household') {
            signature = `${saved.type}|${saved.data?.addressLine1 || ''}|${saved.data?.insuredValue || 0}`;
          } else {
            signature = `${saved.type}|${String(saved.id)}|${saved.data?.name || saved.name || ''}`;
          }

          if (existingSignatures.has(signature)) {
            console.log(`🗑️ Skipping probable duplicate draft: ${signature}`);
            return false;
          }

          console.log(`➕ Adding unique draft item: ${signature}`);
          return true;
        });

        if (toAdd.length === 0) {
          console.log(`No unique new items from localStorage for quote ${quoteId}`);
          return prev;
        }

        console.log(`Adding ${toAdd.length} unique draft products to quote ${quoteId}`);

        return {
          ...prev,
          [quoteId]: [...current, ...toAdd]
        };
      });
    } catch (err) {
      console.error(`Failed to parse localStorage for quote ${quoteId}:`, err);
    }
  });
}, [allQuotes, loading]);

useEffect(() => {
  if (allQuotes.length > 0) {
    const currentQuote = allQuotes[0];
    console.log('🔍 DEBUG - Current quote insurers data:', {
      quoteId: currentQuote.id,
      quoteReference: currentQuote.referenceNumber,
      administrator_ids: currentQuote.administrator_ids,
      underwriter_ids: currentQuote.underwriter_ids,
      administrators: administrators,
      underwriters: underwriters
    });
  }
}, [allQuotes, administrators, underwriters]);

// Add this useEffect to preserve products when the quote data refreshes
useEffect(() => {
  // Save current products to a backup cache whenever they change
  if (Object.keys(allQuoteProducts).length > 0) {
    Object.entries(allQuoteProducts).forEach(([quoteId, products]) => {
      if (products.length > 0) {
        const backupKey = `quote_${quoteId}_products_backup`;
        localStorage.setItem(backupKey, JSON.stringify(products));
        console.log(`💾 Backed up ${products.length} products for quote ${quoteId}`);
      }
    });
  }
}, [allQuoteProducts]);

// restore backed up products on component mount
useEffect(() => {
  if (allQuotes.length > 0 && !loading) {
    allQuotes.forEach(quote => {
      const backupKey = `quote_${quote.id}_products_backup`;
      const backupData = localStorage.getItem(backupKey);
      
      if (backupData) {
        try {
          const backedUpProducts = JSON.parse(backupData);
          const existingProducts = allQuoteProducts[quote.id] || [];
          
          // Only restore if we have fewer products than backed up
          if (backedUpProducts.length > existingProducts.length) {
            console.log(`📥 Restoring ${backedUpProducts.length} backed up products for quote ${quote.id}`);
            
            setAllQuoteProducts(prev => ({
              ...prev,
              [quote.id]: backedUpProducts
            }));
          }
          
          // Clean up backup
          localStorage.removeItem(backupKey);
        } catch (error) {
          console.error('Error restoring backed up products:', error);
        }
      }
    });
  }
}, [allQuotes, loading]);

// function to handle viewing risk item details
const handleViewRiskItemDetails = (product: any, quote: QuoteSummary) => {
  console.log('🔍 Opening risk item details:', { product, quote });
  
  // Set the product for modal viewing
  setProductToView({ product, quote });
  setIsViewProductModalVisible(true);
};

  // Helper function to get status badge color
  const getStatusBadgeColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'sent':
      case 'accepted':
      case 'active':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'draft':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
      case 'declined':
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Helper function to format phone number for WhatsApp
const formatPhoneForWhatsApp = (phoneNumber: string): string | null => {
  if (!phoneNumber) return null;

  console.log('🔧 Formatting phone number:', phoneNumber);

  // Remove all non-digit characters except +
  let cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
  
  // Handle different formats
  if (cleanNumber.startsWith('+')) {
    // Already in international format, remove + for WhatsApp
    cleanNumber = cleanNumber.substring(1);
  } else if (cleanNumber.startsWith('27') && cleanNumber.length === 11) {
    // Already in correct format (27XXXXXXXXX)
    // No changes needed
  } else if (cleanNumber.startsWith('0') && cleanNumber.length === 10) {
    // Convert 083... to 2783...
    cleanNumber = '27' + cleanNumber.substring(1);
  } else {
    // Try to add country code
    cleanNumber = '27' + cleanNumber;
  }

  // Validate the final number
  if (cleanNumber.length !== 11 || !cleanNumber.startsWith('27')) {
    console.error('❌ Invalid phone number format after cleaning:', cleanNumber);
    return null;
  }

  console.log('✅ Final formatted number:', cleanNumber);
  return cleanNumber;
};

  const handleViewFileDetails = (product: any, quote: QuoteSummary) => {
  console.log('📄 Opening file details:', { product, quote });
  
  // For now, just show in the existing modal
  setProductToView({ product, quote });
  setIsViewProductModalVisible(true);
};

  // function to render note cards
  const renderNoteCard = (product: any, quote: QuoteSummary) => {
    const noteContent = product.data.note_content || product.data.content || product.description || '';
    const noteDate = product.data.created_at || product.data.createdAt || '';
    
    // Limit content for display
    const previewContent = noteContent.length > 100 
      ? `${noteContent.substring(0, 100)}...` 
      : noteContent;
    
    return (
      <div 
        key={product.uniqueKey || product.id}
        className="border border-yellow-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-yellow-50"
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <h4 className="font-bold text-gray-800">Note</h4>
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex flex-col">
            <span className="text-gray-600 mb-1">Content:</span>
            <span className="text-gray-800 text-xs bg-white p-3 rounded border border-yellow-200">
              {previewContent}
            </span>
          </div>
          
          {noteDate && (
            <div className="flex justify-between text-xs text-gray-500">
              <span>Created:</span>
              <span>{formatDate(noteDate)}</span>
            </div>
          )}
          
          <div className="pt-2 border-t border-yellow-100 flex justify-between items-center">
            <button 
              onClick={() => handleViewProductDetails(product, quote)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View Full Note →
            </button>
            
            <button
              onClick={() => handleRemoveProduct(quote.id, product.id, product.type, 'Note')}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
              title="Remove this note"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviousInsuranceCard = (product: any, quote: QuoteSummary) => {
  const insurance = product.data;
  
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    try {
      // Parse the date and format it nicely
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'Invalid date';
      // Format as DD/MM/YYYY
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  const startDateFormatted = formatDate(insurance.startDate || insurance.start_date);
  const endDateFormatted = formatDate(insurance.endDate || insurance.end_date);
  // ✅ Use the formatted variables instead of calling formatDate again
  const insurancePeriod = `${startDateFormatted} - ${endDateFormatted}`;
  
  return (
    <div 
      key={product.uniqueKey || product.id}
      className="border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-green-50"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h4 className="font-bold text-gray-800">Previous Insurance</h4>
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        {/* Description */}
        <div className="flex flex-col">
          <span className="text-gray-600 mb-1">Description:</span>
          <span className="font-medium text-gray-800 text-xs bg-white p-2 rounded border border-green-200">
            {insurance.description}
          </span>
        </div>
        
        {/* Policy Number */}
        <div className="flex justify-between">
          <span className="text-gray-600">Policy Number:</span>
          <span className="font-medium">{insurance.policyNumber || insurance.policy_number}</span>
        </div>
        
        {/* Insurer */}
        <div className="flex justify-between">
          <span className="text-gray-600">Insurer:</span>
          <span className="font-medium">{insurance.insurer}</span>
        </div>
        
        {/* Insurance Period - USING THE FORMATTED VARIABLES */}
        <div className="flex justify-between">
          <span className="text-gray-600">Insurance Period:</span>
          <span className="font-medium">{insurancePeriod}</span>
        </div>
      </div>
      
      <div className="pt-2 border-t border-green-100 flex justify-between items-center mt-3">
        <button 
          onClick={() => handleViewProductDetails(product, quote)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          View Details →
        </button>
        
        <button
          onClick={() => handleRemoveProduct(quote.id, product.id, product.type, 'Previous Insurance')}
          className="text-red-500 hover:text-red-700 text-sm font-medium"
          title="Remove this insurance"
        >
          Remove
        </button>
      </div>
    </div>
  );
};

  const renderClaimCard = (product: any, quote: QuoteSummary) => {
    // ✅ DEFENSIVE: If this record has "insurer" but not "previous_insurer", it's a
    // Previous Insurance that got miscategorized as a claim. Render it with the green card instead.
    const hasInsurerField = Boolean(product.data.insurer);
    const hasPreviousInsurerField = Boolean(
      product.data.previous_insurer || product.data.previousInsurer
    );
    const hasClaimValue = product.data.claim_value !== undefined || product.data.claimValue !== undefined;

    if (hasInsurerField && !hasPreviousInsurerField && !hasClaimValue) {
      console.log('🔧 Re-rendering miscategorized record as Previous Insurance:', product.data);
      return renderPreviousInsuranceCard({ ...product, type: 'previous_insurance' }, quote);
    }

    // Extract data - check both snake_case and camelCase
    const description = product.data.description || '';
    const policyNumber = product.data.policy_number || product.data.policyNumber || '';
    const previousInsurer = product.data.previous_insurer || product.data.previousInsurer || '';
    const claimDate = product.data.claim_date || product.data.claimDate || '';
    const claimValue = product.data.claim_value || product.data.claimValue || 0;

    // ✅ DEFENSIVE: If this is a previous insurance miscategorized as a claim, use the green card instead
    const isActuallyPreviousInsurance = 
      product.data.insurer && !product.data.previous_insurer;
    
    if (isActuallyPreviousInsurance) {
      return renderPreviousInsuranceCard(product, quote);
    }
    
    console.log('🔍 DEBUG - Claim card data:', {
      description,
      policyNumber,
      previousInsurer,
      claimDate,
      claimValue,
      rawData: product.data
    });
    
    // Limit content for display
    const previewDescription = description.length > 100 
      ? `${description.substring(0, 100)}...` 
      : description;
    
    // Format the claim value
    const formatClaimValue = (value: any): string => {
      if (!value && value !== 0) return 'Not specified';
      
      try {
        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
        return isNaN(numValue) ? 'Not specified' : `R ${numValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`;
      } catch {
        return 'Not specified';
      }
    };
    
    return (
      <div 
        key={product.uniqueKey || product.id}
        className="border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-red-50"
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h4 className="font-bold text-gray-800">Previous Claim</h4>
          </div>
        </div>
        
        <div className="space-y-3 text-sm">
          {/* Description */}
          <div className="flex flex-col">
            <span className="text-gray-600 mb-1">Description:</span>
            <span className="text-gray-800 text-xs bg-white p-3 rounded border border-red-200">
              {previewDescription}
            </span>
          </div>
          
          {/* Claim Details Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Policy Number */}
            <div className="flex flex-col">
              <span className="text-gray-600 text-xs mb-1">Policy #</span>
              <span className="font-medium text-gray-900 bg-white p-2 rounded border">
                {policyNumber || 'Not specified'}
              </span>
            </div>
            
            {/* Previous Insurer */}
            {previousInsurer && (
              <div className="flex flex-col">
                <span className="text-gray-600 text-xs mb-1">Previous Insurer</span>
                <span className="font-medium text-gray-900 bg-white p-2 rounded border">
                  {previousInsurer}
                </span>
              </div>
            )}
            
            {/* Claim Date */}
            {claimDate && (
              <div className="flex flex-col">
                <span className="text-gray-600 text-xs mb-1">Claim Date</span>
                <span className="font-medium text-gray-900 bg-white p-2 rounded border">
                  {formatDate(claimDate)}
                </span>
              </div>
            )}
            
            {/* Claim Value - ALWAYS SHOW THIS */}
            <div className="flex flex-col">
              <span className="text-gray-600 text-xs mb-1">Claim Value</span>
              <span className="font-medium text-red-600 bg-white p-2 rounded border border-red-200">
                {formatClaimValue(claimValue)}
              </span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="pt-2 border-t border-red-100 flex justify-between items-center">
            <button 
              onClick={() => handleViewClaimDetails(product, quote)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details →
            </button>
            
            <button
              onClick={() => handleRemoveProduct(quote.id, product.id, product.type, 'Previous Claim')}
              className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1"
              title="Remove this claim"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add this function to render document cards
const renderDocumentCard = (product: any, quote: QuoteSummary) => {
  const documentData = product.data;
  const fileName = documentData.filename || documentData.file_name || documentData.original_name || 'Document';
  const fileSize = documentData.file_size || documentData.size || 0;
  const fileType = documentData.mime_type || documentData.mimetype || 'Unknown type';
  const uploadDate = documentData.created_at || documentData.uploaded_at || '';
  const description = documentData.description || '';
  
  // Format file size
  const formatFileSize = (bytes: any) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Get file icon based on type
  const getFileIcon = () => {
    if (fileType.includes('pdf')) {
      return (
        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    } else if (fileType.includes('image')) {
      return (
        <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
  };
  
  // Get file type display
  const getFileTypeDisplay = () => {
    if (fileType.includes('pdf')) return 'PDF Document';
    if (fileType.includes('image')) return 'Image';
    if (fileType.includes('word')) return 'Word Document';
    if (fileType.includes('excel')) return 'Excel Document';
    if (fileType.includes('text')) return 'Text Document';
    return 'Document';
  };
  
  // Handle document download
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotes/files/download/${product.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to download document');
    }
  };
  
  // Handle document delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/quotes/files/${product.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove from state
      const quoteId = quote.id;
      setAllQuoteProducts(prev => ({
        ...prev,
        [quoteId]: (prev[quoteId] || []).filter(p => p.id !== product.id)
      }));
      
      setSaveStatus('success');
      setSaveMessage('Document deleted successfully!');
    } catch (error) {
      console.error('Error deleting document:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to delete document');
    }
  };
  
  return (
    <div 
      key={product.uniqueKey || product.id}
      className="border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-blue-50"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
          {getFileIcon()}
          <div>
            <h4 className="font-bold text-gray-800 truncate max-w-xs" title={fileName}>
              {fileName.length > 30 ? `${fileName.substring(0, 30)}...` : fileName}
            </h4>
            <p className="text-xs text-gray-600">{getFileTypeDisplay()}</p>
          </div>
        </div>
 
      </div>
      
      <div className="space-y-3 text-sm">
        {description && (
          <div className="flex flex-col">
            <span className="text-gray-600 mb-1">Description:</span>
            <span className="text-gray-800 text-xs bg-white p-3 rounded border border-blue-200">
              {description.length > 100 
                ? `${description.substring(0, 100)}...` 
                : description}
            </span>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col">
            <span className="text-gray-600 text-xs mb-1">Size</span>
            <span className="font-medium text-gray-900 bg-white p-2 rounded border">
              {formatFileSize(fileSize)}
            </span>
          </div>
          
          {uploadDate && (
            <div className="flex flex-col">
              <span className="text-gray-600 text-xs mb-1">Uploaded</span>
              <span className="font-medium text-gray-900 bg-white p-2 rounded border">
                {formatDate(uploadDate)}
              </span>
            </div>
          )}
        </div>
        
        <div className="pt-2 border-t border-blue-100 flex justify-between items-center">
          <button 
            onClick={handleDownload}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download
          </button>
          
          <div className="flex gap-3">
            <button 
              onClick={() => handleViewDocumentDetails(product, quote)}
              className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details
            </button>
            
            <button
              onClick={handleDelete}
              className="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1"
              title="Delete this document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

  // Add this function to handle viewing document details
  const handleViewDocumentDetails = (product: any, quote: QuoteSummary) => {
    console.log('📄 Opening document details:', { product, quote });
    
    // Set the product for modal viewing
    setProductToView({ product, quote });
    
    // Check if we need to show in a modal or open directly
    if (product.data && product.data.mime_type && 
        (product.data.mime_type.includes('pdf') || product.data.mime_type.includes('image'))) {
      // For PDFs and images, open in new tab if URL exists
      if (product.data.url || product.data.file_path) {
        const url = product.data.url || `${process.env.REACT_APP_API_URL}/${product.data.file_path}`;
        window.open(url, '_blank');
      } else {
        // Otherwise show in modal
        setIsViewProductModalVisible(true);
      }
    } else {
      // For other types, show in modal
      setIsViewProductModalVisible(true);
    }
  };

    const handleViewClaimDetails = (product: any, quote: QuoteSummary) => {
      console.log('📝 Opening claim details:', { product, quote });
      
      // For now, just show in the existing modal
      setProductToView({ product, quote });
      setIsViewProductModalVisible(true);
    };

    // Add this function alongside renderNoteCard
    const renderFileCard = (product: any, quote: QuoteSummary) => {
      const fileName = product.data.file_name || product.data.fileName || 'File';
      const fileSize = product.data.file_size || product.data.fileSize || 'Unknown size';
      const description = product.data.description || '';
      const uploadedAt = product.data.uploaded_at || product.data.uploadedAt || '';
      const documentType = product.data.document_type || product.data.documentType || 'Document';
      
      // Format file size
      const formatFileSize = (bytes: any) => {
        if (!bytes) return 'Unknown';
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = parseFloat(bytes);
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
          size /= 1024;
          unitIndex++;
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`;
      };
      
      return (
        <div 
          key={product.uniqueKey || product.id}
          className="border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-blue-50"
        >
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h4 className="font-bold text-gray-800 truncate" title={fileName}>
                {fileName.length > 30 ? `${fileName.substring(0, 30)}...` : fileName}
              </h4>
            </div>
            
          </div>
          
          <div className="space-y-2 text-sm">
            {description && (
              <div className="flex flex-col">
                <span className="text-gray-600 mb-1">Description:</span>
                <span className="text-gray-800 text-xs bg-white p-3 rounded border border-blue-200">
                  {description.length > 100 
                    ? `${description.substring(0, 100)}...` 
                    : description}
                </span>
              </div>
            )}
            
            <div className="flex justify-between text-xs text-gray-500">
              <span>Size: {formatFileSize(fileSize)}</span>
              {uploadedAt && (
                <span>Uploaded: {formatDate(uploadedAt)}</span>
              )}
            </div>
            
            <div className="pt-2 border-t border-blue-100 flex justify-between items-center">
              <button 
                onClick={() => handleViewFileDetails(product, quote)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View Details →
              </button>
              
              <div className="flex gap-3">
                {product.data.url && (
                  <a 
                    href={product.data.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                    title="Download file"
                  >
                    Download
                  </a>
                )}
                
                <button
                  onClick={() => handleRemoveProduct(quote.id, product.id, product.type, fileName)}
                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                  title="Remove this file"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    // function to handle viewing note details
    const handleViewNoteDetails = (product: any, quote: QuoteSummary) => {
      console.log('📝 Opening note details:', { product, quote });
      
      // For now, just show in the existing modal
      setProductToView({ product, quote });
      setIsViewProductModalVisible(true);
    };

  // Format currency
  const formatCurrency = (value: any): string => {
    if (!value && value !== 0) return 'Not set';
    
    try {
      const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
      return isNaN(numValue) ? 'Not set' : `R ${numValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    } catch {
      return 'Not set';
    }
  };

  // Get product type display name
const getProductTypeDisplay = (productType: string): string => {
  const typeMap: { [key: string]: string } = {
    'vehicle': 'Vehicle',
    'property': 'Property',
    'risk_item': 'Risk Item',
    'household': 'Household Contents',
    'trailer': 'Trailer & Caravan',
    'value_added': 'Value Added',
    'water_craft': 'Water Craft',
    'note': 'Note',
    'file': 'Document',
    'document': 'Document',
    'claim': 'Previous Claim'
  };
  
  return typeMap[productType] || productType || 'Product';
};

  if (!can('quotes', 'read')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to access Quotes.</p>
      </div>
    );
  }


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-500">Loading quotes...</p>
          <p className="text-sm text-gray-400 mt-2">Client ID: {clientIdFromState}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col transition-all duration-300 ${
      isSidebarVisible ? "ml-64" : "ml-0"}`}>

      <Sidebar
        isVisible={isSidebarVisible}
        toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
      />

      <div className="flex-1 flex flex-col">
        <Header
          title=""
          user={user}
          client={clientFromHook}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />

        <div className="p-4 border-b bg-white">
          <div className="flex items-center space-x-4">
            <Link
              to={`/personal/${clientIdFromState}`}
              state={{ activeTab: "quotes" }}
              className="text-blue-600 hover:underline text-sm font-medium"
            >
              ← Back to Client
            </Link>
            
            {viewMode === 'detail' && (
              <button
                onClick={handleBackToMaster}
                className="text-blue-600 hover:underline text-sm font-medium"
              >
                ← Back to All Quotes
              </button>
            )}
            
            <div className="flex-1"></div>
            
            <div className="text-sm text-gray-500">
              {viewMode === 'master' ? `Showing ${allQuotes.length} quotes` : 'Quote Details'}
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-6">
          
          {viewMode === 'master' ? (
  /* MASTER PAGE VIEW - Products from Specific Quote */
  <div className="max-w-7xl mx-auto">
    {/* Page Header */}
    <div className="mb-8">
      <h1 className="text-3xl font-bold text-gray-800">Quotation Master Page</h1>
      <p className="text-gray-600 mt-2">
        {allQuotes.length === 1 
          ? `Products for quote: ${allQuotes[0].referenceNumber}`
          : `All products for ${clientFromHook?.name} ${clientFromHook?.surname}`
        }
        {clientFromHook?.client_number && ` (${clientFromHook.client_number})`}
      </p>
      
            {/* ACTION BUTTONS SECTION */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button 
          onClick={() => setShowEmailTemplateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Request Quotation
        </button>
        
        <button 
          onClick={async () => {
            if (allQuotes.length > 0) {
              const currentQuote = allQuotes[0];
              
              try {
                const token = localStorage.getItem('token');
                
                // Fetch the full quote details
                const response = await axios.get(
                  `${process.env.REACT_APP_API_URL}/api/quotes/${currentQuote.id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                // Start with the base quote data
                const fullQuoteData = response.data.data || {};
                
                // ✅ Fetch ALL products from individual endpoints and structure them properly
                const productEndpoints = [
                  { name: 'properties', url: `${process.env.REACT_APP_API_URL}/api/properties?quote_id=${currentQuote.id}`, key: 'properties' },
                  { name: 'householdContents', url: `${process.env.REACT_APP_API_URL}/api/household-contents?quote_id=${currentQuote.id}`, key: 'householdContents' },
                  { name: 'vehicles', url: `${process.env.REACT_APP_API_URL}/api/vehicles?quote_id=${currentQuote.id}`, key: 'vehicles' },
                  { name: 'trailersCaravans', url: `${process.env.REACT_APP_API_URL}/api/trailer-caravans?quote_id=${currentQuote.id}`, key: 'trailersCaravans' },
                  { name: 'valueAddedProducts', url: `${process.env.REACT_APP_API_URL}/api/value-added?quote_id=${currentQuote.id}`, key: 'valueAddedProducts' },
                  { name: 'waterCraft', url: `${process.env.REACT_APP_API_URL}/api/water-craft?quote_id=${currentQuote.id}`, key: 'waterCraft' },
                  { name: 'riskItems', url: `${process.env.REACT_APP_API_URL}/api/risk-items?quote_id=${currentQuote.id}`, key: 'riskItems' }
                ];
                
                // Fetch all products in parallel
                const productResults = await Promise.all(
                  productEndpoints.map(async (endpoint) => {
                    try {
                      const res = await axios.get(endpoint.url, { 
                        headers: { Authorization: `Bearer ${token}` },
                        timeout: 10000
                      });
                      // Handle wrapped response { success: true, data: [...] }
                      let data = res.data?.data || res.data;
                      if (!Array.isArray(data)) data = [];
                      console.log(`✅ Fetched ${data.length} ${endpoint.name} for quote ${currentQuote.id}`);
                      return { key: endpoint.key, data: data };
                    } catch (err: any) {
                      console.log(`⚠️ No ${endpoint.name} found or error:`, err.message);
                      return { key: endpoint.key, data: [] };
                    }
                  })
                );
                
                // Build the complete quote data object with all products
                const completeQuoteData = {
                  ...fullQuoteData,
                  // Add all products arrays
                  properties: productResults.find(r => r.key === 'properties')?.data || [],
                  householdContents: productResults.find(r => r.key === 'householdContents')?.data || [],
                  vehicles: productResults.find(r => r.key === 'vehicles')?.data || [],
                  trailersCaravans: productResults.find(r => r.key === 'trailersCaravans')?.data || [],
                  valueAddedProducts: productResults.find(r => r.key === 'valueAddedProducts')?.data || [],
                  waterCraft: productResults.find(r => r.key === 'waterCraft')?.data || [],
                  riskItems: productResults.find(r => r.key === 'riskItems')?.data || [],
                };
                
                console.log('📦 Complete quote data for editing:', {
                  properties: completeQuoteData.properties?.length,
                  householdContents: completeQuoteData.householdContents?.length,
                  vehicles: completeQuoteData.vehicles?.length,
                  trailersCaravans: completeQuoteData.trailersCaravans?.length,
                  valueAddedProducts: completeQuoteData.valueAddedProducts?.length,
                  waterCraft: completeQuoteData.waterCraft?.length,
                  riskItems: completeQuoteData.riskItems?.length,
                });
                
                navigate(`/create-quote/${clientIdFromState}`, { 
                  state: { 
                    editingQuoteId: currentQuote.id,
                    quoteData: completeQuoteData,
                    clientId: clientIdFromState,
                    isEditing: true
                  } 
                });
              } catch (error) {
                console.error('❌ Error fetching quote details:', error);
                navigate(`/create-quote/${clientIdFromState}`, { 
                  state: { 
                    editingQuoteId: currentQuote.id,
                    quoteData: currentQuote,
                    clientId: clientIdFromState,
                    isEditing: true
                  } 
                });
              }
            }
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </button>
        
        <button
          key={`accept-btn-${hasSavedInsurers}`}
          onClick={handleOpenAcceptQuotationModal}
          disabled={(() => {
            const activeAdmins = administrators.filter(a => a.quotation_status === 'active' || a.quotation_status === undefined);
            const activeUnderwriters = underwriters.filter(u => u.quotation_status === 'active' || u.quotation_status === undefined);
            const activeCount = activeAdmins.length + activeUnderwriters.length;
            
            // Condition 1: Must have exactly ONE active insurer
            if (activeCount !== 1) return true;
            
            const activeInsurer = [...activeAdmins, ...activeUnderwriters][0];
            const currentPremium = activeInsurer.premium;
            
            // Condition 2: Must have a premium value entered and it must be > 0
            if (!currentPremium || currentPremium === '' || parseFloat(currentPremium) <= 0) return true;
            
            // Condition 3: Must have clicked "Save Insurers" (hasSavedInsurers must be true)
            if (!hasSavedInsurers) return true;
            
            // ✅ FIX: Compare numeric values to avoid "100" vs "100.00" string mismatch
            if (parseFloat(savedPremiumValue || '0') !== parseFloat(currentPremium || '0')) return true;
            
            return false;
          })()}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap ${
            (() => {
              const activeAdmins = administrators.filter(a => a.quotation_status === 'active' || a.quotation_status === undefined);
              const activeUnderwriters = underwriters.filter(u => u.quotation_status === 'active' || u.quotation_status === undefined);
              const activeCount = activeAdmins.length + activeUnderwriters.length;
              
              if (activeCount !== 1) return false;
              
              const activeInsurer = [...activeAdmins, ...activeUnderwriters][0];
              const currentPremium = activeInsurer.premium;
              
              if (!currentPremium || currentPremium === '' || parseFloat(currentPremium) <= 0) return false;
              if (!hasSavedInsurers) return false;
              if (parseFloat(savedPremiumValue || '0') !== parseFloat(currentPremium || '0')) return false;
              
              return true;
            })()
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
              : 'bg-gray-400 cursor-not-allowed text-gray-200'
          }`}
          title={(() => {
            const activeAdmins = administrators.filter(a => a.quotation_status === 'active' || a.quotation_status === undefined);
            const activeUnderwriters = underwriters.filter(u => u.quotation_status === 'active' || u.quotation_status === undefined);
            const activeCount = activeAdmins.length + activeUnderwriters.length;
            
            if (activeCount !== 1) {
              return 'Please select exactly ONE active insurer (Administrator OR Underwriter)';
            }
            
            const activeInsurer = [...activeAdmins, ...activeUnderwriters][0];
            const currentPremium = activeInsurer.premium;
            
            if (!currentPremium || currentPremium === '' || parseFloat(currentPremium) <= 0) {
              return `Please enter a premium amount for ${activeInsurer.name}`;
            }
            
            if (!hasSavedInsurers || parseFloat(savedPremiumValue || '0') !== parseFloat(currentPremium || '0')) {
              return `Please click "Save Insurers" after entering the premium for ${activeInsurer.name}`;
            }
            
            return 'Accept Quotation';
          })()}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Accept Quotation
        </button>
        
        <button
          onClick={() => setIsDeclineModalVisible(true)}
          disabled={allQuotes.length === 0 || allQuotes[0]?.status === 'declined'}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap ${
            allQuotes.length === 0 || allQuotes[0]?.status === 'declined'
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
          title={allQuotes[0]?.status === 'declined' ? 'Quote is already declined' : 'Decline Quotation'}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Decline Quotation
        </button>

        {/* Documents Button */}
        <button 
          onClick={() => setIsDocumentsModalVisible(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documents
        </button>

        {/* Create Policy Button */}
          <button 
            onClick={() => {
              // Get the active insurer name from administrators or underwriters
              const activeAdmin = administrators.find(a => a.quotation_status === 'active' || a.quotation_status === undefined);
              const activeUnderwriter = underwriters.find(u => u.quotation_status === 'active' || u.quotation_status === undefined);
              const activeInsurer = activeAdmin || activeUnderwriter;
              
              // Pre-populate insurer name if available
              const insurerName = activeInsurer?.name || '';
              
              // Pre-populate premium amount if available
              const premiumAmount = activeInsurer?.premium || savedPremiumValue || '';
              
              // ✅ Get the current quote ID
              const currentQuoteId = allQuotes[0]?.id;
              
              // Navigate to Create Policy page with pre-filled data
              navigate(`/create-policy/${clientIdFromState}`, {
                state: {
                  prefillData: {
                    insurer: insurerName,
                    premium_amount: premiumAmount,
                    policy_number: allQuotes[0]?.referenceNumber || `POL-${allQuotes[0]?.id || Date.now()}`,
                    quoteId: currentQuoteId,  // ✅ Pass the quote ID
                    quoteReference: allQuotes[0]?.referenceNumber
                  }
                }
              });
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Create Policy
          </button>
        
        <button
          onClick={() => setIsQuoteEmailModalVisible(true)}
          disabled={allQuotes.length === 0}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap ${
            allQuotes.length === 0
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
          title="Send email with optional attachments"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Email
        </button>
        
        <button 
          onClick={() => setIsQuoteWhatsAppModalVisible(true)}
          disabled={allQuotes.length === 0}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap ${
            clientFromHook?.mobile_number 
              ? 'bg-green-500 hover:bg-green-600 text-white' 
              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
          }`}
          title={clientFromHook?.mobile_number ? 'Send quote via WhatsApp' : 'No mobile number available'}
        >
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893-.001-3.189-1.262-6.209-3.553-8.531"/>
          </svg>
          WhatsApp
        </button>

        <button 
          onClick={() => setIsNotesModalVisible(true)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap bg-indigo-500 hover:bg-indigo-600 text-white"
          title="View activity log"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Notes
        </button>
      </div>

      {/* CARDS SECTION - Two cards side by side */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QUOTE INFORMATION CARD */}
<div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
    Quote Information
  </h2>
  
  <div className="space-y-6">
    {/* Quote Reference Number Section */}
    {allQuotes.length > 0 && (
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Quote Reference</h3>
        <div className="space-y-2">
          <div>
            <p className="text-sm text-gray-600">Quote Reference Number</p>
            <p className="font-medium text-gray-900 text-lg bg-blue-50 p-3 rounded-lg border border-blue-100">
              {allQuotes[0]?.referenceNumber || allQuotes[0]?.reference || 'N/A'}
            </p>
          </div>
        </div>
      </div>
    )}

          {/* ✅ NEW: Assigned Consultant UI */}
      {allQuotes.length > 0 && can('quotes', 'write') && (
        <div className="space-y-3 pb-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Assigned Consultant
          </h3>
          <div className="flex items-center space-x-3">
            <select
              data-testid="assigned-consultant-select"
              value={selectedConsultantId}
              onChange={(e) => setSelectedConsultantId(e.target.value)}
              className="shadow-sm border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
              disabled={isLoadingConsultantsList || isSavingConsultant}
            >
              <option value="">Unassigned</option>
              {consultantsList.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={handleAssignConsultant}
              disabled={isSavingConsultant || !selectedConsultantId || selectedConsultantId === (allQuotes[0]?.consultant_id?.toString() || '')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
            >
              {isSavingConsultant ? 'Assigning...' : 'Assign'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Change who is responsible for managing and sending this quote.
          </p>
        </div>
      )}
    {/* Client Information */}
    <div className="space-y-3">
      <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Client Details</h3>
      <div className="space-y-2">
        <div>
          <p className="text-sm text-gray-600">Client Name</p>
          <p className="font-medium text-gray-900">
            {clientFromHook ? 
              `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim() 
              : 'Loading...'
            }
          </p>
        </div>
        
        {clientFromHook?.client_number && (
          <div>
            <p className="text-sm text-gray-600">Client Number</p>
            <p className="font-medium text-gray-900">{clientFromHook.client_number}</p>
          </div>
        )}

        {clientFromHook?.id_passport_number && (
          <div>
            <p className="text-sm text-gray-600">Identification Number</p>
            <p className="font-medium text-gray-900">{clientFromHook.id_passport_number}</p>
          </div>
        )}
      </div>
    </div>

    {/* Client Address Section - UPDATED TO SHOW FROM CLIENT ADDRESSES ARRAY */}
    {(() => {
      // Check if client has addresses with proper null/undefined checks
      const hasAddresses = clientFromHook && 
                         clientFromHook.addresses && 
                         Array.isArray(clientFromHook.addresses) && 
                         clientFromHook.addresses.length > 0;
      
      if (!hasAddresses) {
        return (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Client Address</h3>
            <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-gray-600 font-medium">No Address Available</p>
              <p className="text-gray-500 text-sm mt-1">Add address in client profile</p>
            </div>
          </div>
        );
      }

      // Find primary address or use the first one
      const primaryAddress = clientFromHook!.addresses!.find(addr => addr.is_primary) || clientFromHook!.addresses![0];
      
      // Helper function to get address field with fallbacks
      const getAddressField = (primaryAddress: any, fieldNames: string[]): string => {
        for (const fieldName of fieldNames) {
          if (primaryAddress[fieldName]) {
            return primaryAddress[fieldName];
          }
        }
        return '';
      };
      
      // Get address fields with multiple possible property names
      const streetAddress = getAddressField(primaryAddress, [
        'address_line_1', 'addressLine1', 'street_address', 'street', 'line1', 'address'
      ]);
      
      const city = getAddressField(primaryAddress, [
        'city', 'city_town', 'town', 'suburb'
      ]);
      
      const region = getAddressField(primaryAddress, [
        'region', 'state', 'province', 'county'
      ]);
      
      const postalCode = getAddressField(primaryAddress, [
        'postal_code', 'postalCode', 'zip_code', 'zipCode', 'zip'
      ]);
      
      const country = getAddressField(primaryAddress, [
        'country', 'country_name', 'nation'
      ]);
      
      // Check if we have any address data at all
      const hasAnyAddressData = streetAddress || city || region || postalCode || country;
      
      if (!hasAnyAddressData) {
        return (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Client Address</h3>
            <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-gray-600 font-medium">No Address Available</p>
              <p className="text-gray-500 text-sm mt-1">Add address in client profile</p>
            </div>
          </div>
        );
      }
      
      return (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Client Address</h3>
          <div className="space-y-2">
            {streetAddress && (
              <div>
                <p className="text-sm text-gray-600">Street Address</p>
                <p className="font-medium text-gray-900">{streetAddress}</p>
              </div>
            )}
            
            {city && (
              <div>
                <p className="text-sm text-gray-600">City</p>
                <p className="font-medium text-gray-900">{city}</p>
              </div>
            )}
            
            {region && (
              <div>
                <p className="text-sm text-gray-600">Region/State/Province</p>
                <p className="font-medium text-gray-900">{region}</p>
              </div>
            )}
            
            {postalCode && (
              <div>
                <p className="text-sm text-gray-600">Postal Code</p>
                <p className="font-medium text-gray-900">{postalCode}</p>
              </div>
            )}
            
            {country && (
              <div>
                <p className="text-sm text-gray-600">Country</p>
                <p className="font-medium text-gray-900">{country}</p>
              </div>
            )}
            
            {/* Optional: Show address type badge */}
            {primaryAddress.address_type && (
              <div className="mt-2">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  primaryAddress.is_primary 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {primaryAddress.is_primary ? 'Primary Address' : primaryAddress.address_type}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    })()}
  </div>
</div>

        {/* INSURERS CARD WITH REQUEST QUOTATION */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Insurers
          </h2>

          {/* Save Insurer Button */}
          <button
            onClick={saveInsurersData}
            disabled={saveStatus === 'loading'}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${
              saveStatus === 'loading' 
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
            title="Click to save insurer changes before accepting quotation"
          >
            {saveStatus === 'loading' ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Insurers
              </>
            )}
          </button>
        </div>
          
          <div className="space-y-6">
            {/* Multiple Administrators Section */}
            {administrators.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Administrators</h3>
                {administrators.map((admin, index) => {
                  // Determine if this administrator is active
                  const isActive = admin.quotation_status === 'active' || admin.quotation_status === undefined;
                  
                  return (
                    <div key={`admin-${admin.id}`} className={`rounded-lg p-5 border ${isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Administrator Info */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 text-lg">
                                {admin.name}
                              </p>
                              {isActive && (
                                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Active
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Request Quotation and Premium for this Administrator */}
                        <div className="space-y-6">
                          {/* Request Quotation */}
                          <div className="flex flex-col space-y-3">
                            <label className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                              Request Quotation
                            </label>
                            <select 
                              value={admin.quotation_status || 'active'}
                              onChange={(e) => handleAdminQuotationStatusChange(admin.id, e.target.value)}
                              className={`border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-xs bg-white ${isActive ? 'border-green-300' : 'border-gray-300'}`}
                            >
                              <option value="active">Active</option>
                              <option value="not-active">Not Active</option>
                            </select>
                          </div>
                          
                          {/* Premium Field */}
                          <div className="flex flex-col space-y-3">
                            <label className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                              Premium
                            </label>
                            <div className="relative w-full max-w-xs">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <span className="text-gray-500 font-medium">R</span>
                              </div>
                              <input
                                type="number"
                                value={admin.premium || ''}
                                onChange={(e) => handleAdminPremiumChange(admin.id, e.target.value)}
                                placeholder="Enter premium"
                                className={`border rounded-lg pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full bg-white ${isActive ? 'border-green-300' : 'border-gray-300'}`}
                                step="0.01"
                                min="0"
                                style={{
                                  MozAppearance: 'textfield',
                                  appearance: 'textfield',
                                }}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Multiple Underwriters Section */}
            {underwriters.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Underwriters</h3>
                
                {underwriters.map((underwriter, index) => {
                  const isActive = underwriter.quotation_status === 'active' || underwriter.quotation_status === undefined;
                  
                  return (
                    <div key={`underwriter-${underwriter.id}-${index}`} className={`rounded-lg p-5 border ${isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Underwriter Info */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 text-lg">
                                {underwriter.name}
                              </p>
                              {isActive && (
                                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Active
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Request Quotation and Premium for this Underwriter */}
                        <div className="space-y-6">
                          {/* Request Quotation */}
                          <div className="flex flex-col space-y-3">
                            <label className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                              Request Quotation
                            </label>
                            <select 
                              value={underwriter.quotation_status || 'active'}
                              onChange={(e) => handleUnderwriterQuotationStatusChange(underwriter.id, e.target.value)}
                              className={`border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-xs bg-white ${isActive ? 'border-green-300' : 'border-gray-300'}`}
                            >
                              <option value="active">Active</option>
                              <option value="pending">Pending</option>
                              <option value="not-active">Not Active</option>
                              <option value="accepted">Accepted</option>
                              <option value="declined">Declined</option>
                            </select>
                          </div>
                          
                          {/* Premium Field */}
                          <div className="flex flex-col space-y-3">
                            <label className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                              Premium
                            </label>
                            <div className="relative w-full max-w-xs">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <span className="text-gray-500 font-medium">R</span>
                              </div>
                              <input
                                type="number"
                                value={underwriter.premium || ''}
                                onChange={(e) => handleUnderwriterPremiumChange(underwriter.id, e.target.value)}
                                placeholder="Enter premium"
                                className={`border rounded-lg pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full bg-white ${isActive ? 'border-green-300' : 'border-gray-300'}`}
                                step="0.01"
                                min="0"
                                style={{
                                  MozAppearance: 'textfield',
                                  appearance: 'textfield',
                                }}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty State - when no administrators or underwriters */}
            {administrators.length === 0 && underwriters.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <div className="text-gray-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-gray-500">No insurers assigned to this quote</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Add Product Button */}
      <div className="pt-4 border-t border-gray-200">
        <button 
          onClick={() => setIsAddProductModalVisible(true)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Product
        </button>
      </div>

      <div className="mt-4 flex items-center space-x-4">
        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
          {getTotalProductsCount()} Total Products
        </span>
      </div>
    </div>

    {saveStatus === 'success' && (
      <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
        {saveMessage}
      </div>
    )}
    {saveStatus === 'error' && (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
        {saveMessage}
      </div>
    )}

    {/* Products Grid - Show products from the specific quote(s) */}
    {getTotalProductsCount() > 0 ? (
      <div className="space-y-8">
        {allQuotes.map((quote: QuoteSummary) => (
          <div key={quote.id} className="bg-white rounded-xl shadow-lg border border-gray-200">
            {/* Quote Header */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-blue-600">
                    {quote.referenceNumber || quote.reference || `Quote-${quote.id}`}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Created: {formatDate(quote.createdDate || quote.created_at)} • 
                    Status: <span className={`ml-1 text-xs px-2 py-1 rounded-full ${getStatusBadgeColor(quote.status)}`}>
                      {quote.status}
                    </span>
                  </p>
                </div>
                
              </div>
            </div>

            {/* Products Section */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Products in this Quote ({getProductsForQuote(quote.id).length})
              </h3>
              
              {getProductsForQuote(quote.id).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getProductsForQuote(quote.id)
                    // ✅ Filter out signed documents before rendering
                    .filter((product: any) => {
                      if (product.type === 'file' && product.name?.includes('signed_proposal')) {
                        return false;
                      }
                      if (product.type === 'document' && product.documentType === 'signed_proposal') {
                        return false;
                      }
                      if (product.data?.document_type === 'signed_proposal') {
                        return false;
                      }
                      return true;
                    })
                    .map((product: any, productIndex: number) => {
                      console.log(`🎨 Rendering product ${productIndex + 1}:`, {
                        id: product.id,
                        type: product.type,
                        name: product.name,
                        uniqueKey: product.uniqueKey
                      });

                    if (product.type === 'note') {
                      return renderNoteCard(product, quote);
                    }

                    // Render file cards
                    if (product.type === 'file') {
                      return renderFileCard(product, quote);
                    }

                    // Render document cards (from upload-files endpoint)
                    if (product.type === 'document') {
                      return renderDocumentCard(product, quote);
                    }

                    if (product.type === 'previous_insurance') {
                      return renderPreviousInsuranceCard(product, quote);
                    }

                    // ✅ Claim card branch (was missing — claims fell through to default)
                    if (product.type === 'claim') {
                      return renderClaimCard(product, quote);
                    }

                    // risk item
                    if (product.type === 'risk_item') {
                      return (
                        <div 
                          key={product.uniqueKey || product.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-white"
                        >
                          <EditableRiskItemCard
                            riskItem={{
                              ...product.data,
                              // Ensure all fields are available
                              itemType: product.data.itemType || product.data.item_type || product.data.category,
                              itemName: product.data.itemName || product.data.item_name,
                              serialNumber: product.data.serialNumber || product.data.serial_number,
                              coverType: product.data.coverType || product.data.cover_type,
                              noClaimsBonus: product.data.noClaimsBonus || product.data.no_claims_bonus,
                              insuredValue: product.data.insuredValue || product.data.insured_value,
                              premium: product.data.premium,
                              excess: product.data.excess,
                              description: product.data.description,
                              id: product.id,
                              isEditing: false
                            }}
                            index={productIndex}
                            onSave={(idx, updatedData) => {
                              // Update risk item in state
                              const quoteId = quote.id;
                              setAllQuoteProducts(prev => {
                                const currentProducts = prev[quoteId] || [];
                                const updatedProducts = currentProducts.map((p, i) => {
                                  if (i === idx && p.type === 'risk_item') {
                                    return {
                                      ...p,
                                      name: updatedData.itemName || p.name,
                                      value: updatedData.insuredValue || p.value,
                                      description: updatedData.description || p.description,
                                      data: {
                                        ...p.data,
                                        ...updatedData,
                                        // Ensure backward compatibility
                                        item_type: updatedData.itemType,
                                        item_name: updatedData.itemName,
                                        serial_number: updatedData.serialNumber,
                                        cover_type: updatedData.coverType,
                                        no_claims_bonus: updatedData.noClaimsBonus,
                                        insured_value: updatedData.insuredValue
                                      }
                                    };
                                  }
                                  return p;
                                });
                                return {
                                  ...prev,
                                  [quoteId]: updatedProducts
                                };
                              });
                            }}
                            onCancel={(idx) => {
                              // Cancel editing
                              const quoteId = quote.id;
                              setAllQuoteProducts(prev => {
                                const currentProducts = prev[quoteId] || [];
                                const updatedProducts = currentProducts.map((p, i) => {
                                  if (i === idx && p.type === 'risk_item') {
                                    return {
                                      ...p,
                                      data: {
                                        ...p.data,
                                        isEditing: false
                                      }
                                    };
                                  }
                                  return p;
                                });
                                return {
                                  ...prev,
                                  [quoteId]: updatedProducts
                                };
                              });
                            }}
                            onEdit={(idx) => {
                              // Start editing
                              const quoteId = quote.id;
                              setAllQuoteProducts(prev => {
                                const currentProducts = prev[quoteId] || [];
                                const updatedProducts = currentProducts.map((p, i) => {
                                  if (i === idx && p.type === 'risk_item') {
                                    return {
                                      ...p,
                                      data: {
                                        ...p.data,
                                        isEditing: true
                                      }
                                    };
                                  }
                                  // Set all other risk items to not editing
                                  if (p.type === 'risk_item') {
                                    return {
                                      ...p,
                                      data: {
                                        ...p.data,
                                        isEditing: false
                                      }
                                    };
                                  }
                                  return p;
                                });
                                return {
                                  ...prev,
                                  [quoteId]: updatedProducts
                                };
                              });
                            }}
                            showCardNumber={false}
                            cardTitle="Risk Item"
                          />
                          
                          <div className="pt-2 border-t border-gray-100 flex justify-between items-center mt-4">
                            <button 
                              onClick={() => handleViewProductDetails(product, quote)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              View / Edit Product Details →
                            </button>
                            
                            <button
                              onClick={() => handleRemoveProduct(quote.id, product.id, product.type, product.name)}
                              className="text-red-500 hover:text-red-700 text-sm font-medium"
                              title="Remove this risk item"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    }

                    if (product.type === 'water_craft') {
  return ( 
    <div 
      key={product.uniqueKey || product.id}
      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-white"
    >
      <div className="flex justify-between items-start mb-3">
        <h4 className="font-bold text-gray-800">
          Water Craft
        </h4>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Value:</span>
          <span className="font-medium text-green-600">
            {formatCurrency(product.value)}
          </span>
        </div>
        
        {product.description && product.description !== 'N/A' && (
          <div className="flex flex-col">
            <span className="text-gray-600 mb-1">Vessel Name:</span>
            <span className="text-gray-800 text-xs bg-gray-50 p-2 rounded border">
              {product.description}
            </span>
          </div>
        )}
        
        <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
          <button 
            onClick={() => handleViewProductDetails(product, quote)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View / Edit Product Details →
          </button>
          
          <button
            onClick={() => handleRemoveProduct(quote.id, product.id, product.type, product.name)}
            className="text-red-500 hover:text-red-700 text-sm font-medium"
            title="Remove this product"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

                    // Vehicle specific card with enhanced details
                    if (product.type === 'vehicle') {
                      const vehicleData = product.data;
                      const year = vehicleData.year || vehicleData.year;
                      const make = vehicleData.make || '';
                      const model = vehicleData.model || '';
                      const registrationNumber = vehicleData.registration_number || vehicleData.registrationNumber || '';
                      const color = vehicleData.color || '';
                      
                      const vehicleTitle = `${make} ${model}`.trim() || 'Vehicle';
                      const yearDisplay = year ? `(${year})` : '';
                      const fullTitle = `${vehicleTitle} ${yearDisplay}`.trim();
                      
                      return ( 
                        <div 
                          key={product.uniqueKey || product.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-white"
                        >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-bold text-gray-800">Vehicle</h4>
                            {year && <p className="text-xs text-gray-500 mt-1">Year: {year}</p>}
                          </div>
                        </div>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Value:</span>
                              <span className="font-medium text-green-600">{formatCurrency(product.value)}</span>
                            </div>
                            
                            {make && model && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Make/Model:</span>
                                <span className="font-medium">{`${make} ${model}`}</span>
                              </div>
                            )}
                            
                            {registrationNumber && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Registration:</span>
                                <span className="font-medium">{registrationNumber}</span>
                              </div>
                            )}
                            
                            {color && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Color:</span>
                                <span className="font-medium">{color}</span>
                              </div>
                            )}
                            
                            <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                              <button onClick={() => handleViewProductDetails(product, quote)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">View / Edit Product Details →</button>
                              <button onClick={() => handleRemoveProduct(quote.id, product.id, product.type, product.name)} className="text-red-500 hover:text-red-700 text-sm font-medium" title="Remove this product">Remove</button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                   return ( 
                    <div 
                      key={product.uniqueKey || product.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-white"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-bold text-gray-800">
                          {getProductTypeDisplay(product.type)}
                        </h4>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Value:</span>
                          <span className="font-medium text-green-600">
                            {formatCurrency(product.value)}
                          </span>
                        </div>
                        
                        {product.description && product.description !== 'N/A' && (
                          <div className="flex flex-col">
                            <span className="text-gray-600 mb-1">Address:</span>
                            <span className="text-gray-800 text-xs bg-gray-50 p-2 rounded border">
                              {product.description}
                            </span>
                          </div>
                        )}
                        
                        <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                          <button 
                            onClick={() => handleViewProductDetails(product, quote)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View / Edit Product Details →
                          </button>
                          
                          <button
                            onClick={() => handleRemoveProduct(quote.id, product.id, product.type, product.name)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium"
                            title="Remove this product"
                          >
                            Remove
                          </button>
                        </div>

                      </div>
                    </div>
                   );
})}
                </div>
              ) : (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <p className="text-gray-500">No products found in this quote.</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    ) : (
      /* Empty State */
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
        <p className="text-gray-500 mb-6">
          {allQuotes.length === 1 
            ? `The quote "${allQuotes[0]?.referenceNumber}" doesn't have any products yet.`
            : 'No quotes found with products for this client.'
          }
        </p>
        <Link
          to={`/personal/${clientIdFromState}`}
          state={{ activeTab: "quotes" }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back to Quotes
        </Link>
      </div>
    )}
  </div>
) : (
            selectedQuote && (
              <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-gray-800">Quote Details</h1>
                  <p className="text-gray-600 mt-2">
                    Reference: {selectedQuote.referenceNumber || selectedQuote.reference}
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-xl font-semibold mb-4">
                    {selectedQuote.referenceNumber || selectedQuote.reference}
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-2">Quote Information</h3>
                      <div className="space-y-2">
                        <p><span className="text-gray-600">Status:</span> 
                          <span className={`ml-2 text-xs px-2 py-1 rounded-full ${getStatusBadgeColor(selectedQuote.status)}`}>
                            {selectedQuote.status}
                          </span>
                        </p>
                        <p><span className="text-gray-600">Created:</span> {formatDate(selectedQuote.created_at)}</p>
                        <p><span className="text-gray-600">Reference:</span> {selectedQuote.referenceNumber || selectedQuote.reference}</p>
                        <p><span className="text-gray-600">Products:</span> {allProducts.length}</p>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-2">Consultant</h3>
                      <p className="text-gray-600">
                        {consultant ? `${consultant.first_name} ${consultant.last_name}` : 'Loading...'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </main>
      </div>

          {/* Select Product Type Modal */}
          {isAddProductModalVisible && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
    <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
      <h3 className="text-xl font-bold mb-4">Select Product Type</h3>
      
      <div className="space-y-3">
        
        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'previous_insurance', 'Previous Insurance');
    
    navigate(`/add-previous-insurance/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Previous Insurance
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'claim', 'Previous Claim');
    
    navigate(`/add-previous-claim/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          Previous Claim
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'note', 'Note');
    
    navigate(`/add-note/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Note
        </button>

        {/* Horizontal separator */}
        <div className="border-t border-gray-200 my-4"></div>

        {/* Existing product buttons */}
        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'property', 'Property');
    
    navigate(`/add-property/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Property
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'household', 'Household Contents');
    
    navigate(`/add-household-contents/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          Household Contents
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'vehicle', 'Vehicle');
    
    navigate(`/add-vehicle/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Vehicle
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'trailer', 'Trailer & Caravan');
    
    navigate(`/add-trailer-caravan/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Trailer & Caravan
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'value_added', 'Value Added');
    
    navigate(`/add-value-added/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Value Added
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'water_craft', 'Water Craft');
    
    navigate(`/add-water-craft/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
          </svg>
          Water Craft
        </button>

        <button
  onClick={() => {
    setIsAddProductModalVisible(false);
    const currentQuoteId = allQuotes[0]?.id;
    const clientIdValue = clientIdFromState || allQuotes[0]?.client_id;
    const returnUrl = `/quotes/master/${clientIdValue}/${currentQuoteId}`;
    
    // Log product addition
    logProductAddition(currentQuoteId, 'risk_item', 'Risk Item');
    
    navigate(`/add-risk-item/${currentQuoteId}?clientId=${clientIdValue}&returnTo=${encodeURIComponent(returnUrl)}`);
  }}
  className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-4 rounded-lg text-left flex items-center gap-3 transition-colors duration-200"
>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          Risk Items
        </button>
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={() => setIsAddProductModalVisible(false)}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* Removal Reason Modal */}
{isRemoveModalVisible && productToRemove && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
    <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
      <h3 className="text-xl font-bold mb-4 text-red-600">Remove Product</h3>
      
      <div className="mb-4">
        <p className="text-gray-700 mb-2">
          You are about to remove: <strong>{productToRemove.productName}</strong>
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Type: {getProductTypeDisplay(productToRemove.productType)}
        </p>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label htmlFor="removalReason" className="block text-gray-700 font-bold mb-2 text-sm">
            Reason for Removal *
          </label>
          <textarea
            id="removalReason"
            value={removalReason}
            onChange={(e) => setRemovalReason(e.target.value)}
            className="shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-red-300 text-sm"
            rows={4}
            placeholder="Please provide the reason for removing this product..."
            required
          />
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={() => {
            setIsRemoveModalVisible(false);
            setProductToRemove(null);
            setRemovalReason('');
          }}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded text-sm"
        >
          Cancel
        </button>
        <button
          onClick={confirmRemoveProduct}
          className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm disabled:bg-red-300"
          disabled={!removalReason.trim()}
        >
          Confirm Removal
        </button>
      </div>
    </div>
  </div>
)}

{/* Product Details View Modal */}
{isViewProductModalVisible && productToView && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
    <div className="relative bg-white p-8 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800">
          {getProductTypeDisplay(productToView.product.type)} Details
        </h3>
        <button
          onClick={() => {
            setIsViewProductModalVisible(false);
            setProductToView(null);
          }}
          className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
        >
          ×
        </button>
      </div>

      {/* Render the appropriate modal based on product type */}
      {productToView.product.type === 'vehicle' && (
  <AddVehicleModal
    isVisible={true}
    onClose={() => {
      setIsViewProductModalVisible(false);
      setProductToView(null);
    }}
    onSave={async (updatedVehicle: any) => {
      try {
        const quoteId = productToView.quote.id;
        const productId = productToView.product.id;

        if (!quoteId || !productId) {
          throw new Error("Missing quote or product ID");
        }

        // Prepare data for backend (snake_case names expected by most APIs)
        const backendData = {
          client_id: parseInt(clientIdFromState || "0"),
          quote_id: quoteId,

          vehicle_type: updatedVehicle.vehicle_type || updatedVehicle.vehicleType || "",
          registration_number: updatedVehicle.registration_number || "",
          make: updatedVehicle.make || "",
          model: updatedVehicle.model || "",
          year: updatedVehicle.year ? parseInt(updatedVehicle.year) : null,
          variant: updatedVehicle.variant || "",
          color: updatedVehicle.color || "",
          usage: updatedVehicle.usage || updatedVehicle.vehicle_usage || "",
          vin_number: updatedVehicle.vin_number || updatedVehicle.vinNumber || "",
          engine_number: updatedVehicle.engine_number || "",
          mmcode: updatedVehicle.mmcode || "",
          insured_value: Number(updatedVehicle.insured_value || updatedVehicle.insuredValue || 0),
          cover_type: updatedVehicle.cover_type || updatedVehicle.coverType || "",
          day_parking_location: updatedVehicle.day_parking_location || "",
          night_parking_location: updatedVehicle.night_parking_location || "",
          registered_owner: updatedVehicle.registered_owner || "",
          regular_driver: updatedVehicle.regular_driver || "",
          other_drivers: updatedVehicle.other_drivers || "",
          safety_features: updatedVehicle.safety_features || "",
          excess_buy_back: !!updatedVehicle.excess_buy_back,
          credit_shortfall: !!updatedVehicle.credit_shortfall,
          car_hire: !!updatedVehicle.car_hire,

          premium: Number(updatedVehicle.premium || 0),
          excess: Number(updatedVehicle.excess || 0),
          no_claim_bonus: updatedVehicle.no_claim_bonus || "",
        };

        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token");

        // Update in database
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/vehicles/${productId}`,
          backendData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Vehicle updated in DB:", response.data);

        // Update local state (so card refreshes immediately)
        setAllQuoteProducts((prev) => {
          const products = prev[quoteId] || [];
          const updatedProducts = products.map((p) =>
            p.id === productId && p.type === "vehicle"
              ? {
                  ...p,
                  name: `${updatedVehicle.make || ""} ${updatedVehicle.model || ""}`.trim() || "Vehicle",
                  value: Number(updatedVehicle.insured_value || 0),
                  description:
                    updatedVehicle.registration_number ||
                    `${updatedVehicle.make || ""} ${updatedVehicle.model || ""}`.trim() ||
                    "Vehicle",
                  data: {
                    ...p.data,
                    ...updatedVehicle,
                    id: productId,
                    quote_id: quoteId,
                    quoteId: quoteId,
                  },
                  quoteId: quoteId,
                }
              : p
          );

          return { ...prev, [quoteId]: updatedProducts };
        });

// ✅ LOG ACTIVITY: Vehicle Updated
if (quoteId && updatedVehicle) {
  try {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
let performedBy = 'System';

if (userData) {
  try {
    const user = JSON.parse(userData);
    const firstName = user.first_name || user.name || '';
    const lastName = user.last_name || user.surname || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    if (fullName) {
      performedBy = fullName;
    } else if (user.username) {
      performedBy = user.username;
    } else if (user.email) {
      performedBy = user.email;
    }
  } catch (parseError) {
    console.error('Error parsing user data:', parseError);
  }
}
    
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/quote-activity-logs`,
      {
        quote_id: parseInt(quoteId),
        action_type: 'product_updated',
        description: `Vehicle updated: ${updatedVehicle.make || ''} ${updatedVehicle.model || ''}`.trim() || 'Vehicle',
        metadata: {
          productType: 'vehicle',
          productId: productId,
          updatedFields: Object.keys(updatedVehicle).filter(key => 
            key !== 'id' && key !== 'isEditing'
          )
        }
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    console.log('✅ Activity logged: vehicle_updated');
  } catch (logError) {
    console.error('❌ Failed to log vehicle update:', logError);
  }
}

        setSaveStatus("success");
        setSaveMessage("Vehicle updated successfully!");

      } catch (err: any) {
        console.error("Vehicle update failed:", err);
        setSaveStatus("error");
        setSaveMessage(
          "Failed to update vehicle – " +
            (err.response?.data?.message || err.message || "Unknown error")
        );
      } finally {
        setIsViewProductModalVisible(false);
        setProductToView(null);

        setTimeout(() => {
          setSaveStatus("idle");
          setSaveMessage("");
        }, 4000);
      }
    }}
    clientId={parseInt(clientIdFromState || "0")}
    vehicle={productToView.product.data}
    mode="edit"
    openPersonModal={handleOpenPersonModal}
    personsOfInterest={personsOfInterest.filter(person => person.status !== 'inactive')}
  />
)}
      
      {productToView.product.type === 'household' && (
  <AddHouseholdContentsModal
    isVisible={true}
    onClose={() => {
      setIsViewProductModalVisible(false);
      setProductToView(null);
    }}
    onSave={async (updatedData: any) => {
  console.log('💾 Saving updated household contents:', updatedData);
      
      const quoteId = productToView.quote.id;
      const productId = updatedData.id || productToView.product.id;
      
      if (quoteId) {
        // Update the product in state
        setAllQuoteProducts(prev => {
          const currentProducts = prev[quoteId] || [];
          const updatedProducts = currentProducts.map(p => {
            if (p.id === productId && p.type === 'household') {
              console.log('✅ Updating household product in state:', p.id);
              
              const updatedProduct = {
                ...p,
                name: updatedData.propertyType || 'Household Contents',
                value: updatedData.insuredValue || p.value,
                description: updatedData.addressLine1 || 'Household Contents',
                data: { 
                  ...p.data, 
                  ...updatedData,
                  // Ensure critical fields are preserved
                  id: productId,
                  quote_id: quoteId,
                  quoteId: quoteId
                },
                quoteId: quoteId
              };
              
              console.log('🔄 Household product after update:', updatedProduct);
              return updatedProduct;
            }
            return p;
          });
          
          return {
            ...prev,
            [quoteId]: updatedProducts
          };
        });
        
        // Update in backend if needed
        if (updatedData.id && !updatedData.id.toString().startsWith('temp-')) {
          const token = localStorage.getItem('token');
          fetch(`${process.env.REACT_APP_API_URL}/api/household-contents/${updatedData.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              ...updatedData,
              quote_id: quoteId,
              client_id: clientIdFromState
            }),
          }).catch(error => {
            console.error('❌ Error updating household contents in backend:', error);
          });
        }

        // ✅ LOG ACTIVITY: Household Contents Updated
        if (quoteId && updatedData) {
          try {
            await logProductUpdate(quoteId, 'household', updatedData.propertyType || 'Household Contents', {
              productId: productId,
              updatedFields: Object.keys(updatedData).filter(key => 
                key !== 'id' && key !== 'isEditing' && key !== 'isTemp'
              )
            });
          } catch (logError) {
            console.error('❌ Failed to log household update:', logError);
          }
        }
        
        setSaveStatus('success');
        setSaveMessage('Household contents updated successfully!');
      }
      
      setIsViewProductModalVisible(false);
      setProductToView(null);
    }}
    clientId={parseInt(clientIdFromState || '0')}
    contents={productToView.product.data}
    mode="edit"
    quoteId={productToView.quote.id}
  />
)}
      
      {productToView.product.type === 'trailer' && (
  <TrailerCaravanModal
    isVisible={true}
    onClose={() => {
      setIsViewProductModalVisible(false);
      setProductToView(null);
    }}
    onSave={async (updatedData: any) => {
      const currentQuoteId = productToView.quote.id;
      const productId = updatedData.id || productToView.product.id;
      
      try {
        setSaveStatus('loading');
        setSaveMessage('Updating trailer/caravan...');
        
        console.log('🚛 Updating trailer/caravan:', updatedData);
        
        // The modal already saves to database, so we just need to update local state
        const updatedProduct = {
          ...productToView.product,
          name: `${updatedData.vehicleType || updatedData.vehicle_type || 'Trailer'} - ${updatedData.make} ${updatedData.model}`.trim() || 'Trailer/Caravan',
          value: Number(updatedData.insuredValue || updatedData.insured_value) || 0,
          description: updatedData.registrationNumber || updatedData.registration_number || 
                      `${updatedData.make || ''} ${updatedData.model || ''}`.trim() || 
                      'Trailer/Caravan',
          data: {
            ...productToView.product.data,
            ...updatedData,
            vehicle_type: updatedData.vehicleType || updatedData.vehicle_type,
            registration_number: updatedData.registrationNumber || updatedData.registration_number,
            cover_type: updatedData.coverType || updatedData.coverType,
            insured_value: Number(updatedData.insuredValue || updatedData.insured_value) || 0,
            use_retail_value: updatedData.useRetailValue || updatedData.use_retail_value || false,
            no_claims_bonus: updatedData.noClaimsBonus || updatedData.no_claims_bonus,
            premium: Number(updatedData.premium) || 0,
            excess: Number(updatedData.excess) || 0,
            quote_id: currentQuoteId,
            quoteId: currentQuoteId
          }
        };
        
        // Update local state
        setAllQuoteProducts(prev => {
          const currentProducts = prev[currentQuoteId] || [];
          const updatedProducts = currentProducts.map(p => 
            p.id === productId ? updatedProduct : p
          );
          return {
            ...prev,
            [currentQuoteId]: updatedProducts
          };
        });
        
        // ✅ LOG ACTIVITY: Trailer/Caravan Updated
        try {
          const token = localStorage.getItem('token');
          const userData = localStorage.getItem('user');
let performedBy = 'System';

if (userData) {
  try {
    const user = JSON.parse(userData);
    const firstName = user.first_name || user.name || '';
    const lastName = user.last_name || user.surname || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    if (fullName) {
      performedBy = fullName;
    } else if (user.username) {
      performedBy = user.username;
    } else if (user.email) {
      performedBy = user.email;
    }
  } catch (parseError) {
    console.error('Error parsing user data:', parseError);
  }
}
          
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/quote-activity-logs`,
            {
              quote_id: parseInt(currentQuoteId),
              action_type: 'product_updated',
              description: `Trailer/Caravan updated: ${updatedData.make || ''} ${updatedData.model || ''}`.trim() || 'Trailer/Caravan',
              metadata: {
                productType: 'trailer',
                productId: productId,
                updatedFields: Object.keys(updatedData).filter(key => 
                  key !== 'id' && key !== 'isEditing'
                )
              }
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('✅ Activity logged: trailer_updated');
        } catch (logError) {
          console.error('❌ Failed to log trailer update:', logError);
        }
        
        setIsViewProductModalVisible(false);
        setProductToView(null);
        setSaveStatus('success');
        setSaveMessage('Trailer/Caravan updated successfully!');
        
      } catch (error) {
        console.error('❌ Error updating trailer/caravan:', error);
        setSaveStatus('error');
        setSaveMessage('Failed to update trailer/caravan');
      } finally {
        setTimeout(() => {
          setSaveStatus('idle');
          setSaveMessage('');
        }, 3000);
      }
    }}
    clientId={parseInt(clientIdFromState || '0')}
    trailerCaravan={productToView.product.data}
    mode="edit"
    setSaveStatus={setSaveStatus}
    setSaveMessage={setSaveMessage}
  />
)}
      
      {productToView.product.type === 'value_added' && (
        <QuoteValueAddedModal
          isVisible={true}
          onClose={() => {
            setIsViewProductModalVisible(false);
            setProductToView(null);
          }}
          onSave={(updatedData: any) => {
            console.log('💾 onSave called with updated data:', updatedData);
            console.log('💾 Original product:', productToView.product);
            
            // Ensure we have the quote ID - use the original product's quoteId
            const quoteId = productToView.quote.id;
            const productId = updatedData.id || productToView.product.id;
            
            console.log('🔍 Update details:', { 
              quoteId, 
              productId,
              updatedDataQuoteId: updatedData.quoteId,
              originalProductQuoteId: productToView.product.quoteId 
            });
            
            // Update the product in state
            const updatedProducts = allQuoteProducts[quoteId]?.map(p => {
              if (p.id === productId) {
                console.log('✅ Updating product in state:', p.id);
                
                const updatedProduct = {
                  ...p,
                  name: updatedData.productType || p.name,
                  value: updatedData.value?.toString() || p.value,
                  description: updatedData.description || p.description,
                  data: { 
                    ...p.data, 
                    ...updatedData,
                    // Ensure critical fields are preserved
                    id: productId,
                    quote_id: quoteId, // Ensure quote_id is set
                    quoteId: quoteId   // Ensure quoteId is set
                  },
                  // Ensure quoteId is preserved
                  quoteId: quoteId
                };
                
                console.log('🔄 Product after update:', updatedProduct);
                return updatedProduct;
              }
              return p;
            }) || [];
            
            console.log('📊 All products after update:', updatedProducts);
            
            setAllQuoteProducts(prev => ({
              ...prev,
              [quoteId]: updatedProducts
            }));
            
            setIsViewProductModalVisible(false);
            setProductToView(null);
            setSaveStatus('success');
            setSaveMessage('Value added product updated successfully!');
          }}
          clientId={parseInt(clientIdFromState || '0')}
          valueAddedProduct={productToView.product.data}
          mode="edit"
          setSaveStatus={setSaveStatus}
          setSaveMessage={setSaveMessage}
        />
      )}
      
      {productToView.product.type === 'risk_item' && (
        <QuoteRiskItemsModal
          isVisible={true}
          onClose={() => {
            setIsViewProductModalVisible(false);
            setProductToView(null);
          }}
          onSave={(updatedData: any) => {
            // Handle saving updated risk item
            console.log('💾 Saving updated risk item:', updatedData);
            
            // Update the product in state
            const updatedProducts = allQuoteProducts[productToView.quote.id]?.map(p => 
              p.id === productToView.product.id ? { ...p, ...updatedData } : p
            ) || [];
            
            setAllQuoteProducts(prev => ({
              ...prev,
              [productToView.quote.id]: updatedProducts
            }));
            
            setIsViewProductModalVisible(false);
            setProductToView(null);
            setSaveStatus('success');
            setSaveMessage('Risk item updated successfully!');
          }}
          clientId={parseInt(clientIdFromState || '0')}
          riskItem={productToView.product.data}
          mode="edit"
          setSaveStatus={setSaveStatus}
          setSaveMessage={setSaveMessage}
        />
      )}

      {productToView.product.type === 'water_craft' && (
        <WaterCraftModal
          isVisible={true}
          onClose={() => {
            setIsViewProductModalVisible(false);
            setProductToView(null);
          }}
          onSave={(updatedData: any) => {
            // Handle saving updated water craft
            console.log('💾 Saving updated water craft:', updatedData);
            
            // Update the product in state
            const quoteId = productToView.quote.id;
            const productId = updatedData.id || productToView.product.id;
            
            if (quoteId) {
              setAllQuoteProducts(prev => {
                const currentProducts = prev[quoteId] || [];
                const updatedProducts = currentProducts.map(p => {
                  if (p.id === productId && p.type === 'water_craft') {
                    console.log('✅ Updating water craft product in state:', p.id);
                    
                    const updatedProduct = {
                      ...p,
                      name: updatedData.vesselName || `${updatedData.craftType || ''} ${updatedData.year || ''}`.trim() || 'Water Craft',
                      value: updatedData.totalSumInsured || updatedData.total_sum_insured || 0,
                      description: updatedData.vesselName || `${updatedData.hullMake || ''} ${updatedData.craftType || ''}`.trim() || 'Water Craft',
                      data: { 
                        ...p.data, 
                        ...updatedData,
                        // Ensure critical fields are preserved
                        id: productId,
                        quote_id: quoteId,
                        quoteId: quoteId
                      },
                      quoteId: quoteId
                    };
                    
                    console.log('🔄 Water craft product after update:', updatedProduct);
                    return updatedProduct;
                  }
                  return p;
                });
                
                return {
                  ...prev,
                  [quoteId]: updatedProducts
                };
              });
              
              // Update in backend if needed
              if (updatedData.id && !updatedData.id.toString().startsWith('temp-')) {
                const token = localStorage.getItem('token');
                // Convert camelCase to snake_case for backend
                const apiData = {
                  year: updatedData.year,
                  craft_type: updatedData.craftType,
                  hull_make: updatedData.hullMake,
                  hull_length: updatedData.hullLength,
                  year_model: updatedData.yearModel,
                  body_paint: updatedData.bodyPaint,
                  outboard_motor_make: updatedData.outboardMotorMake,
                  outboard_engine_number: updatedData.outboardEngineNumber,
                  horse_power: updatedData.horsePower,
                  max_speed: updatedData.maxSpeed,
                  vessel_name: updatedData.vesselName,
                  vessel_storage: updatedData.vesselStorage,
                  vessel_usage: updatedData.vesselUsage,
                  amateur_built: updatedData.amateurBuilt,
                  registered_owner: updatedData.registeredOwner,
                  finance_house: updatedData.financeHouse,
                  finance_branch: updatedData.financeBranch,
                  hull_value: updatedData.hullValue,
                  outboard_value: updatedData.outboardValue,
                  sails_value: updatedData.sailsValue,
                  total_sum_insured: updatedData.totalSumInsured,
                  total_monthly_premium: updatedData.totalMonthlyPremium,
                  trailer_make: updatedData.trailerMake,
                  trailer_model: updatedData.trailerModel,
                  trailer_registration: updatedData.trailerRegistration,
                  trailer_value: updatedData.trailerValue,
                  trailer_premium: updatedData.trailerPremium,
                  client_id: clientIdFromState,
                  quote_id: quoteId
                };
                
                fetch(`${process.env.REACT_APP_API_URL}/api/water-craft/${updatedData.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(apiData),
                }).catch(error => {
                  console.error('❌ Error updating water craft in backend:', error);
                });
              }
              
              setSaveStatus('success');
              setSaveMessage('Water craft updated successfully!');
            }
            
            setIsViewProductModalVisible(false);
            setProductToView(null);
          }}
          clientId={parseInt(clientIdFromState || '0')}
          waterCraft={productToView.product.data}
          mode="edit"
        />
      )}

      {productToView.product.type === 'file' && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            File Details
          </h4>
          
          <div className="space-y-6">
            {/* File Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Name
                </label>
                <p className="text-gray-900 font-medium text-lg p-3 bg-gray-50 rounded-lg border">
                  {productToView.product.data.file_name || productToView.product.data.fileName || 'Unnamed File'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Type
                </label>
                <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
                  {productToView.product.data.document_type || productToView.product.data.documentType || 'Document'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Size
                </label>
                <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
                  {(() => {
                    const bytes = productToView.product.data.file_size || productToView.product.data.fileSize;
                    if (!bytes) return 'Unknown';
                    const units = ['B', 'KB', 'MB', 'GB'];
                    let size = parseFloat(bytes);
                    let unitIndex = 0;
                    while (size >= 1024 && unitIndex < units.length - 1) {
                      size /= 1024;
                      unitIndex++;
                    }
                    return `${size.toFixed(1)} ${units[unitIndex]}`;
                  })()}
                </p>
              </div>
            </div>

            {/* Description */}
            {productToView.product.data.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <p className="text-gray-900 p-4 bg-gray-50 rounded-lg border whitespace-pre-wrap">
                  {productToView.product.data.description}
                </p>
              </div>
            )}

            {/* Upload Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {productToView.product.data.uploaded_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uploaded Date
                  </label>
                  <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
                    {formatDate(productToView.product.data.uploaded_at)}
                  </p>
                </div>
              )}
              
              {productToView.product.data.uploaded_by && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uploaded By
                  </label>
                  <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
                    {productToView.product.data.uploaded_by}
                  </p>
                </div>
              )}
            </div>

            {/* Download Button */}
            {productToView.product.data.url && (
              <div className="pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-1">File Download</h5>
                    <p className="text-sm text-gray-500">Click the button below to download this file</p>
                  </div>
                  <a 
                    href={productToView.product.data.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download File
                  </a>
                </div>
              </div>
            )}

            {/* View Button (for image/PDF preview) */}
            {productToView.product.data.url && (
              <div className="flex justify-center">
                <a 
                  href={productToView.product.data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View File in New Tab
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {productToView.product.type === 'note' && (
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Note Details
          </h4>
          
          <div className="space-y-6">
            {/* Note Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note Content
              </label>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-gray-900 whitespace-pre-wrap">
                  {productToView.product.data.note_content || productToView.product.data.content || 'No content'}
                </p>
              </div>
            </div>

            {/* Note Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {productToView.product.data.created_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created Date
                  </label>
                  <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
                    {formatDate(productToView.product.data.created_at)}
                  </p>
                </div>
              )}
              
              {productToView.product.data.created_by && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created By
                  </label>
                  <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
                    {productToView.product.data.created_by}
                  </p>
                </div>
              )}
            </div>

            {/* Associated Quote */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Associated Quote
              </label>
              <p className="text-gray-900 p-3 bg-blue-50 rounded-lg border border-blue-100">
                {productToView.quote.referenceNumber || productToView.quote.reference || `Quote-${productToView.quote.id}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {productToView.product.type === 'claim' && (
  <div className="bg-white rounded-lg p-6 border border-gray-200">
    <div className="space-y-4">
      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border whitespace-pre-wrap">
          {productToView.product.data.description || 'No description'}
        </p>
      </div>

      {/* Policy Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
        <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
          {productToView.product.data.policy_number || productToView.product.data.policyNumber || 'Not specified'}
        </p>
      </div>

      {/* Previous Insurer */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Previous Insurer</label>
        <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
          {productToView.product.data.previous_insurer || productToView.product.data.previousInsurer || 'Not specified'}
        </p>
      </div>

      {/* Claim Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Claim Date</label>
        <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
          {productToView.product.data.claim_date || productToView.product.data.claimDate
            ? new Date(productToView.product.data.claim_date || productToView.product.data.claimDate).toLocaleDateString()
            : 'Not specified'}
        </p>
      </div>

      {/* Claim Value */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Claim Value</label>
        <p className="text-gray-900 p-3 bg-gray-50 rounded-lg border">
          R {Number(productToView.product.data.claim_value || productToView.product.data.claimValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    </div>
  </div>
)}

    </div>
  </div>
)}

{/* Property Details/Edit Modal */}
{showPropertyDetailsModal && selectedProperty && (
  <AddPropertyModal
    isVisible={showPropertyDetailsModal}
    onClose={() => {
      setShowPropertyDetailsModal(false);
      setSelectedProperty(null);
      setPropertyModalMode('view');
    }}
    onSave={async (updatedProperty: Property) => {
      console.log('💾 Property modal onSave called with:', {
        updatedProperty,
        productToView,
        quoteIdFromProductToView: productToView?.quote?.id,
        clientIdFromState
      });
      
      // Ensure we have the quote ID
      const quoteId = productToView?.quote?.id;
      const productId = updatedProperty.id || selectedProperty.id;
      
      console.log('📋 Property save - quoteId:', quoteId, 'productId:', productId);
      
      if (quoteId && productToView?.product) {
        // Update the product in state
        setAllQuoteProducts(prev => {
          const currentProducts = prev[quoteId] || [];
          const updatedProducts = currentProducts.map(p => {
            if (p.id === productId) {
              console.log('✅ Updating product in state:', p.id);
              
              const updatedProduct = {
                ...p,
                name: updatedProperty.propertyType || p.name,
                value: updatedProperty.insuredValue || p.value,
                description: formatPropertyDescription(updatedProperty),
                data: { 
                  ...p.data, 
                  ...updatedProperty,
                  // Ensure critical fields are preserved
                  id: productId,
                  quote_id: quoteId,
                  quoteId: quoteId,
                  // Ensure cover values are included
                  has_power_surge_cover: updatedProperty.hasPowerSurgeCover,
                  power_surge_cover_value: updatedProperty.powerSurgeCoverValue,
                  has_accidental_damage_cover: updatedProperty.hasAccidentalDamageCover,
                  accidental_damage_cover_value: updatedProperty.accidentalDamageCoverValue,
                  // Also include camelCase versions for consistency
                  hasPowerSurgeCover: updatedProperty.hasPowerSurgeCover,
                  powerSurgeCoverValue: updatedProperty.powerSurgeCoverValue,
                  hasAccidentalDamageCover: updatedProperty.hasAccidentalDamageCover,
                  accidentalDamageCoverValue: updatedProperty.accidentalDamageCoverValue,
                },
                quoteId: quoteId
              };
              
              console.log('🔄 Product after update:', {
                ...updatedProduct,
                coverFields: {
                  hasPowerSurgeCover: updatedProduct.data.hasPowerSurgeCover,
                  powerSurgeCoverValue: updatedProduct.data.powerSurgeCoverValue,
                  hasAccidentalDamageCover: updatedProduct.data.hasAccidentalDamageCover,
                  accidentalDamageCoverValue: updatedProduct.data.accidentalDamageCoverValue,
                }
              });
              return updatedProduct;
            }
            return p;
          });
          
          return {
            ...prev,
            [quoteId]: updatedProducts
          };
        });
        
        // ✅ LOG ACTIVITY: Property Updated - MOVED TO TOP LEVEL
        console.log('🔍 Attempting to log property update...');
        if (quoteId && updatedProperty && updatedProperty.propertyType) {
          try {
            console.log('📝 Calling logProductUpdate with:', {
              quoteId,
              propertyType: updatedProperty.propertyType,
              productId
            });
            await logProductUpdate(quoteId, 'property', updatedProperty.propertyType || 'Property', {
              productId: productId,
              updatedFields: Object.keys(updatedProperty).filter(key => 
                key !== 'id' && key !== 'isEditing' && key !== 'isTemp'
              )
            });
            console.log('✅ Property update logged successfully');
          } catch (logError) {
            console.error('❌ Failed to log property update:', logError);
          }
        } else {
          console.warn('⚠️ Skipping property update log - missing data:', {
            quoteId,
            hasUpdatedProperty: !!updatedProperty,
            hasPropertyType: !!(updatedProperty && updatedProperty.propertyType)
          });
        }
        
        // Also update in backend if needed
        if (updatedProperty.id && !updatedProperty.id.toString().startsWith('temp-')) {
          const token = localStorage.getItem('token');
          
          // Prepare data for backend with ALL fields mapped to snake_case
          const backendData = {
            // Basic address fields
            address_line_1: updatedProperty.addressLine1,
            address_line_2: updatedProperty.addressLine2,
            suburb: updatedProperty.suburb,
            city: updatedProperty.city,
            postal_code: updatedProperty.postalCode,
            region: updatedProperty.region,
            country: updatedProperty.country,
            
            // Property details
            property_type: updatedProperty.propertyType,
            residence_type: updatedProperty.residenceType,
            building_age: updatedProperty.buildingAge,
            situated_at: updatedProperty.situatedAt,
            cover_type: updatedProperty.coverType,
            insured_value: updatedProperty.insuredValue,
            
            // Boolean fields
            has_swimming_pool: updatedProperty.hasSwimmingPool,
            has_motorized_gate: updatedProperty.hasMotorizedGate,
            is_unoccupied_more_than_60_days: updatedProperty.isUnoccupiedMoreThan60Days,
            is_commune: updatedProperty.isCommune,
            under_construction: updatedProperty.underConstruction,
            built_on_steep_slope: updatedProperty.builtOnSteepSlope,
            has_excessive_retaining_walls: updatedProperty.hasExcessiveRetainingWalls,
            near_water_body: updatedProperty.nearWaterBody,
            has_rising_damp: updatedProperty.hasRisingDamp,
            has_wall_cracks: updatedProperty.hasWallCracks,
            has_subsidence_history: updatedProperty.hasSubsidenceHistory,
            has_outbuilding: updatedProperty.hasOutbuilding,
            has_perimeter_fence: updatedProperty.hasPerimeterFence,
            has_electric_fence: updatedProperty.hasElectricFence,
            
            // Thatch roof fields
            has_thatch_roof: updatedProperty.hasThatchRoof,
            thatch_less_than_5m: updatedProperty.thatchLessThan5m,
            thatch_sabs_conductor: updatedProperty.thatchSabsConductor,
            thatch_treated: updatedProperty.thatchTreated,
            
            // COVER FIELDS
            hasPowerSurgeCover: updatedProperty.hasPowerSurgeCover,
            powerSurgeCoverValue: updatedProperty.powerSurgeCoverValue || 0,
            hasAccidentalDamageCover: updatedProperty.hasAccidentalDamageCover,
            accidentalDamageCoverValue: updatedProperty.accidentalDamageCoverValue || 0,
            
            // Material arrays
            wall_materials: Array.isArray(updatedProperty.wallMaterials) 
              ? updatedProperty.wallMaterials 
              : [],
            roof_materials: Array.isArray(updatedProperty.roofMaterials) 
              ? updatedProperty.roofMaterials 
              : [],
            floor_materials: Array.isArray(updatedProperty.floorMaterials) 
              ? updatedProperty.floorMaterials 
              : [],
            other_surrounding_areas: Array.isArray(updatedProperty.otherSurroundingAreas) 
              ? updatedProperty.otherSurroundingAreas 
              : [],
            
            // Insurance details
            premium: updatedProperty.premium,
            excess: updatedProperty.excess,
            no_claim_bonus: updatedProperty.noClaimBonus,
            
            // Relationship fields
            quote_id: quoteId,
            client_id: clientIdFromState
          };
          
          console.log('📤 Sending property update to backend...');
          fetch(`${process.env.REACT_APP_API_URL}/api/properties/${updatedProperty.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(backendData),
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('Failed to update property in backend');
            }
            return response.json();
          })
          .then(data => {
            console.log('✅ Backend update successful:', {
              ...data,
              coverFields: {
                has_power_surge_cover: data.has_power_surge_cover,
                power_surge_cover_value: data.power_surge_cover_value,
                has_accidental_damage_cover: data.has_accidental_damage_cover,
                accidental_damage_cover_value: data.accidental_damage_cover_value,
              }
            });
          })
          .catch(error => {
            console.error('❌ Error updating property in backend:', error);
            // Show error but don't fail the whole operation
            setSaveStatus('warning');
            setSaveMessage('Property updated locally, but backend update failed. Please try again later.');
          });
        }
        
        setSaveStatus('success');
        setSaveMessage('Property updated successfully!');

        // ✅ Force refresh of products to ensure arrays are loaded correctly
        setTimeout(async () => {
          if (quoteId) {
            try {
              const token = localStorage.getItem('token');
              const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/quotes/${quoteId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (response.data?.data) {
                await extractProductsForMasterView(response.data.data);
              }
            } catch (refreshError) {
              console.error('Error refreshing products after update:', refreshError);
            }
          }
          setSaveStatus('idle');
          setSaveMessage('');
        }, 1500);

      } else {
        console.error('❌ Missing quoteId or productToView:', {
          quoteId,
          hasProductToView: !!productToView,
          hasProduct: !!(productToView?.product)
        });
        setSaveStatus('error');
        setSaveMessage('Failed to update: Missing quote information');
      }
      
      // Close the modal
      setShowPropertyDetailsModal(false);
      setSelectedProperty(null);
      setPropertyModalMode('view');
    }}
    clientId={parseInt(clientIdFromState || '0')}
    property={selectedProperty}
    mode={propertyModalMode}
    onEdit={() => setPropertyModalMode('edit')}
  />
)}

{/* Removal Reasons Section */}
{(() => {
  if (loading) return null;
  
  // Check localStorage directly for removal reasons
  let removalReasonsToDisplay: RemovalReason[] = [];
  
  if (clientIdFromState) {
    try {
      const savedRemovalReasons = localStorage.getItem(`removalReasons_${clientIdFromState}`);
      if (savedRemovalReasons && savedRemovalReasons !== '[]') {
        const parsedReasons = JSON.parse(savedRemovalReasons);
        if (Array.isArray(parsedReasons) && parsedReasons.length > 0) {
          removalReasonsToDisplay = parsedReasons;
          console.log('📋 Displaying removal reasons from localStorage:', removalReasonsToDisplay);
        }
      }
    } catch (error) {
      console.error('❌ Error reading removal reasons from localStorage:', error);
    }
  }

  // Filter for current quotes if we have quotes
  if (allQuotes.length > 0 && removalReasonsToDisplay.length > 0) {
    const currentQuoteIds = allQuotes.map(quote => quote.id);
    removalReasonsToDisplay = removalReasonsToDisplay.filter(removal => 
      removal && currentQuoteIds.includes(removal.quoteId)
    );
  }

  if (removalReasonsToDisplay.length === 0) return null;

  return (
    <div className="mt-8 bg-white rounded-xl shadow-lg border border-gray-200">
      <div className="bg-red-50 px-6 py-4 border-b border-red-200 rounded-t-xl">
        <h2 className="text-xl font-bold text-red-700 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Removed Products
          <span className="bg-red-200 text-red-800 text-sm px-2 py-1 rounded-full ml-2">
            {removalReasonsToDisplay.length}
          </span>
        </h2>
        <p className="text-red-600 text-sm mt-1">
          Products that have been removed from quotes
        </p>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {removalReasonsToDisplay.map((removal, index) => {
            if (!removal) return null;
            
            const quote = allQuotes.find(q => q.id === removal.quoteId);
            return (
              <div 
                key={`${removal.quoteId}-${removal.productId}-${removal.productType}-${index}`}
                className="border border-red-200 rounded-lg p-4 bg-red-50"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-red-800">
                      {removal.productName || 'Unknown Product'}
                    </h4>
                    <p className="text-sm text-red-600">
                      Type: {getProductTypeDisplay(removal.productType)} • 
                      {quote && ` Quote: ${quote.referenceNumber} •`}
                      Removed: {formatDate(removal.removedAt)}
                    </p>
                  </div>
                  <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                    Removed
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col">
                    <span className="text-red-700 font-semibold mb-1">Removal Reason:</span>
                    <span className="text-red-800 text-xs bg-white p-3 rounded border border-red-200">
                      {removal.reason || 'No reason provided'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
})()}

      {/* Email Template Modal */}
        <EmailTemplateModal
          isVisible={showEmailTemplateModal}
          subject={`Quotation - ${allQuotes[0]?.referenceNumber || allQuotes[0]?.reference || 'Quote'}`}
          recipients={administrators
            .filter(admin => 
              (admin.quotation_status === 'active' || admin.quotation_status === undefined) && 
              admin.email && admin.email.trim() !== ''
            )
            .concat(underwriters.filter(uw => 
              (uw.quotation_status === 'active' || uw.quotation_status === undefined) && 
              uw.email && uw.email.trim() !== ''
            ))
            .map(person => person.email!.trim())}
          administrators={administrators.filter(a => a.quotation_status === 'active' || a.quotation_status === undefined)}
          underwriters={underwriters.filter(u => u.quotation_status === 'active' || u.quotation_status === undefined)}
          onSend={async (emailData) => {
            setIsSendingEmail(true);
            setShowEmailTemplateModal(false);
            
            try {
              // First, ensure insurers data is saved
              await saveInsurersData();
              
              // Then send the email
              await handleEmailQuoteWithMessage(
                emailData.includePDF, 
                emailData.subject,
                emailData.message
              );
            } catch (error) {
              console.error('Error saving insurers or sending email:', error);
              setSaveStatus('error');
              setSaveMessage('Failed to save insurers settings. Please save first.');
            } finally {
              setIsSendingEmail(false);
            }
          }}
          onCancel={() => {
            setShowEmailTemplateModal(false);
            setSaveStatus('idle');
            setSaveMessage('');
          }}
          isLoading={isSendingEmail}
          showInsurersNote={true}
        />

        {isAcceptQuotationModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">

      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Accept Quotation</h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Enter final details for each active insurer
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsAcceptQuotationModalOpen(false)}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>
      </div>

      {/* Form – one block per insurer */}
      <div className="p-6 space-y-8">
        {acceptFormData.map((insurer, index) => (
          <div
            key={insurer.id}
            className="border border-gray-200 rounded-xl p-5 bg-gray-50 hover:bg-gray-100/50 transition-colors"
          >
            {/* Insurer header */}
            <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{insurer.name}</h3>
                <span className="text-sm text-gray-500 capitalize">{insurer.type}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                insurer.type === 'administrator' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
              }`}>
                {insurer.type}
              </span>
            </div>

            {/* 4 fields grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Premium */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Final Premium <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 font-medium">R</span>
                  </div>
                  <input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={insurer.premium || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setAcceptFormData(prev =>
                        prev.map((item, i) =>
                          i === index ? { ...item, premium: newValue } : item
                        )
                      );
                    }}
                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="Enter premium"
                    required
                  />
                </div>
              </div>

              {/* Broker Fee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Broker Fee
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 font-medium">R</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={insurer.brokerFee || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setAcceptFormData(prev =>
                        prev.map((item, i) =>
                          i === index ? { ...item, brokerFee: newValue } : item
                        )
                      );
                    }}
                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="Enter broker fee"
                  />
                </div>
              </div>

              {/* Management Fee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Management Fee
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 font-medium">R</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={insurer.managementFee || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setAcceptFormData(prev =>
                        prev.map((item, i) =>
                          i === index ? { ...item, managementFee: newValue } : item
                        )
                      );
                    }}
                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="Enter management fee"
                  />
                </div>
              </div>

              {/* Insurer Quotation Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Insurer Quotation Number / Ref <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={insurer.insurerQuotationNumber}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setAcceptFormData(prev =>
                      prev.map((item, i) =>
                        i === index ? { ...item, insurerQuotationNumber: newValue } : item
                      )
                    );
                  }}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500 bg-white shadow-sm"
                  placeholder="QTN-2025-0048721 / POL-ABC123"
                  required
                />
              </div>

            </div>

          </div>
        ))}

      </div>

      {/* Footer */}
<div className="px-6 py-5 border-t border-gray-200 flex justify-end gap-4 bg-gray-50 rounded-b-2xl">
  <button
    onClick={() => setIsAcceptQuotationModalOpen(false)}
    className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
  >
    Cancel
  </button>

  {/* PREVIEW BUTTON - Added before Sign Online */}
  <button
  onClick={() => {
    // Validate required fields
    const invalid = acceptFormData.some(item =>
      !item.premium || parseFloat(item.premium) <= 0 || 
      !item.insurerQuotationNumber.trim()
    );

    if (invalid) {
      alert("Please fill in Premium and Insurer Quotation Number for every insurer.");
      return;
    }

    const activeInsurer = acceptFormData[0];
    
    // Save data to localStorage for the proposal form to read
    localStorage.setItem('proposalAcceptData', JSON.stringify({
      insurers: acceptFormData,
      quoteId: allQuotes[0]?.id,
      clientId: clientIdFromState,
      timestamp: new Date().toISOString(),
      acceptMethod: 'preview',
      premium: activeInsurer?.premium || '',
      brokerFee: activeInsurer?.brokerFee || '',
      managementFee: activeInsurer?.managementFee || '',
      quotation_ref: activeInsurer?.insurerQuotationNumber || ''  // ✅ ADD THIS
    }));

    // Open proposal form in a NEW TAB for preview with preview flag
    const proposalUrl = `/proposal-form/${allQuotes[0]?.id}?preview=true&clientId=${clientIdFromState}`;
    window.open(proposalUrl, '_blank');
    
    setSaveStatus('success');
    setSaveMessage('Preview opened in new tab. Close it to return and send.');
    setTimeout(() => setSaveStatus('idle'), 3000);
  }}
  className="px-7 py-2.5 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
>
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
    Preview Proposal
  </button>

</div>

    </div>
  </div>
)}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
          <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <div className="mb-6">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Validation Required</h3>
              <p className="text-gray-600 text-center">{validationMessage}</p>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => setShowValidationModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD PERSON MODAL */}
      {isPersonModalVisible && (
        <AddPersonModal
          isOpen={isPersonModalVisible}
          person={personFormData}
          errors={personErrors}
          editingPersonId={null}
          onClose={handleClosePersonModal}
          onSubmit={handlePersonSubmit}
          onInputChange={handlePersonInputChange}
          onDriverLicensesChange={handleDriverLicensesChange}
        />
      )}

      {/* Client Documents Modal */}
            <ClientDocumentsModal
        isVisible={isDocumentsModalVisible}
        onClose={() => setIsDocumentsModalVisible(false)}
        clientId={clientIdFromState}
        quoteIds={documentQuoteIds}        // <-- USE THE MEMOIZED VARIABLE
        allQuotes={allQuotes}
      />

      {/* Quote Email Modal */}
<QuoteEmailModal
  isVisible={isQuoteEmailModalVisible}
  onClose={() => setIsQuoteEmailModalVisible(false)}
  quoteId={allQuotes[0]?.id || ''}
  clientId={clientIdFromState || ''}
  quoteRef={allQuotes[0]?.referenceNumber || allQuotes[0]?.reference || ''}
  clientName={
    clientFromHook
      ? `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim()
      : ''
  }
  onSent={handleQuoteEmailSent}
/>

{/* Quote WhatsApp Modal */}
<QuoteWhatsAppModal
  isVisible={isQuoteWhatsAppModalVisible}
  onClose={() => setIsQuoteWhatsAppModalVisible(false)}
  quoteId={allQuotes[0]?.id || ''}
  clientId={clientIdFromState || ''}
  quoteRef={allQuotes[0]?.referenceNumber || allQuotes[0]?.reference || ''}
  clientName={
    clientFromHook
      ? `${clientFromHook.name || ''} ${clientFromHook.surname || ''}`.trim()
      : ''
  }
  clientPhone={clientFromHook?.mobile_number || clientFromHook?.phone || ''}
  onSent={handleQuoteWhatsAppSent}
/>

{/* Decline Quotation Modal */}
<DeclineQuotationModal
  isVisible={isDeclineModalVisible}
  onClose={() => setIsDeclineModalVisible(false)}
  quoteId={allQuotes[0]?.id || ''}
  quoteRef={allQuotes[0]?.referenceNumber || allQuotes[0]?.reference || ''}
  onDeclined={handleQuoteDeclineSuccess}
/>

      {/* Notes Modal */}
{isNotesModalVisible && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Activity Log</h2>
            <p className="text-sm text-gray-500">
              All activities and changes for {allQuotes[0]?.referenceNumber || 'this quote'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsNotesModalVisible(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <QuoteFullNotes 
          quoteId={allQuotes[0]?.id} 
          clientId={clientIdFromState || ''} 
        />
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
        <button
          onClick={() => setIsNotesModalVisible(false)}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default QuoteDetailPage;